import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import FileMenu from "./FileMenu";
import { useWorkOffline, OfflineStatusBar, QueuePanel } from "./WorkOffline";
import { RIcon, FolderIcon } from "./RibbonIcons";
import { RBtn, RGroup, RibbonTabButton, RibbonOverflowRow, RibbonMenu, RibbonDropdownButton, StatusChip } from "./ribbon/RibbonPrimitives";
import { loadTemplates, TEMPLATES_STORAGE_KEY, fetchTemplateRepo, markTemplateUsed, TEMPLATE_CATEGORIES } from "./emailTemplates";
import { WORKSPACE_THEME, WORKSPACE_RADIUS } from "./workspaceTheme";
import { useModalA11y } from "./useModalA11y";
// Phase 4b (Workspace/Mail Merge modal/context-menu unification, see
// linear-brewing-toast.md) — the shared right-click menu shell from Phase 1.
// Its own chrome (border/radius/shadow/hover/font) intentionally adopts the
// new Apple/iOS tokens from ui/theme.js, replacing the old hand-rolled
// Segoe-UI/4px-radius Outlook-desktop menu look — item labels/actions below
// are untouched, only the surrounding box changes.
import { ContextMenu, ContextMenuItem, ContextMenuDivider, ContextSubMenu } from "./ui/ContextMenu";
// Shared confirm/form-dialog shell (backdrop + centered card + focus-trap),
// same Phase 1 origin as ContextMenu above. Properties, New Folder, Rules,
// Rule Editor, Inbox Zero and New Sweep Rule all adopt it below — each
// keeps its own inner content/fields exactly as before, only the
// surrounding box (radius/shadow/backdrop) is now shared and unified.
import { Modal } from "./ui/Modal";
import RichTextEditor from "./richtext/RichTextEditor";
import WordRibbon from "./richtext/WordRibbon";
import FindReplaceDialog from "./richtext/FindReplaceDialog";
import { sanitizeEmailHtml } from "./sanitizeEmailHtml";
import { useVoiceDraft } from "./richtext/voiceDictation";

// Same values as before — now sourced from the shared workspaceTheme.js
// instead of a copy hard-coded in this file (see that file's header
// comment). Kept the local name `C` so every existing usage below is
// unchanged.
const C = WORKSPACE_THEME;
const WR = WORKSPACE_RADIUS;

const MERGE_TAGS = [
  "{{FirstName}}","{{LastName}}","{{Title}}","{{Company}}",
  "{{CompanyNameForEmails}}","{{Email}}","{{SenderName}}","{{SenderCompany}}"
];
// Human-readable labels for the Insert Field dropdown (compose pane) — same
// wording MailMerge.js's own MERGE_TAG_LABELS already uses, kept as a
// separate copy rather than a shared import since the two files don't
// otherwise share constants and this is tiny, static data.
const MERGE_TAG_LABELS = {
  "{{FirstName}}":"First Name","{{LastName}}":"Last Name","{{Title}}":"Job Title",
  "{{Company}}":"Company","{{CompanyNameForEmails}}":"Company (email-safe)",
  "{{Email}}":"Email","{{SenderName}}":"Sender Name","{{SenderCompany}}":"Sender Company",
};

// Quick Access Toolbar (P3 #18) — the full catalog of commands a user can
// pin, and which ones show by default (matches exactly what was hardcoded
// before this feature existed, so nobody's toolbar visibly changes on
// upgrade). The runtime fn/disabled for each id is built inside the
// component (QAT_ACTIONS below), since those need to close over
// component-scope handlers and state; this stays static metadata only.
const QAT_COMMANDS=[
  {id:"newEmail",label:"New Email",icon:"mailNew"},
  {id:"reply",label:"Reply",icon:"reply"},
  {id:"replyAll",label:"Reply All",icon:"replyAll"},
  {id:"forward",label:"Forward",icon:"forward"},
  {id:"delete",label:"Delete",icon:"trash"},
  {id:"archive",label:"Archive",icon:"archive"},
  {id:"flag",label:"Flag",icon:"flag"},
  {id:"markRead",label:"Mark as Read",icon:"check"},
  {id:"sync",label:"Send / Receive All",icon:"refresh"},
  {id:"save",label:"Save Draft",icon:"save"},
  {id:"print",label:"Print",icon:"print"},
];
const QAT_DEFAULT_IDS=["reply","forward","sync","save"];
const QAT_STORAGE_KEY="sdr_qat_commands";

const LEAD_STATUSES = ["New","Contacted","Replied","Interested","Meeting Scheduled","Opportunity","Proposal Sent","Won","Lost"];
const STATUS_CFG = {
  "New":{bg:"#f3f2f1",tc:"#605e5c"},
  "Contacted":{bg:"#deecf9",tc:"#0078d4"},
  "Replied":{bg:"#dff6dd",tc:"#107c10"},
  "Interested":{bg:"#dff6dd",tc:"#107c10"},
  "Meeting Scheduled":{bg:"#f4f0ff",tc:"#5c2d91"},
  "Opportunity":{bg:"#fff4ce",tc:"#d83b01"},
  "Proposal Sent":{bg:"#fff4ce",tc:"#d83b01"},
  "Won":{bg:"#dff6dd",tc:"#107c10"},
  "Lost":{bg:"#fde7e9",tc:"#a4262c"},
};

const OPPS_STAGES = ["Discovery","Qualification","Proposal","Negotiation","Won","Lost"];

const RIBBON_TABS = [
  {id:"file",label:"File"},
  {id:"home",label:"Home"},
  {id:"send",label:"Send / Receive"},
  {id:"folder",label:"Folder"},
  {id:"view",label:"View"},
  {id:"help",label:"Help"},
];

// Search scope options, matching the reference screenshot's dropdown exactly
// (Current Folder / Subfolders / Current Mailbox / All Mailboxes / All
// Outlook Items / Favorites). Favorites is disabled — this app has no real
// favorites feature yet (its one existing "Add to Favorites" ribbon button
// is already disabled with a "Not built yet" tooltip elsewhere), so it stays
// honestly disabled here too rather than faking a scope with nothing behind
// it.
const SEARCH_SCOPE_OPTIONS = [
  {id:"currentFolder",label:"Current Folder"},
  {id:"subfolders",label:"Subfolders"},
  {id:"currentMailbox",label:"Current Mailbox"},
  {id:"allMailboxes",label:"All Mailboxes"},
  {id:"allOutlookItems",label:"All Outlook Items"},
  {id:"favorites",label:"Favorites",disabled:true},
];
// The canonical mail folders searched for cross-folder scopes — deliberately
// excludes "focused" (an alternate Inbox view mapping to the same backend
// folder as "inbox", see FOLDER_API_MAP below; searching it too would just
// duplicate every Inbox result).
const CANONICAL_SEARCH_FOLDERS = ["inbox","sent","drafts","deleted","archive","junk"];

// Secondary nav — the CRM/Campaign Management/Productivity section headers
// and their default-expanded state already existed below (see `expanded`'s
// useState default: Mail/Campaign Management start open, CRM/Productivity
// start collapsed) with a whole render tree already built for it (badges,
// selection highlighting, expand/collapse chevrons — see the "Nav tree
// sections" block further down). NAV_TREE itself was just left empty, so
// none of that ever rendered. Every id below already has a working
// getListItems()/render branch elsewhere in this file (leads/contacts/
// campaigns/sequences/opportunities, and now templates — see the Templates
// section further down for that one's own story); this only adds the
// entries that point at them.
const NAV_TREE = [
  {section:"CRM", items:[
    {id:"leads", label:"Leads", icon:<RIcon name="person" size={13}/>},
    {id:"contacts", label:"Contacts", icon:<RIcon name="people" size={13}/>},
    {id:"opportunities", label:"Opportunities", icon:<RIcon name="briefcase" size={13}/>},
  ]},
  {section:"Campaign Management", items:[
    {id:"campaigns", label:"Campaigns", icon:<RIcon name="campaign" size={13}/>},
    {id:"sequences", label:"Sequences", icon:<RIcon name="sequence" size={13}/>},
  ]},
  {section:"Productivity", items:[
    {id:"templates", label:"Templates", icon:<RIcon name="doc" size={13}/>},
  ]},
];

// UI folder ids -> backend canonical folder ids. "outbox" has no backend
// equivalent yet (Resend sends synchronously, so there's no real queue to
// show); it stays empty until a scheduled-send/queue feature exists.
const FOLDER_API_MAP = { inbox:"inbox", focused:"inbox", sent:"sent", drafts:"drafts", deleted:"trash", archive:"archive", junk:"junk" };

// Pane layout (folder-pane width/collapsed state, reading-pane position,
// list-pane width/height) used to reset to these same hardcoded defaults on
// every reload — the values themselves aren't new, only that they're now a
// starting point read from localStorage instead of the only values that
// ever existed. Saved as one object under one key rather than five separate
// ones so a corrupt/partial value can't leave the layout in a mixed state.
const PANE_LAYOUT_KEY = "sdr_workspace_pane_layout";
function loadPaneLayout(){
  try{
    const raw = localStorage.getItem(PANE_LAYOUT_KEY);
    if(raw){
      const parsed = JSON.parse(raw);
      if(parsed && typeof parsed==="object") return parsed;
    }
  }catch(e){}
  return {};
}

// Real font names/point sizes matching the Outlook 365 desktop compose editor.
const FONT_LIST = [
  {value:"calibri",label:"Calibri",css:"Calibri,sans-serif"},
  {value:"arial",label:"Arial",css:"Arial,Helvetica,sans-serif"},
  {value:"timesnewroman",label:"Times New Roman",css:"'Times New Roman',Times,serif"},
  {value:"georgia",label:"Georgia",css:"Georgia,serif"},
  {value:"verdana",label:"Verdana",css:"Verdana,Geneva,sans-serif"},
  {value:"tahoma",label:"Tahoma",css:"Tahoma,Geneva,sans-serif"},
  {value:"couriernew",label:"Courier New",css:"'Courier New',Courier,monospace"},
  {value:"trebuchetms",label:"Trebuchet MS",css:"'Trebuchet MS',sans-serif"},
  {value:"comicsansms",label:"Comic Sans MS",css:"'Comic Sans MS',cursive"},
  {value:"impact",label:"Impact",css:"Impact,Charcoal,sans-serif"},
];
const SIZE_LIST = [
  {value:"8pt",label:"8"},{value:"9pt",label:"9"},{value:"10pt",label:"10"},{value:"10.5pt",label:"10.5"},
  {value:"11pt",label:"11"},{value:"12pt",label:"12"},{value:"14pt",label:"14"},{value:"16pt",label:"16"},
  {value:"18pt",label:"18"},{value:"20pt",label:"20"},{value:"24pt",label:"24"},{value:"28pt",label:"28"},
  {value:"36pt",label:"36"},{value:"48pt",label:"48"},{value:"72pt",label:"72"},
];

// NOTE: this file's own Quill Attributor registration + toolbar config +
// picker-label CSS hack (previously here) were removed in favor of the
// shared client/src/richtext/ module, which now registers the same (and
// more) formats once, app-wide, for every editor surface including this
// one's in-pane composer below. FONT_LIST/SIZE_LIST stay in this file only
// because the pop-out compose window (a separate vanilla-JS/CDN-loaded Quill
// instance in its own browser window, not part of the React bundle) still
// builds its own toolbar HTML directly from them.

function Sp({size=13,color="#0078d4"}){
  return <div style={{width:size,height:size,border:"2px solid #e1dfdd",borderTopColor:color,borderRadius:"50%",display:"inline-block",flexShrink:0,animation:"owspin .7s linear infinite"}}/>;
}

// RBtn, RibbonTabButton, RGroup, and RibbonOverflowRow (the ribbon button,
// tab-strip button, command group, and responsive ">>" overflow wrapper
// used throughout this file's ribbon) now live in ./ribbon/RibbonPrimitives,
// shared with MailMerge.js — see that file's header comment for why they
// were extracted and the one small dormant-bug fix (a hardcoded divider
// gray that never picked up palette changes) that came with it. Every call
// site below is unchanged; only the import moved.

// Draggable divider between two panes. "vertical" means the bar itself is
// a vertical line the user drags left/right (resizing a width); "horizontal"
// means a horizontal line dragged up/down (resizing a height). onDrag gets
// the pointer's movement since the previous event and is responsible for
// clamping — it's typically a setState updater like
// w=>Math.max(min,Math.min(max,w+delta)).
function Splitter({orientation,onDrag}){
  const draggingRef=useRef(false);
  const lastPosRef=useRef(0);
  const [hover,setHover]=useState(false);
  const [active,setActive]=useState(false);

  useEffect(()=>{
    const onMove=(e)=>{
      if(!draggingRef.current)return;
      const pos=orientation==="vertical"?e.clientX:e.clientY;
      onDrag(pos-lastPosRef.current);
      lastPosRef.current=pos;
    };
    const onUp=()=>{
      if(!draggingRef.current)return;
      draggingRef.current=false;
      setActive(false);
      document.body.style.cursor="";
      document.body.style.userSelect="";
    };
    window.addEventListener("mousemove",onMove);
    window.addEventListener("mouseup",onUp);
    return ()=>{window.removeEventListener("mousemove",onMove);window.removeEventListener("mouseup",onUp);};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[orientation]);

  const onMouseDown=(e)=>{
    e.preventDefault();
    draggingRef.current=true;
    setActive(true);
    lastPosRef.current=orientation==="vertical"?e.clientX:e.clientY;
    document.body.style.cursor=orientation==="vertical"?"col-resize":"row-resize";
    document.body.style.userSelect="none";
  };

  const highlighted=hover||active;
  return(
    <div role="separator" aria-orientation={orientation==="vertical"?"vertical":"horizontal"} aria-label="Resize pane"
      onMouseDown={onMouseDown} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{
        width:orientation==="vertical"?5:"100%", height:orientation==="vertical"?"auto":5,
        flexShrink:0, cursor:orientation==="vertical"?"col-resize":"row-resize",
        position:"relative", zIndex:2, background:"transparent",
      }}>
      <div style={{
        position:"absolute",
        top:orientation==="vertical"?0:"50%", left:orientation==="vertical"?"50%":0,
        width:orientation==="vertical"?(highlighted?3:1):"100%", height:orientation==="vertical"?"100%":(highlighted?3:1),
        transform:orientation==="vertical"?"translateX(-50%)":"translateY(-50%)",
        background:highlighted?C.accent:C.border, transition:"background .1s,width .1s,height .1s",
      }}/>
    </div>
  );
}

function Badge({v,color=C.accent}){
  if(!v)return null;
  return <span style={{background:color,color:"#fff",fontSize:10,fontWeight:700,padding:"1px 5px",borderRadius:99,minWidth:16,textAlign:"center",fontFamily:"monospace"}}>{v}</span>;
}

// General-purpose reading-pane notification bar — the same "InfoBar" shape
// Outlook Desktop uses for things like "This message was sent with High
// importance" and "Some pictures have been blocked for your safety", each
// with an optional single action button (e.g. "Download Pictures").
function NotificationBar({tone="warning",text,action}){
  const palette={
    warning:{bg:"#fff4ce",border:"#e8c66a",text:"#5c4813"},
    info:{bg:C.accentL,border:C.accent+"55",text:C.text},
  }[tone]||{bg:"#fff4ce",border:"#e8c66a",text:"#5c4813"};
  return(
    <div role="status" style={{display:"flex",alignItems:"center",gap:10,padding:"6px 20px",background:palette.bg,borderBottom:"1px solid "+palette.border,fontSize:12,color:palette.text}}>
      <span style={{flex:1}}>{text}</span>
      {action&&(
        <button onClick={action.onClick}
          style={{background:"#fff",border:"1px solid "+palette.border,borderRadius:3,padding:"3px 10px",fontSize:11,color:palette.text,cursor:"pointer",fontWeight:600,flexShrink:0}}>
          {action.label}
        </button>
      )}
    </div>
  );
}

// Blocks remote (http/https) <img> sources in an HTML email body, the way
// every real mail client does by default — inline (data:) and same-origin
// images are left alone since they don't leak anything by loading. Returns
// the transformed HTML plus whether anything was actually blocked, so the
// caller only needs to show the "pictures blocked" bar when it's true.
// The original html the caller passed in is never mutated; parsing happens
// in a detached document so nothing here can trigger a real image request.
//
// The result also always goes through sanitizeEmailHtml before being
// returned — inbound mail is attacker-controlled (anyone can email a
// connected mailbox), and this used to be the only processing step before
// the body was handed straight to dangerouslySetInnerHTML, with nothing
// stripping <script>/event handlers/javascript: URLs.
function blockRemoteImages(html){
  if(!html)return{safeHtml:"",hadBlocked:false};
  try{
    const doc=new DOMParser().parseFromString(html,"text/html");
    let hadBlocked=false;
    doc.querySelectorAll("img[src]").forEach(img=>{
      const src=img.getAttribute("src")||"";
      if(/^https?:\/\//i.test(src)){
        hadBlocked=true;
        img.setAttribute("data-blocked-src",src);
        img.removeAttribute("src");
        img.setAttribute("alt","Picture blocked for your safety"+(img.getAttribute("alt")?": "+img.getAttribute("alt"):""));
        const prevStyle=img.getAttribute("style")||"";
        img.setAttribute("style",prevStyle+";border:1px dashed #c1c1c1;background:#f3f2f1;min-width:120px;min-height:60px;");
      }
    });
    return{safeHtml:sanitizeEmailHtml(doc.body.innerHTML),hadBlocked};
  }catch(e){
    // If parsing ever fails for some malformed body, still sanitize before
    // showing anything — a stripped-down or blank message body is far
    // safer than falling back to unsanitized attacker-controlled HTML.
    return{safeHtml:sanitizeEmailHtml(html),hadBlocked:false};
  }
}

// Folder-type icons (inbox/focused/sent/drafts/... ) now live in
// RibbonIcons.js's <FolderIcon> (imported above) instead of a second,
// separate icon set defined in this file — same exact SVG paths, just one
// shared home instead of two. See RibbonIcons.js's header comment.

function StatusBadge({status}){
  const sc=STATUS_CFG[status]||STATUS_CFG["New"];
  return <span style={{background:sc.bg,color:sc.tc,fontSize:10,padding:"1px 7px",borderRadius:3,fontWeight:500,whiteSpace:"nowrap"}}>{status}</span>;
}

const DAY_NAMES=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const HOURS=Array.from({length:14},(_,i)=>7+i); // 7am - 8pm

function startOfWeek(d){const x=new Date(d);const day=x.getDay();x.setDate(x.getDate()-day);x.setHours(0,0,0,0);return x;}
function addDays(d,n){const x=new Date(d);x.setDate(x.getDate()+n);return x;}
function sameDay(a,b){return a.getFullYear()===b.getFullYear()&&a.getMonth()===b.getMonth()&&a.getDate()===b.getDate();}
function fmtTime(d){return new Date(d).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});}

function CalendarView({C,onBack,meetings,setMeetings,calViewMode,setCalViewMode,calDate,setCalDate,showMeetingModal,setShowMeetingModal,editingMeeting,setEditingMeeting,addToast}){
  const token=()=>localStorage.getItem("sdr_token")||"";

  const saveMeeting=async(payload)=>{
    try{
      const isEdit=editingMeeting&&editingMeeting.id;
      const r=await fetch(isEdit?"/api/meetings/"+editingMeeting.id:"/api/meetings",{
        method:isEdit?"PUT":"POST",
        headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},
        body:JSON.stringify(payload),
      });
      const d=await r.json();
      if(!r.ok)throw new Error(d.error||"Save failed");
      setMeetings(p=>isEdit?p.map(m=>m.id===d.id?d:m):[...p,d]);
      setShowMeetingModal(false);setEditingMeeting(null);
      addToast(isEdit?"Meeting updated":"Meeting created","success");
    }catch(e){addToast(e.message,"error");}
  };
  const deleteMeeting=async(id)=>{
    if(!window.confirm("Delete this meeting?"))return;
    try{
      await fetch("/api/meetings/"+id,{method:"DELETE",headers:{Authorization:"Bearer "+token()}});
      setMeetings(p=>p.filter(m=>m.id!==id));
      setShowMeetingModal(false);setEditingMeeting(null);
      addToast("Meeting deleted","info");
    }catch(e){addToast("Couldn't delete: "+e.message,"error");}
  };

  const openNewMeeting=(start,end)=>{
    setEditingMeeting({start:start.toISOString(),end:(end||new Date(start.getTime()+30*60000)).toISOString(),title:"",attendees:[],location:"",description:""});
    setShowMeetingModal(true);
  };
  const openEditMeeting=(m)=>{setEditingMeeting({...m});setShowMeetingModal(true);};

  const meetingsOn=(day)=>meetings.filter(m=>sameDay(new Date(m.start),day)).sort((a,b)=>new Date(a.start)-new Date(b.start));

  const navigate=(dir)=>{
    const amount=calViewMode==="day"?1:calViewMode==="week"?7:30;
    setCalDate(d=>{
      if(calViewMode==="month"){const x=new Date(d);x.setMonth(x.getMonth()+dir);return x;}
      return addDays(d,dir*amount);
    });
  };

  const rangeLabel=(()=>{
    if(calViewMode==="day")return calDate.toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"});
    if(calViewMode==="week"){const s=startOfWeek(calDate),e=addDays(s,6);return s.toLocaleDateString("en-US",{month:"short",day:"numeric"})+" – "+e.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});}
    return calDate.toLocaleDateString("en-US",{month:"long",year:"numeric"});
  })();

  const daysToShow=calViewMode==="day"?[calDate]:calViewMode==="week"?Array.from({length:7},(_,i)=>addDays(startOfWeek(calDate),i)):[];

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"'Segoe UI',Calibri,sans-serif"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:"1px solid "+C.ribbonBorder,flexShrink:0,background:"#fff"}}>
        <button onClick={onBack} style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"5px 10px",fontSize:12,cursor:"pointer",color:C.text}}> Back to Mail</button>
        <button onClick={()=>openNewMeeting(new Date())} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>+ New Meeting</button>
        <div style={{display:"flex",gap:4,marginLeft:8}}>
          <button onClick={()=>setCalDate(new Date())} style={{background:"#fff",border:"1px solid "+C.border,borderRadius:4,padding:"5px 10px",fontSize:12,cursor:"pointer"}}>Today</button>
          <button onClick={()=>navigate(-1)} aria-label="Previous period" style={{background:"#fff",border:"1px solid "+C.border,borderRadius:4,padding:"5px 9px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",color:C.text}}><RIcon name="arrowLeft" size={13}/></button>
          <button onClick={()=>navigate(1)} aria-label="Next period" style={{background:"#fff",border:"1px solid "+C.border,borderRadius:4,padding:"5px 9px",fontSize:12,cursor:"pointer",display:"flex",alignItems:"center",color:C.text}}><RIcon name="chevronRight" size={13}/></button>
        </div>
        <span style={{fontSize:15,fontWeight:600,color:C.text,marginLeft:6}}>{rangeLabel}</span>
        <span style={{flex:1}}/>
        <div style={{display:"flex",gap:2,background:"#f3f2f1",borderRadius:6,padding:2}}>
          {["day","week","month"].map(v=>(
            <button key={v} onClick={()=>setCalViewMode(v)} style={{background:calViewMode===v?"#fff":"transparent",border:"none",borderRadius:4,padding:"5px 14px",fontSize:12,fontWeight:calViewMode===v?600:400,cursor:"pointer",color:calViewMode===v?C.accent:C.textSub,boxShadow:calViewMode===v?"0 1px 3px rgba(0,0,0,.15)":"none",textTransform:"capitalize"}}>{v}</button>
          ))}
        </div>
      </div>

      {(calViewMode==="day"||calViewMode==="week")&&(
        <div style={{flex:1,overflow:"auto",display:"flex",flexDirection:"column"}}>
          <div style={{display:"grid",gridTemplateColumns:"56px repeat("+daysToShow.length+",1fr)",borderBottom:"1px solid "+C.border,flexShrink:0,position:"sticky",top:0,background:"#fff",zIndex:2}}>
            <div/>
            {daysToShow.map((d,i)=>(
              <div key={i} onClick={()=>{setCalDate(d);setCalViewMode("day");}} style={{padding:"8px 4px",textAlign:"center",cursor:"pointer",borderLeft:"1px solid "+C.border}}>
                <div style={{fontSize:11,color:C.textSub}}>{DAY_NAMES[d.getDay()]}</div>
                <div style={{fontSize:16,fontWeight:600,color:sameDay(d,new Date())?C.accent:C.text}}>{d.getDate()}</div>
              </div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"56px repeat("+daysToShow.length+",1fr)",flex:1}}>
            <div>
              {HOURS.map(h=>(
                <div key={h} style={{height:52,borderBottom:"1px solid "+C.border,fontSize:10,color:C.textSub,textAlign:"right",paddingRight:6,boxSizing:"border-box"}}>
                  {h===12?"12 PM":h>12?(h-12)+" PM":h+" AM"}
                </div>
              ))}
            </div>
            {daysToShow.map((d,di)=>(
              <div key={di} style={{position:"relative",borderLeft:"1px solid "+C.border}}>
                {HOURS.map(h=>(
                  <div key={h} onClick={()=>{const s=new Date(d);s.setHours(h,0,0,0);openNewMeeting(s);}}
                    style={{height:52,borderBottom:"1px solid "+C.border,cursor:"pointer"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f8f7f6"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}/>
                ))}
                {meetingsOn(d).map(m=>{
                  const s=new Date(m.start),e=new Date(m.end);
                  const startH=s.getHours()+s.getMinutes()/60, endH=e.getHours()+e.getMinutes()/60;
                  const top=Math.max(0,(startH-7)*52), height=Math.max(20,(endH-startH)*52);
                  return(
                    <div key={m.id} onClick={ev=>{ev.stopPropagation();openEditMeeting(m);}}
                      style={{position:"absolute",top,height,left:2,right:2,background:m.color||C.accent,color:"#fff",borderRadius:4,padding:"3px 6px",fontSize:11,cursor:"pointer",overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}>
                      <div style={{fontWeight:600,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.title}</div>
                      <div style={{opacity:.85,fontSize:10}}>{fmtTime(m.start)}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {calViewMode==="month"&&(()=>{
        const first=new Date(calDate.getFullYear(),calDate.getMonth(),1);
        const gridStart=startOfWeek(first);
        const cells=Array.from({length:42},(_,i)=>addDays(gridStart,i));
        return(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"auto"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid "+C.border}}>
              {DAY_NAMES.map(d=><div key={d} style={{padding:"6px 8px",fontSize:11,color:C.textSub,textAlign:"center"}}>{d}</div>)}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gridTemplateRows:"repeat(6,1fr)",flex:1}}>
              {cells.map((d,i)=>{
                const inMonth=d.getMonth()===calDate.getMonth();
                const evs=meetingsOn(d);
                return(
                  <div key={i} onClick={()=>{const s=new Date(d);s.setHours(9,0,0,0);openNewMeeting(s);}}
                    style={{border:"1px solid "+C.border,padding:4,cursor:"pointer",opacity:inMonth?1:.4,minHeight:70,overflow:"hidden"}}
                    onMouseEnter={e=>e.currentTarget.style.background="#f8f7f6"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{fontSize:11,fontWeight:sameDay(d,new Date())?700:400,color:sameDay(d,new Date())?C.accent:C.text,marginBottom:2}}>{d.getDate()}</div>
                    {evs.slice(0,3).map(m=>(
                      <div key={m.id} onClick={ev=>{ev.stopPropagation();openEditMeeting(m);}}
                        style={{background:m.color||C.accent,color:"#fff",fontSize:10,borderRadius:3,padding:"1px 4px",marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {m.title}
                      </div>
                    ))}
                    {evs.length>3&&<div style={{fontSize:9,color:C.textSub}}>+{evs.length-3} more</div>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {showMeetingModal&&editingMeeting&&(
        <Modal open={showMeetingModal&&!!editingMeeting} onClose={()=>{setShowMeetingModal(false);setEditingMeeting(null);}} width={440} ariaLabel={editingMeeting.id?"Edit Meeting":"New Meeting"}>
          <div style={{padding:22}}>
            <p style={{fontSize:15,fontWeight:600,marginBottom:14}}>{editingMeeting.id?"Edit Meeting":"New Meeting"}</p>
            <input value={editingMeeting.title||""} onChange={e=>setEditingMeeting(p=>({...p,title:e.target.value}))} placeholder="Meeting title"
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:8,boxSizing:"border-box"}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <label style={{fontSize:10,color:C.textSub,display:"block",marginBottom:2}}>Start</label>
                <input type="datetime-local" value={editingMeeting.start?new Date(new Date(editingMeeting.start).getTime()-new Date().getTimezoneOffset()*60000).toISOString().slice(0,16):""}
                  onChange={e=>setEditingMeeting(p=>({...p,start:new Date(e.target.value).toISOString()}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"7px 8px",fontSize:12,boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,color:C.textSub,display:"block",marginBottom:2}}>End</label>
                <input type="datetime-local" value={editingMeeting.end?new Date(new Date(editingMeeting.end).getTime()-new Date().getTimezoneOffset()*60000).toISOString().slice(0,16):""}
                  onChange={e=>setEditingMeeting(p=>({...p,end:new Date(e.target.value).toISOString()}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"7px 8px",fontSize:12,boxSizing:"border-box"}}/>
              </div>
            </div>
            <input value={editingMeeting.location||""} onChange={e=>setEditingMeeting(p=>({...p,location:e.target.value}))} placeholder="Location (optional)"
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:8,boxSizing:"border-box"}}/>
            <input value={(editingMeeting.attendees||[]).join(", ")} onChange={e=>setEditingMeeting(p=>({...p,attendees:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)}))} placeholder="Attendee emails, comma separated (optional)"
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:8,boxSizing:"border-box"}}/>
            <textarea value={editingMeeting.description||""} onChange={e=>setEditingMeeting(p=>({...p,description:e.target.value}))} placeholder="Notes (optional)" rows={3}
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:14,boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div>
                {editingMeeting.id&&(
                  <button onClick={()=>deleteMeeting(editingMeeting.id)} style={{background:"#FDE7E9",color:"#A4262C",border:"none",borderRadius:6,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>Delete</button>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setShowMeetingModal(false);setEditingMeeting(null);}} style={{background:"#f3f2f1",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>saveMeeting(editingMeeting)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Save</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const TASK_STATUS_LABELS={notStarted:"Not Started",inProgress:"In Progress",waiting:"Waiting on someone else",deferred:"Deferred",completed:"Completed"};
const TASK_PRIORITY_CFG={low:{label:"Low",color:"#0078D4"},normal:{label:"Normal",color:"#605e5c"},high:{label:"High",color:"#D13438"}};

// Tasks (P3 #16) — mirrors CalendarView's structure/props pattern above
// (fetch/save/delete owned by this component, list state lifted to the
// parent so the New Items ribbon dropdown and Ctrl+Shift+K can also open
// the same "new task" modal from outside this view).
function TasksView({C,onBack,tasks,setTasks,taskFilter,setTaskFilter,showTaskModal,setShowTaskModal,editingTask,setEditingTask,addToast}){
  const token=()=>localStorage.getItem("sdr_token")||"";

  const saveTask=async(payload)=>{
    if(!payload.title||!payload.title.trim()){addToast("Give the task a title","error");return;}
    try{
      const isEdit=editingTask&&editingTask.id;
      const r=await fetch(isEdit?"/api/tasks/"+editingTask.id:"/api/tasks",{
        method:isEdit?"PUT":"POST",
        headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},
        body:JSON.stringify(payload),
      });
      const d=await r.json();
      if(!r.ok)throw new Error(d.error||"Save failed");
      setTasks(p=>isEdit?p.map(t=>t.id===d.id?d:t):[...p,d]);
      setShowTaskModal(false);setEditingTask(null);
      addToast(isEdit?"Task updated":"Task created","success");
    }catch(e){addToast(e.message,"error");}
  };
  const deleteTask=async(id)=>{
    if(!window.confirm("Delete this task?"))return;
    try{
      await fetch("/api/tasks/"+id,{method:"DELETE",headers:{Authorization:"Bearer "+token()}});
      setTasks(p=>p.filter(t=>t.id!==id));
      setShowTaskModal(false);setEditingTask(null);
      addToast("Task deleted","info");
    }catch(e){addToast("Couldn't delete: "+e.message,"error");}
  };
  const toggleComplete=async(t)=>{
    const nextStatus=t.status==="completed"?"notStarted":"completed";
    try{
      const r=await fetch("/api/tasks/"+t.id,{method:"PUT",headers:{"Content-Type":"application/json",Authorization:"Bearer "+token()},body:JSON.stringify({status:nextStatus})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error||"Update failed");
      setTasks(p=>p.map(x=>x.id===d.id?d:x));
    }catch(e){addToast("Couldn't update: "+e.message,"error");}
  };

  const openNewTask=()=>{setEditingTask({title:"",notes:"",dueDate:null,priority:"normal",status:"notStarted"});setShowTaskModal(true);};
  const openEditTask=(t)=>{setEditingTask({...t});setShowTaskModal(true);};

  const visible=tasks
    .filter(t=>taskFilter==="all"?true:taskFilter==="completed"?t.status==="completed":t.status!=="completed")
    .slice()
    .sort((a,b)=>{
      if(!!a.dueDate!==!!b.dueDate)return a.dueDate?-1:1; // tasks with a due date first
      if(a.dueDate&&b.dueDate)return new Date(a.dueDate)-new Date(b.dueDate);
      return new Date(b.createdAt||0)-new Date(a.createdAt||0);
    });
  const now=new Date();

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",fontFamily:"'Segoe UI',Calibri,sans-serif"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:"1px solid "+C.ribbonBorder,flexShrink:0,background:"#fff"}}>
        <button onClick={onBack} style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"5px 10px",fontSize:12,cursor:"pointer",color:C.text}}> Back to Mail</button>
        <button onClick={openNewTask} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>+ New Task</button>
        <span style={{fontSize:15,fontWeight:600,color:C.text,marginLeft:6}}>Tasks</span>
        <span style={{flex:1}}/>
        <div style={{display:"flex",gap:2,background:"#f3f2f1",borderRadius:6,padding:2}}>
          {["active","completed","all"].map(v=>(
            <button key={v} onClick={()=>setTaskFilter(v)} style={{background:taskFilter===v?"#fff":"transparent",border:"none",borderRadius:4,padding:"5px 14px",fontSize:12,fontWeight:taskFilter===v?600:400,cursor:"pointer",color:taskFilter===v?C.accent:C.textSub,boxShadow:taskFilter===v?"0 1px 3px rgba(0,0,0,.15)":"none",textTransform:"capitalize"}}>{v}</button>
          ))}
        </div>
      </div>

      <div style={{flex:1,overflow:"auto"}}>
        {visible.length===0?(
          <div style={{padding:40,textAlign:"center",color:C.textSub,fontSize:13}}>No {taskFilter==="all"?"":taskFilter} tasks.</div>
        ):visible.map(t=>{
          const overdue=t.dueDate&&t.status!=="completed"&&new Date(t.dueDate)<now;
          const pCfg=TASK_PRIORITY_CFG[t.priority]||TASK_PRIORITY_CFG.normal;
          return(
            <div key={t.id} onClick={()=>openEditTask(t)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderBottom:"1px solid "+C.border,cursor:"pointer",background:"#fff"}}
              onMouseEnter={e=>e.currentTarget.style.background="#f8f7f6"} onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
              <input type="checkbox" checked={t.status==="completed"} onClick={e=>e.stopPropagation()} onChange={()=>toggleComplete(t)}
                aria-label={t.status==="completed"?"Mark "+t.title+" incomplete":"Mark "+t.title+" complete"}
                style={{width:15,height:15,flexShrink:0,cursor:"pointer"}}/>
              {t.priority==="high"&&<span title="High priority" style={{color:pCfg.color,fontSize:13,flexShrink:0}}>&#9873;</span>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,color:t.status==="completed"?C.textSub:C.text,textDecoration:t.status==="completed"?"line-through":"none",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</div>
                <div style={{fontSize:11,color:overdue?C.red:C.textSub}}>
                  {t.dueDate?("Due "+new Date(t.dueDate).toLocaleDateString()+(overdue?" — Overdue":"")):"No due date"}
                  {t.status!=="notStarted"&&t.status!=="completed"?" · "+TASK_STATUS_LABELS[t.status]:""}
                </div>
              </div>
              <button onClick={e=>{e.stopPropagation();deleteTask(t.id);}} title="Delete task" aria-label={"Delete task "+t.title}
                style={{background:"none",border:"none",color:C.textSub,cursor:"pointer",fontSize:16,padding:"2px 6px",flexShrink:0}}>&times;</button>
            </div>
          );
        })}
      </div>

      {showTaskModal&&editingTask&&(
        <Modal open={showTaskModal&&!!editingTask} onClose={()=>{setShowTaskModal(false);setEditingTask(null);}} width={420} ariaLabel={editingTask.id?"Edit Task":"New Task"}>
          <div style={{padding:22}}>
            <p style={{fontSize:15,fontWeight:600,marginBottom:14}}>{editingTask.id?"Edit Task":"New Task"}</p>
            <input autoFocus value={editingTask.title||""} onChange={e=>setEditingTask(p=>({...p,title:e.target.value}))} placeholder="Task title"
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:8,boxSizing:"border-box"}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              <div>
                <label style={{fontSize:10,color:C.textSub,display:"block",marginBottom:2}}>Due date</label>
                <input type="date" value={editingTask.dueDate?new Date(editingTask.dueDate).toISOString().slice(0,10):""}
                  onChange={e=>setEditingTask(p=>({...p,dueDate:e.target.value?new Date(e.target.value+"T12:00:00").toISOString():null}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"7px 8px",fontSize:12,boxSizing:"border-box"}}/>
              </div>
              <div>
                <label style={{fontSize:10,color:C.textSub,display:"block",marginBottom:2}}>Priority</label>
                <select value={editingTask.priority||"normal"} onChange={e=>setEditingTask(p=>({...p,priority:e.target.value}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"7px 4px",fontSize:12,boxSizing:"border-box"}}>
                  {Object.entries(TASK_PRIORITY_CFG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:10,color:C.textSub,display:"block",marginBottom:2}}>Status</label>
                <select value={editingTask.status||"notStarted"} onChange={e=>setEditingTask(p=>({...p,status:e.target.value}))}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"7px 4px",fontSize:12,boxSizing:"border-box"}}>
                  {Object.entries(TASK_STATUS_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <textarea value={editingTask.notes||""} onChange={e=>setEditingTask(p=>({...p,notes:e.target.value}))} placeholder="Notes (optional)" rows={3}
              style={{width:"100%",border:"1px solid "+C.border,borderRadius:6,padding:"8px 10px",fontSize:13,marginBottom:14,boxSizing:"border-box",resize:"vertical",fontFamily:"inherit"}}/>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div>
                {editingTask.id&&(
                  <button onClick={()=>deleteTask(editingTask.id)} style={{background:"#FDE7E9",color:"#A4262C",border:"none",borderRadius:6,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>Delete</button>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setShowTaskModal(false);setEditingTask(null);}} style={{background:"#f3f2f1",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>saveTask(editingTask)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:6,padding:"7px 16px",fontSize:12,fontWeight:600,cursor:"pointer"}}>Save</button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Status/Info/Notes/Activity body shared between the full-page Lead Detail
// view (reached via the Leads/Contacts nav) and the collapsible contact
// panel that now sits beside an open conversation whose sender matches a
// known contact (see the isMail reading-pane block below). Extracted so
// both places render the exact same real data the same way instead of two
// copies drifting apart — `compact` only adjusts sizing/layout, never which
// data is shown.
// "View Sequence Progress" (Phase 5) — same localStorage+reload handoff
// Workspace's own "People" sidebar icon already uses to jump the main app
// shell to a different tab (see the "contacts" nav icon's onClick, which
// sets sdr_page then navigates to "/"). Workspace itself is a standalone
// popup window (a different browser window than the main app shell, per
// this redesign's architecture decision to keep Workspace/Mail Merge
// separate popups rather than merge them with Sequences), so this can't
// just call a setActive() prop the way the main shell's own nav does —
// it navigates THIS window away to "/" and leaves a one-shot breadcrumb in
// localStorage for SequenceBuilder.js to pick up on load and search across
// every sequence's enrollments for this contact's email.
function viewSequenceProgress(contact){
  if(!contact?.email)return;
  localStorage.setItem("sdr_focus_contact_email",contact.email);
  localStorage.setItem("sdr_page","sequences");
  window.location.href="/";
}

function ContactDetailContent({contact,note,setNote,onAddNote,onStatusChange,onEmail,onOpportunity,compact}){
  if(!contact)return null;
  const labelStyle={fontSize:11,color:C.textSub,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:6};
  const actionBtnPrimary={background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"6px 14px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"};
  const actionBtnSecondary={background:"#f3f2f1",color:C.text,border:"1px solid "+C.border,borderRadius:3,padding:"6px 14px",fontSize:12,cursor:"pointer"};
  // Real activity, not the old hardcoded 2-entry stand-in: the contact's
  // actual creation date (the old "Contact imported" line read
  // `selectedItem.importedAt`, a field that only ever existed on
  // mock-server fixtures — real /api/leads data has never had it, so this
  // line silently rendered a blank date for every real contact) plus every
  // real note on file. Sequence step history joins this list once Phase 4
  // ships a real per-enrollment history to read from.
  const activity=[];
  if(contact.createdAt)activity.push({text:"Contact added",time:contact.createdAt});
  (contact.notes||[]).forEach(n=>{
    if(!n)return;
    const preview=(n.text||"").length>90?(n.text||"").slice(0,90)+"":(n.text||"");
    activity.push({text:"Note added: "+preview,time:n.createdAt});
  });
  activity.sort((a,b)=>new Date(b.time||0)-new Date(a.time||0));
  return(
    <>
      <div style={{display:"flex",alignItems:"flex-start",gap:compact?10:14,marginBottom:compact?12:16,paddingBottom:compact?10:14,borderBottom:"1px solid "+C.border}}>
        <div style={{width:compact?40:52,height:compact?40:52,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:compact?16:22,fontWeight:700,flexShrink:0}}>
          {(contact.first||contact.name||"?").slice(0,1).toUpperCase()}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <p style={{fontSize:compact?15:20,fontWeight:700,color:C.text,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{contact.name}</p>
          <p style={{fontSize:compact?11:13,color:C.textSub,marginBottom:6,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{contact.title}{contact.company?"  "+contact.company:""}</p>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <a href={"mailto:"+contact.email} style={{fontSize:compact?11:12,color:C.accent,textDecoration:"none",wordBreak:"break-all"}}> {contact.email}</a>
            {contact.linkedin&&<a href={contact.linkedin} target="_blank" rel="noreferrer" style={{fontSize:compact?11:12,color:C.accent,textDecoration:"none"}}> LinkedIn</a>}
          </div>
        </div>
        {!compact&&(
          <div style={{display:"flex",gap:6,flexShrink:0,flexWrap:"wrap"}}>
            <button onClick={()=>onEmail(contact)} style={actionBtnPrimary}> Email</button>
            <button onClick={()=>onOpportunity(contact)} style={actionBtnSecondary}> Opportunity</button>
            <button onClick={()=>viewSequenceProgress(contact)} title="Hand off to Sequences and find this contact's enrollment" style={actionBtnSecondary}> Sequence Progress</button>
          </div>
        )}
      </div>
      {compact&&(
        <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
          <button onClick={()=>onEmail(contact)} style={{...actionBtnPrimary,flex:1}}> Email</button>
          <button onClick={()=>onOpportunity(contact)} style={{...actionBtnSecondary,flex:1}}> Opportunity</button>
          <button onClick={()=>viewSequenceProgress(contact)} title="Hand off to Sequences and find this contact's enrollment" style={{...actionBtnSecondary,flex:1}}> Sequence Progress</button>
        </div>
      )}
      {/* Status */}
      <div style={{marginBottom:14}}>
        <p style={labelStyle}>Lead Status</p>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {LEAD_STATUSES.map(s=>{
            const sc=STATUS_CFG[s]||STATUS_CFG["New"];
            const isA=(contact.leadStatus||"New")===s;
            return(
              <button key={s} onClick={()=>onStatusChange(contact.id,s)}
                style={{background:isA?sc.bg:"#fff",color:isA?sc.tc:C.textSub,border:"1px solid "+(isA?sc.tc:C.border),borderRadius:3,padding:"4px 10px",fontSize:11,cursor:"pointer",fontWeight:isA?600:400,fontFamily:"'Segoe UI',sans-serif"}}>
                {s}
              </button>
            );
          })}
        </div>
      </div>
      {/* Info */}
      <div style={{display:"grid",gridTemplateColumns:compact?"1fr":"1fr 1fr",gap:8,marginBottom:14}}>
        {[{l:"Company",v:contact.company},{l:"Title",v:contact.title},{l:"Email",v:contact.email},{l:"Location",v:contact.location},{l:"Score",v:contact.score},{l:"Source",v:contact.source||"Import"}].map(({l,v})=>(
          <div key={l} style={{background:"#f8f7f6",border:"1px solid "+C.border,borderRadius:3,padding:"8px 10px",minWidth:0}}>
            <p style={{fontSize:10,color:C.textSub,fontWeight:700,textTransform:"uppercase",letterSpacing:".5px",marginBottom:2}}>{l}</p>
            <p style={{fontSize:13,color:C.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v||""}</p>
          </div>
        ))}
      </div>
      {/* Notes */}
      <div style={{marginBottom:12}}>
        <p style={labelStyle}>Notes</p>
        {(contact.notes||[]).map(n=>(
          <div key={n.id} style={{background:"#fff",border:"1px solid "+C.border,borderRadius:3,padding:"8px 12px",marginBottom:5}}>
            <p style={{fontSize:12,color:C.text,lineHeight:1.5}}>{n.text}</p>
            <p style={{fontSize:10,color:C.textMuted,marginTop:3}}>{n.author||"SDR"}  {n.createdAt?new Date(n.createdAt).toLocaleString():""}</p>
          </div>
        ))}
        <div style={{display:"flex",gap:6,marginTop:6}}>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Add a note..." onKeyDown={e=>e.key==="Enter"&&onAddNote(contact.id)}
            style={{flex:1,background:"#fff",border:"1px solid "+C.border,borderRadius:3,padding:"6px 10px",fontSize:12,color:C.text,outline:"none",fontFamily:"'Segoe UI',sans-serif",minWidth:0}}/>
          <button onClick={()=>onAddNote(contact.id)} style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"6px 14px",fontSize:12,cursor:"pointer",flexShrink:0}}>Add</button>
        </div>
      </div>
      {/* Activity  now built from real data above instead of hardcoded */}
      <div>
        <p style={labelStyle}>Activity Timeline</p>
        {activity.length===0&&<p style={{fontSize:12,color:C.textMuted}}>No activity yet.</p>}
        {activity.map((a,i)=>(
          <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"5px 0",borderBottom:"1px solid #f0efee"}}>
            <span style={{fontSize:14,width:20,flexShrink:0,textAlign:"center"}}></span>
            <div style={{minWidth:0}}>
              <p style={{fontSize:12,color:C.text,overflowWrap:"break-word"}}>{a.text}</p>
              <p style={{fontSize:10,color:C.textMuted}}>{a.time?new Date(a.time).toLocaleString():""}</p>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default function OutlookWorkspace({settings={},addToast=()=>{},onLogout,onOpenSettings}){
  const { effectiveOffline, browserOnline, isSyncing, queue, toggleOffline, syncQueue, clearQueue, removeFromQueue } = useWorkOffline(addToast);
  const [showQueue,setShowQueue]=useState(false);
  const [showFileMenu,setShowFileMenu]=useState(false);
  const [ribbonTab,setRibbonTab]=useState("home");
  // Move and Categorize each still need their open state held here (rather
  // than inside <RibbonDropdownButton>'s own default internal state) because
  // they close each other via onOpen, and Move's menu body also branches on
  // showMoveOtherFolders, a second piece of state that has to keep living
  // alongside it. Schedule's open state also stays here because
  // scheduleSend() (below) closes that menu itself on a successful send.
  // New Items (formerly showNewItemsMenu, plus all four menus' own anchor
  // refs) no longer needs anything declared here — <RibbonDropdownButton>
  // now owns that scaffolding internally. See RibbonPrimitives.js.
  const [showMoveMenu,setShowMoveMenu]=useState(false);
  const [showMoveOtherFolders,setShowMoveOtherFolders]=useState(false);
  const [showCategoryMenu,setShowCategoryMenu]=useState(false);
  const [showScheduleMenu,setShowScheduleMenu]=useState(false);
  const [scheduleAt,setScheduleAt]=useState("");
  const [scheduling,setScheduling]=useState(false);
  // Quick Access Toolbar customization (P3 #18) — which commands show, and
  // in what order; persisted locally so it survives a reload (real Outlook
  // stores QAT customization per-user too, just via a roaming profile
  // instead of localStorage — this is the same idea scoped to what a
  // single-device web app can actually do).
  const [qatCommandIds,setQatCommandIds]=useState(()=>{
    try{
      const saved=JSON.parse(localStorage.getItem(QAT_STORAGE_KEY));
      if(Array.isArray(saved)&&saved.length)return saved.filter(id=>QAT_COMMANDS.some(c=>c.id===id));
    }catch(e){}
    return QAT_DEFAULT_IDS;
  });
  const [showQatCustomize,setShowQatCustomize]=useState(false);
  useEffect(()=>{localStorage.setItem(QAT_STORAGE_KEY,JSON.stringify(qatCommandIds));},[qatCommandIds]);
  const searchInputRef=useRef(null);
  const [activeFolder,setActiveFolder]=useState("inbox");
  const [mainView,setMainView]=useState("mail"); // "mail" | "calendar" | "tasks"
  const [calViewMode,setCalViewMode]=useState("week"); // "day" | "week" | "month"
  const [calDate,setCalDate]=useState(new Date());
  const [meetings,setMeetings]=useState([]);
  const [showMeetingModal,setShowMeetingModal]=useState(false);
  const [editingMeeting,setEditingMeeting]=useState(null); // meeting object or {start,end} for a new one
  const [tasksList,setTasksList]=useState([]);
  const [taskFilter,setTaskFilter]=useState("active"); // "active" | "completed" | "all"
  const [showTaskModal,setShowTaskModal]=useState(false);
  const [editingTask,setEditingTask]=useState(null); // task object or {} for a new one
  const [expanded,setExpanded]=useState({Mail:true,"Campaign Management":true,CRM:false,Productivity:false,"Team Management":false});
  const [folderItems,setFolderItems]=useState([]);
  const [folderLoading,setFolderLoading]=useState(false);
  const [unreadCounts,setUnreadCounts]=useState({});
  const [customFolders,setCustomFolders]=useState({});
  const [expandedFolders,setExpandedFolders]=useState({});
  const [folderCtxMenu,setFolderCtxMenu]=useState(null); // {x,y,accountEmail,folderId,label,canonical}
  const [emailCtxMenu,setEmailCtxMenu]=useState(null); // {x,y,thread,msg}
  const [leadCtxMenu,setLeadCtxMenu]=useState(null); // {x,y,lead}
  const [campaignCtxMenu,setCampaignCtxMenu]=useState(null); // {x,y,campaign}
  const [seqCtxMenu,setSeqCtxMenu]=useState(null); // {x,y,seq}
  const [propertiesModal,setPropertiesModal]=useState(null); // {thread,msg}
  const [attachmentPreview,setAttachmentPreview]=useState(null); // {url,filename,contentType}
  // Rules engine (P3 #15): rulesModal is the list/manage view; ruleEditor is
  // the create/edit sub-view (null when not editing a rule). Kept as two
  // separate, sibling overlays (not nested) so each gets its own
  // independent focus-trap/Escape via useModalA11y below.
  const [rulesModal,setRulesModal]=useState(false);
  const [rulesList,setRulesList]=useState([]);
  const [rulesLoading,setRulesLoading]=useState(false);
  const [ruleEditor,setRuleEditor]=useState(null); // {id?,name,matchType,conditions,action,targetFolder,markAsRead,alsoCleanExisting}
  // Inbox Zero: a unified panel surfacing Sweep (single-sender standing
  // rules) and Rules (multi-condition auto-filing) as one headline feature.
  // Sweep already existed server-side with a create-only window.prompt()
  // flow (see sweepSender below) but no way to ever see or remove an
  // existing Sweep rule — this finally gives it a real management UI and
  // puts both automation systems in one visible place instead of split
  // across a right-click context-menu action and a separate ribbon button.
  const [inboxZeroModal,setInboxZeroModal]=useState(false);
  const [sweepRules,setSweepRules]=useState([]);
  const [sweepLoading,setSweepLoading]=useState(false);
  const [sweepEditor,setSweepEditor]=useState(null); // {senderEmail,action,targetFolder,alsoCleanExisting} while the "New Sweep Rule" form is open
  // Focus-trap + Escape-to-close for every context menu and dialog in this
  // file (Workspace UX audit item 8) — see useModalA11y.js. Each menu/dialog
  // already closes on backdrop click; this only adds keyboard behavior.
  // Phase 4b: folder/email/lead/campaign/seq context menus, plus the
  // properties/create-folder/rules/rule-editor/inbox-zero/sweep-editor
  // dialogs, now use the shared <ContextMenu>/<Modal> (ui/ContextMenu.js,
  // ui/Modal.js), which each own their own internal ref + useModalA11y call
  // — their standalone refs/hooks here are removed accordingly (nothing
  // else referenced them). attachmentPreview keeps its own below — it's a
  // content-sized dialog (image/PDF), not a fixed-width fit for Modal.
  const attachmentPreviewRef=useRef(null);
  useModalA11y(!!attachmentPreview,()=>setAttachmentPreview(null),attachmentPreviewRef);
  // Previewable inline (images render directly, PDFs render via <iframe> —
  // both are natively renderable by the browser with no extra library);
  // everything else stays download-only, same as before this task.
  const isPreviewableAttachment=(a)=>{
    const ct=a?.contentType||"";
    return ct.startsWith("image/")||ct==="application/pdf";
  };
  // Real, recognizable file-type badges for the attachment chips below —
  // matches how Outlook/Gmail themselves distinguish Word/PDF/Excel/etc at a
  // glance (a colored monogram), rather than a single generic paperclip icon
  // for everything. Checks contentType first (what Microsoft Graph actually
  // sends) and falls back to the filename extension for anything Graph
  // reports as a generic octet-stream.
  const getAttachmentKind=(a)=>{
    const ct=(a?.contentType||"").toLowerCase();
    const name=(a?.filename||"").toLowerCase();
    const ext=name.includes(".")?name.split(".").pop():"";
    if(ct.startsWith("image/")) return "image";
    if(ct==="application/pdf"||ext==="pdf") return "pdf";
    if(ct.includes("word")||["doc","docx"].includes(ext)) return "word";
    if(ct.includes("sheet")||ct.includes("excel")||["xls","xlsx","csv"].includes(ext)) return "excel";
    if(ct.includes("presentation")||["ppt","pptx"].includes(ext)) return "powerpoint";
    if(ct.includes("zip")||ct.includes("compressed")||["zip","rar","7z"].includes(ext)) return "zip";
    if(ct.startsWith("audio/")) return "audio";
    if(ct.startsWith("video/")) return "video";
    if(ct.startsWith("text/")||ext==="txt") return "text";
    return "generic";
  };
  const ATTACHMENT_VISUALS={
    pdf:{bg:"#c0272d",label:"PDF"},
    word:{bg:"#2b579a",label:"DOC"},
    excel:{bg:"#217346",label:"XLS"},
    powerpoint:{bg:"#d24726",label:"PPT"},
    zip:{bg:"#8a8886",label:"ZIP"},
    audio:{bg:"#6b69d6",label:"AUD"},
    video:{bg:"#c239b3",label:"VID"},
    text:{bg:"#605e5c",label:"TXT"},
    generic:{bg:"#605e5c",label:"FILE"},
  };
  const [leads,setLeads]=useState([]);
  const [campaigns,setCampaigns]=useState([]);
  const [opps,setOpps]=useState([]);
  const [sequences,setSequences]=useState([]);
  const [senders,setSenders]=useState([]);
  const [contactFiles,setContactFiles]=useState([]);
  // Team member list-view is gone (see the removed "Team" render branch,
  // near the Templates section below, for why) — TeamPage.js already covers
  // this better and is reachable from the main app shell's own nav. The
  // fetch below still runs (harmless, and not worth restructuring the
  // Promise.all it's part of just to drop one call); setTeamMembers is kept
  // so that fetch has somewhere to put its result, the array-skip below
  // just means nothing renders it.
  const [,setTeamMembers]=useState([]);
  // Templates — the shared repository (server.js's /api/templates, see
  // emailTemplates.js), same one the main app shell's Templates page and
  // Mail Merge/Sequences read and write. Creating/editing templates still
  // stays in Mail Merge and the Templates page, where the rich-text editor
  // and merge-tag tooling live; Workspace consumes the list and can insert
  // one straight into a new compose or reply (see applyTemplateInWorkspace
  // below) rather than only "seeing" it as before. The `storage` listener
  // is what makes the list feel live rather than stale when another
  // same-origin window (Mail Merge, or the Templates page in the main tab)
  // saves/deletes a template — a native browser event, not a poll.
  const [templates,setTemplates]=useState(()=>loadTemplates([]));
  useEffect(()=>{ fetchTemplateRepo().then(setTemplates); },[]);
  useEffect(()=>{
    const onStorage=(e)=>{
      if(e.key===TEMPLATES_STORAGE_KEY) setTemplates(loadTemplates([]));
    };
    window.addEventListener("storage",onStorage);
    return ()=>window.removeEventListener("storage",onStorage);
  },[]);
  // Inserts a template straight into a fresh compose (mode="new") or as the
  // body of a reply to the currently-open conversation (mode="reply") — the
  // concrete "used from ... Workspace for fresh emails/follow-ups" behavior
  // requested, not just a read-only jump-to-Mail-Merge as before.
  const applyTemplateInWorkspace=(t,mode)=>{
    markTemplateUsed(t.id);
    if(mode==="reply"&&selectedItem){
      doReply(selectedItem,"reply");
      setCompose(p=>({...p,subject:p.subject||t.subject||"",body:t.body||""}));
    }else{
      setCompose(p=>({...p,to:"",cc:"",bcc:"",subject:t.subject||"",body:t.body||"",id:null,threadId:null,inReplyTo:null}));
      setComposing(true);setReplyMode(null);setSelectedItem(null);
    }
    addToast?.(`"${t.name}" inserted`,"success");
  };
  const [selectedItem,setSelectedItem]=useState(null);
  // Message ids whose remote images the user has explicitly chosen to load
  // via the "Download Pictures" notification bar — everything else gets its
  // remote <img> tags blocked by default (see blockRemoteImages below).
  const [unblockedImages,setUnblockedImages]=useState(()=>new Set());
  const [composing,setComposing]=useState(false);
  // Cc/Bcc start hidden behind a small "Cc Bcc" link (Gmail's convention),
  // and the signature preview + quoted "Original conversation" thread
  // start collapsed behind a "•••" toggle instead of always dumping as
  // plain text under the editor — both were previously always-visible and
  // read as cluttered/unpolished. Neither hides any actual data: Cc/Bcc
  // still send normally once filled in, and the signature/quoted thread
  // are the same content, just one click away instead of forced open.
  // Re-synced (not just reset to false) each time a compose session opens,
  // so resuming a draft that already has Cc/Bcc filled in still shows them
  // immediately rather than hiding real recipients.
  const [showCcBcc,setShowCcBcc]=useState(false);
  const [showQuoted,setShowQuoted]=useState(false);
  useEffect(()=>{
    if(composing){setShowCcBcc(!!(compose.cc||compose.bcc));setShowQuoted(false);}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[composing]);
  const [search,setSearch]=useState("");
  // Real Outlook "Instant Search" scope + refine state — the search box now
  // lives centered in the ribbon (see the tab-bar row below) and, once
  // focused, surfaces a contextual "Search" ribbon tab (like Outlook's own
  // Search Tools tab) with Scope/Refine/Options groups, matching the
  // reference screenshot. searchActive drives both: whether that tab is
  // shown/selected and whether getListItems() below applies cross-folder
  // scope instead of the plain current-folder filter it always has.
  const [searchActive,setSearchActive]=useState(false);
  const [searchScope,setSearchScope]=useState("currentMailbox"); // matches the checkmark position in the reference screenshot
  const [showScopeMenu,setShowScopeMenu]=useState(false);
  const scopeBtnRef=useRef(null);
  const [searchRefine,setSearchRefine]=useState({unread:false,flagged:false,important:false});
  const [includeDeleted,setIncludeDeleted]=useState(false);
  const [crossFolderResults,setCrossFolderResults]=useState(null); // null = current-folder-only scope, or not loaded yet
  const [crossFolderLoading,setCrossFolderLoading]=useState(false);
  const preSearchRibbonTabRef=useRef("home"); // ribbon tab to restore when Close Search is clicked
  const [mailFilter,setMailFilter]=useState("All");
  const [sending,setSending]=useState(false);
  const [aiLoading,setAiLoading]=useState(false);
  // Reading Pane position — was a plain on/off boolean; now a real
  // Right/Bottom/Off picker like Outlook Desktop's View tab. showReadPane
  // is kept as a derived boolean so the render condition and the status-bar
  // quick-toggle button (which only ever needed on/off) don't need to
  // change at all.
  const [readingPanePos,setReadingPanePos]=useState(()=>loadPaneLayout().readingPanePos||"right"); // "right" | "bottom" | "off"
  const showReadPane=readingPanePos!=="off";
  const setShowReadPane=(v)=>{ if(v){setReadingPanePos(p=>p==="off"?"right":p);} else {setReadingPanePos("off");} };
  const [isMobile,setIsMobile]=useState(()=>window.innerWidth<=768);
  const [mobileView,setMobileView]=useState("folders"); // "folders" | "list" | "reading" — single-pane navigation on small screens, matching how real mobile email apps work rather than squeezing 3 desktop panes onto a phone
  useEffect(()=>{
    const onResize=()=>setIsMobile(window.innerWidth<=768);
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);
  // Open full screen by default, matching Mail Merge's window. Best-effort
  // only — the Fullscreen API requires transient user activation and
  // commonly rejects when called on mount, so this is wrapped in a no-op
  // catch rather than relied on as guaranteed.
  useEffect(()=>{
    document.documentElement.requestFullscreen?.().catch(()=>{});
  },[]);
  useEffect(()=>{ if(isMobile&&mainView==="mail")setMobileView("list"); },[activeFolder]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ if(isMobile&&mainView==="mail"&&(selectedItem||composing))setMobileView("reading"); },[selectedItem,composing]); // eslint-disable-line react-hooks/exhaustive-deps
  // Folder Pane state — was a plain on/off boolean; now a real
  // Normal/Minimized/Off picker like Outlook Desktop's View tab. showSidebar
  // stays a derived boolean so the render condition and the status-bar
  // quick-toggle button don't need to change.
  const [sidebarMode,setSidebarMode]=useState(()=>loadPaneLayout().sidebarMode||"normal"); // "normal" | "minimized" | "off"
  const showSidebar=sidebarMode!=="off";
  const sidebarMinimized=sidebarMode==="minimized";
  const setShowSidebar=(v)=>{ if(v){setSidebarMode(p=>p==="off"?"normal":p);} else {setSidebarMode("off");} };
  // Draggable pane sizes — folder pane width, list pane width (Reading Pane
  // = Right) and list pane height (Reading Pane = Bottom). Each just
  // replaces what used to be a hardcoded number (232 / 320 / a 45%
  // split) with a piece of state a splitter can update; nothing about
  // when each pane shows or how it's laid out otherwise changes.
  const [sidebarWidth,setSidebarWidth]=useState(()=>loadPaneLayout().sidebarWidth||232);
  const [listPaneWidth,setListPaneWidth]=useState(()=>loadPaneLayout().listPaneWidth||320);
  const [listPaneHeight,setListPaneHeight]=useState(()=>loadPaneLayout().listPaneHeight||320);
  // Persists all five pane-layout values above to localStorage on any
  // change, so the layout survives a reload/reopen instead of resetting to
  // the hardcoded defaults every time — the one explicitly-approved
  // standalone behavior change in this pass (see the redesign plan).
  useEffect(()=>{
    try{
      localStorage.setItem(PANE_LAYOUT_KEY, JSON.stringify({
        readingPanePos, sidebarMode, sidebarWidth, listPaneWidth, listPaneHeight,
      }));
    }catch(e){}
  },[readingPanePos,sidebarMode,sidebarWidth,listPaneWidth,listPaneHeight]);
  const [note,setNote]=useState("");
  // Whether the contact-context side panel (next to an open conversation
  // whose sender matches a known contact — see mailContact below) is
  // expanded or collapsed to its thin edge tab. Ephemeral, not persisted:
  // it's a per-conversation glance, not a layout preference like the panes
  // above.
  const [contactPanelOpen,setContactPanelOpen]=useState(true);
  const [newOpp,setNewOpp]=useState({name:"",company:"",value:"",stage:"Discovery",probability:""});
  const [showNewOpp,setShowNewOpp]=useState(false);
  const [showNewEmailPopup,setShowNewEmailPopup]=useState(false);
  const [popupPos,setPopupPos]=useState({x:0,y:0});
  const [lastFocused,setLastFocused]=useState("subject");
  const lastFocRef=useRef("subject");
  const subjectRef=useRef(null);
  const quillRef=useRef(null);
  const fileInputRef=useRef(null);
  const [formatMarksOn,setFormatMarksOn]=useState(false);
  const [showFindReplace,setShowFindReplace]=useState(false);
  const getQuill=()=>quillRef.current?.getEditor?.();
  const [compose,setCompose]=useState({from:"",to:"",cc:"",bcc:"",subject:"",body:"",id:null,importance:"normal",readReceiptRequested:false,threadId:null,inReplyTo:null,attachments:[]});
  // Outlook-style recipient autocomplete for the To/Cc/Bcc rows below:
  // {field:"to"|"cc"|"bcc", items:[contact,...], activeIdx} while a
  // suggestion list is open for that row, else null. Only one row's
  // dropdown is ever open at a time, same as real Outlook.
  const [rcptSuggest,setRcptSuggest]=useState(null);
  // Writing Voice Guard: live, non-blocking check of whether this draft reads
  // like the sender's usual style, based on a heuristic baseline built from
  // their own sent mail (no AI call — see writingVoiceEngine.js on the
  // backend). Purely informational: never blocks Send, just a dismissible
  // hint. Dismissal is keyed to the flags themselves so it clears again once
  // the draft actually changes rather than staying suppressed forever.
  const [voiceGuardFlags,setVoiceGuardFlags]=useState([]);
  const [voiceGuardDismissed,setVoiceGuardDismissed]=useState("");
  // Voice dictation (live continuous refinement): speak into the mic, an AI
  // agent turns it into the subject/body of whatever's in the compose pane.
  const voice=useVoiceDraft({
    getQuill,
    subject:compose.subject||"",
    setSubject:(v)=>setCompose(p=>({...p,subject:v})),
    setBodyHtml:(html)=>setCompose(p=>({...p,body:html})),
    sdrSettings:settings,
    recipientContext:compose.to?`Emailing ${compose.to}`:"",
    addToast,
  });
  const [replyMode,setReplyMode]=useState(null);
  const [filterStatus,setFilterStatus]=useState("all");
  const [sortBy,setSortBy]=useState("date");
  const [sortDir,setSortDir]=useState("desc");
  const [multiSel,setMultiSel]=useState([]);
  const lastClickIdxRef=useRef(null);

  const setFoc=(f)=>{lastFocRef.current=f;setLastFocused(f);};

  useEffect(()=>{loadAll();},[]);

  // Phase 4/5 handoff: Sequence's Monitor view (and, going forward,
  // anywhere else that needs to jump a rep straight to a contact) opens
  // this popup with ?focusContact=<email>. This used to always land on the
  // static Contacts detail view (setActiveFolder("contacts")+
  // setSelectedItem(lead)) — functional, but not what "move engaged
  // prospects into the appropriate Workspace conversation" (the actual ask
  // behind this handoff) means: a rep chasing up a reply wants the
  // conversation, not the CRM record. Now it tries the contact's real
  // email conversation first, via the same /api/email/threads endpoint
  // the folder list itself uses (already supports a cross-folder "search"
  // query — no new backend endpoint needed) — no folder param, so it
  // searches every folder across every connected account for this tenant.
  // If a matching conversation exists, jump straight into it (same
  // setActiveFolder+setSelectedItem+setCompose(from) triple the sidebar's
  // own folder-click handler uses, so list panel/account/reading pane all
  // stay in sync). Falls back to the original Contacts-record behavior —
  // completely unchanged — whenever no conversation is found (a contact
  // with no mail yet, a request failure, etc.), so nothing regresses for
  // that case. Guarded by a ref so it only applies once per window
  // (repeat leads refreshes via loadAll()/refreshContact() shouldn't keep
  // yanking focus back).
  const focusContactAppliedRef=useRef(false);
  useEffect(()=>{
    if(focusContactAppliedRef.current||!leads.length)return;
    const focusEmail=(new URLSearchParams(window.location.search).get("focusContact")||"").trim().toLowerCase();
    if(!focusEmail)return;
    focusContactAppliedRef.current=true;
    const match=leads.find(l=>(l.email||"").toLowerCase()===focusEmail);
    const openContactRecord=()=>{if(match){setActiveFolder("contacts");setSelectedItem(match);}};
    (async()=>{
      try{
        const r=await fetch(`/api/email/threads?search=${encodeURIComponent(focusEmail)}&limit=10`,{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
        if(!r.ok)throw new Error("thread lookup failed");
        const threads=await r.json();
        const hit=Array.isArray(threads)?threads.find(t=>(t.from||"").toLowerCase().includes(focusEmail)||(t.to||"").toLowerCase().includes(focusEmail)):null;
        if(hit&&hit.accountEmail&&hit.folder){
          // hit.folder is the raw DB value (e.g. "trash") — activeFolder
          // uses the sidebar's own UI ids (e.g. "deleted"), same mapping
          // buildFolderTree() below already relies on via CANONICAL_ALIASES.
          const uiFolder=CANONICAL_ALIASES[hit.folder.toLowerCase()]||hit.folder;
          setComposing(false);
          setActiveFolder(uiFolder);
          setCompose(p=>({...p,from:hit.accountEmail}));
          setSelectedItem(hit);
        }else{
          openContactRecord();
        }
      }catch(e){openContactRecord();}
    })();
  },[leads]);

  const loadAll=async()=>{
    try{
      const rs=await Promise.all([
        fetch("/api/leads?limit=10000"),
        fetch("/api/campaigns"),fetch("/api/sequences"),
        fetch("/api/team/senders",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}),fetch("/api/contact-files"),
        fetch("/api/team",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}),fetch("/api/smtp/accounts",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}),
      ]);
      const [l,c,s,snd,cf,tm,smtpAccs]=await Promise.all(rs.map(r=>r.json().catch(()=>[])));
      setLeads(Array.isArray(l)?l.map(x=>({...x,leadStatus:x.leadStatus||"New",notes:x.notes||[],tags:x.tags||[]})):[]);
      setCampaigns(Array.isArray(c)?c:[]);
      setSequences(Array.isArray(s)?s:[]);
      setContactFiles(Array.isArray(cf)?cf:[]);
      setTeamMembers(Array.isArray(tm)?tm:[]);
      const smtpSenders=(Array.isArray(smtpAccs)?smtpAccs:[]).map(a=>({id:a.id||a._id,email:a.email,name:a.name||a.email,type:"smtp"}));
      const allSnd=[...(Array.isArray(snd)?snd:[]),...smtpSenders].filter((v,i,a)=>a.findIndex(x=>x.email===v.email)===i);
      setSenders(allSnd);
      if(allSnd.length>0) setCompose(p=>({...p,from:allSnd[0].email}));
      refreshUnreadCounts(allSnd);
    }catch(e){console.error("loadAll error:",e);}
  };

  const refreshUnreadCounts=async(accs)=>{
    try{
      const results=await Promise.all((accs||senders).map(s=>fetch("/api/email/folders?accountEmail="+encodeURIComponent(s.email),{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}}).then(r=>r.json()).catch(()=>[])));
      const counts={};
      const customs={};
      (accs||senders).forEach((s,i)=>{
        const list=Array.isArray(results[i])?results[i]:[];
        const inboxFolder=list.find(f=>f.id==="inbox");
        counts[s.email]=inboxFolder?.unread||0;
        customs[s.email]=list.filter(f=>f.custom);
      });
      setUnreadCounts(counts);
      setCustomFolders(customs);
    }catch(e){}
  };

  const [showCreateFolderModal,setShowCreateFolderModal]=useState(null); // {accountEmail, parentPath} or null
  const [createFolderName,setCreateFolderName]=useState("");
  const [createFolderError,setCreateFolderError]=useState("");
  const [creatingFolder,setCreatingFolder]=useState(false);
  const createFolderFor=(accountEmail,parentPath)=>{
    setCreateFolderName("");setCreateFolderError("");
    setShowCreateFolderModal({accountEmail,parentPath});
  };
  const submitCreateFolder=async()=>{
    if(creatingFolder)return; // guard against double-submit
    const {accountEmail,parentPath}=showCreateFolderModal;
    const name=createFolderName.trim();
    if(!name)return;
    setCreatingFolder(true);setCreateFolderError("");
    try{
      const r=await fetch("/api/email/folders/create",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,name,parentPath})});
      const d=await r.json().catch(()=>({error:"Server returned an unreadable response (status "+r.status+")"}));
      if(!r.ok)throw new Error(d.error||("Request failed with status "+r.status));
      addToast("Folder created — syncing shortly","success");
      if(parentPath)setExpandedFolders(p=>({...p,[accountEmail+":"+parentPath]:true})); // auto-expand so the new subfolder is immediately visible
      await refreshUnreadCounts();
      setShowCreateFolderModal(null);
    }catch(e){setCreateFolderError(e.message);}
    setCreatingFolder(false);
  };
  const renameFolderFor=async(accountEmail,oldName)=>{
    const newName=window.prompt("Rename folder to:",oldName);
    if(!newName||newName===oldName)return;
    try{
      const r=await fetch("/api/email/folders/rename",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,oldName,newName})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast("Folder renamed","success");
      if(activeFolder===oldName)setActiveFolder(newName);
      await refreshUnreadCounts();
    }catch(e){addToast("Couldn't rename folder: "+e.message,"error");}
  };
  const deleteFolderFor=async(accountEmail,name)=>{
    if(!window.confirm(`Delete the folder "${name}" and everything in it? This can't be undone.`))return;
    try{
      const r=await fetch("/api/email/folders/delete",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,name})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast("Folder deleted","success");
      if(activeFolder===name)setActiveFolder("inbox");
      await refreshUnreadCounts();
    }catch(e){addToast("Couldn't delete folder: "+e.message,"error");}
  };
  const markAllReadFor=async(accountEmail,folder)=>{
    try{
      const r=await fetch("/api/email/folders/mark-all-read",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,folder})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(d.updated?`Marked ${d.updated} as read`:"Nothing to mark","success");
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't mark all as read: "+e.message,"error");}
  };
  const CATEGORY_COLORS={red:"#D13438",yellow:"#FFB900",green:"#107C10",blue:"#0078D4",purple:"#8764B8"};

  const patchMessage=async(msgId,body)=>{
    const r=await fetch("/api/email/"+msgId,{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify(body)});
    if(!r.ok)throw new Error((await r.json()).error);
  };
  // Shared by every undo action below — moves a set of ids back to wherever
  // they came from and refreshes the list/unread counts the same way the
  // original action did, so "Undo" always leaves things exactly as if the
  // action never happened rather than just flipping one field back.
  const undoMoveIds=async(ids,previousFolders)=>{
    try{
      await Promise.all(ids.map(id=>patchMessage(id,{folder:previousFolders[id]!==undefined?previousFolders[id]:previousFolders})));
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't undo: "+e.message,"error");}
  };
  const moveEmailTo=async(thread,msg,targetFolder)=>{
    const previousFolder=msg.folder||thread.folder||activeFolder;
    try{
      await patchMessage(msg.id,{folder:targetFolder});
      if(selectedItem?.id===thread.id)setSelectedItem(null);
      await loadFolderItems();await refreshUnreadCounts();
      addToast("Moved to "+targetFolder,"success",{label:"Undo",onClick:()=>undoMoveIds([msg.id],previousFolder)});
    }catch(e){addToast("Couldn't move: "+e.message,"error");}
  };
  const moveMessageIdsToFolder=async(ids,targetFolder)=>{
    if(!ids||!ids.length)return;
    // Bulk moves always act on messages from the single folder currently
    // being viewed, so one previous-folder value covers the whole batch.
    const previousFolder=activeFolder;
    try{
      await Promise.all(ids.map(id=>patchMessage(id,{folder:targetFolder})));
      setSelectedItem(null);setMultiSel([]);
      await loadFolderItems();await refreshUnreadCounts();
      addToast(`Moved ${ids.length} email${ids.length>1?"s":""} to ${targetFolder}`,"success",{label:"Undo",onClick:()=>undoMoveIds(ids,previousFolder)});
    }catch(e){addToast("Couldn't move: "+e.message,"error");}
  };
  const handleFolderDrop=(e,targetFolder)=>{
    e.preventDefault();
    try{
      const raw=e.dataTransfer.getData("text/plain");
      if(!raw){addToast("Nothing to drop — try dragging the email again","error");return;}
      const ids=JSON.parse(raw);
      if(!Array.isArray(ids)||!ids.length){addToast("Couldn't read the dragged email","error");return;}
      moveMessageIdsToFolder(ids,targetFolder);
    }catch(err){addToast("Drop failed: "+err.message,"error");}
  };
  const copyEmailTo=async(msg,targetFolder)=>{
    try{
      const r=await fetch("/api/email/"+msg.id+"/copy",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({targetFolder})});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Copied to "+targetFolder+" (in this app only — not yet copied on the real mailbox)","info");
    }catch(e){addToast("Couldn't copy: "+e.message,"error");}
  };
  const deleteEmail=async(thread,msg)=>{
    // The DELETE endpoint (without ?permanent=true, which nothing in this
    // UI sets) is itself just a soft-delete — it sets folder:"trash" server
    // side rather than removing the row — so "Undo" here is the same
    // move-back-to-previous-folder operation as Undo on a regular move.
    const previousFolder=msg.folder||thread.folder||activeFolder;
    try{
      await fetch("/api/email/thread/"+msg.id,{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(selectedItem?.id===thread.id)setSelectedItem(null);
      await loadFolderItems();await refreshUnreadCounts();
      addToast("Moved to trash","info",{label:"Undo",onClick:()=>undoMoveIds([msg.id],previousFolder)});
    }catch(e){addToast("Couldn't delete: "+e.message,"error");}
  };
  // Ignore: mutes a whole conversation. Persists server-side (so future
  // synced replies in this thread auto-file to Trash too — see
  // mailFilterRules.js) and immediately trashes every message currently in
  // the thread. Undo un-persists the rule AND moves everything back.
  const ignoreThread=async(thread)=>{
    const threadKey=thread.threadId||thread.id;
    const accountEmail=thread.accountEmail;
    const idsInThread=(thread.messages||[]).map(m=>m.id);
    const previousFolder=thread.folder||activeFolder;
    try{
      const r=await fetch("/api/email/thread/"+encodeURIComponent(threadKey)+"/ignore",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail})});
      if(!r.ok)throw new Error((await r.json()).error);
      if(selectedItem?.id===thread.id)setSelectedItem(null);
      await loadFolderItems();await refreshUnreadCounts();
      addToast("Conversation ignored — moved to Trash, and future replies will go there too","info",{label:"Undo",onClick:async()=>{
        try{
          await fetch("/api/email/thread/"+encodeURIComponent(threadKey)+"/ignore?accountEmail="+encodeURIComponent(accountEmail),{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
          await undoMoveIds(idsInThread,previousFolder);
        }catch(e){addToast("Couldn't undo: "+e.message,"error");}
      }});
    }catch(e){addToast("Couldn't ignore conversation: "+e.message,"error");}
  };
  // Clean Up Conversation: one-time dedupe of messages in this thread whose
  // full text is already quoted verbatim in a later message — no rule is
  // persisted, it's a plain cleanup pass.
  const cleanUpConversation=async(thread)=>{
    const threadKey=thread.threadId||thread.id;
    const accountEmail=thread.accountEmail;
    try{
      const r=await fetch("/api/email/thread/"+encodeURIComponent(threadKey)+"/cleanup",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(d.removed?`Removed ${d.removed} redundant message${d.removed>1?"s":""}`:"Nothing redundant to clean up","success");
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't clean up conversation: "+e.message,"error");}
  };
  // Clean Up Folder: same dedupe pass, applied across every conversation in
  // the given folder — wired up from the folder context menu.
  const cleanUpFolderFor=async(accountEmail,folder)=>{
    try{
      const r=await fetch("/api/email/folders/cleanup",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,folder})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(d.removed?`Removed ${d.removed} redundant message${d.removed>1?"s":""}`:"Nothing redundant to clean up","success");
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't clean up folder: "+e.message,"error");}
  };
  // Sweep: persists a standing rule for this sender (checked by the sync
  // pipeline for every future incoming message — see mailFilterRules.js) and
  // optionally applies the same action to mail already in the mailbox right
  // now. Uses the same lightweight prompt/confirm pattern already used for
  // Rename Folder / Delete Folder above rather than a bespoke modal.
  const sweepSender=async(thread,msg)=>{
    const senderEmail=(msg?.from||thread.from||"").trim();
    const accountEmail=thread.accountEmail;
    if(!senderEmail){addToast("Couldn't determine the sender to sweep","error");return;}
    const choice=window.prompt(
      `Sweep: always handle future mail from ${senderEmail} automatically.\n\nType "delete" to send it straight to Trash, or type a folder name (e.g. "archive", "junk") to always move it there instead. Leave blank to cancel.`,
      "delete"
    );
    if(!choice||!choice.trim())return;
    const trimmed=choice.trim();
    const action=trimmed.toLowerCase()==="delete"?"delete":"moveToFolder";
    const targetFolder=action==="moveToFolder"?trimmed:undefined;
    const alsoCleanExisting=window.confirm(`Also move mail already in the mailbox from ${senderEmail} ${action==="delete"?"to Trash":`to "${targetFolder}"`} right now?`);
    try{
      const r=await fetch("/api/email/sweep",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,senderEmail,action,targetFolder,alsoCleanExisting})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(`Future mail from ${senderEmail} will ${action==="delete"?"go straight to Trash":`move to "${targetFolder}"`}`+(alsoCleanExisting&&d.updatedExisting?` — ${d.updatedExisting} existing message${d.updatedExisting>1?"s":""} moved now`:""),"success");
      if(alsoCleanExisting){await loadFolderItems();await refreshUnreadCounts();}
    }catch(e){addToast("Couldn't set up Sweep: "+e.message,"error");}
  };
  // ── Junk submenu (Outlook's Block Sender / Never Block Sender / Never
  // Block Sender's Domain / Not Junk / Junk E-mail Options...) — reuses the
  // exact same per-sender SweepRule Sweep... already writes above, so
  // blocking from here and un-blocking from Sweep (or vice versa) can never
  // leave two conflicting rules behind for one sender.
  const emailDomainOf=(email)=>{
    const at=(email||"").lastIndexOf("@");
    return at===-1?"":email.slice(at).toLowerCase(); // keeps the leading "@"
  };
  const junkBlockSender=async(thread,msg)=>{
    const senderEmail=(msg?.from||thread.from||"").trim();
    const accountEmail=thread.accountEmail;
    if(!senderEmail){addToast("Couldn't determine the sender to block","error");return;}
    try{
      const r=await fetch("/api/email/sweep",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,senderEmail,action:"moveToFolder",targetFolder:"junk",alsoCleanExisting:true})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast(`Blocked ${senderEmail} — future mail from them goes straight to Junk`,"success");
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't block sender: "+e.message,"error");}
  };
  // Shared by "Never Block Sender" and "Not Junk": drops any standing Sweep
  // rule that auto-files this exact sender's mail into Junk/Trash. Returns
  // true if a rule actually existed and was removed.
  const removeSweepRuleFor=async(accountEmail,senderEmail)=>{
    const tk=localStorage.getItem("sdr_token")||"";
    const r=await fetch("/api/email/sweep?accountEmail="+encodeURIComponent(accountEmail),{headers:{Authorization:"Bearer "+tk}});
    const rules=await r.json().catch(()=>[]);
    const match=(Array.isArray(rules)?rules:[]).find(x=>(x.senderEmail||"").toLowerCase()===senderEmail.toLowerCase()&&(x.action==="delete"||x.targetFolder==="junk"||x.targetFolder==="trash"));
    if(!match)return false;
    await fetch("/api/email/sweep/"+match.id,{method:"DELETE",headers:{Authorization:"Bearer "+tk}});
    return true;
  };
  const junkNeverBlockSender=async(thread,msg)=>{
    const senderEmail=(msg?.from||thread.from||"").trim();
    const accountEmail=thread.accountEmail;
    if(!senderEmail){addToast("Couldn't determine the sender","error");return;}
    try{
      const removed=await removeSweepRuleFor(accountEmail,senderEmail);
      addToast(removed?`${senderEmail} will never be auto-blocked again`:`No block rule exists for ${senderEmail} — nothing to remove`,"success");
    }catch(e){addToast("Couldn't update: "+e.message,"error");}
  };
  // Domain-level blocks only ever exist as a general Rules-engine rule (the
  // Sweep model above targets one exact address, never a whole domain), so
  // this looks for — and removes — any rule whose "From contains @domain"
  // condition routes mail to Junk/Trash.
  const junkNeverBlockDomain=async(thread,msg)=>{
    const senderEmail=(msg?.from||thread.from||"").trim();
    const domain=emailDomainOf(senderEmail);
    const accountEmail=thread.accountEmail;
    if(!domain){addToast("Couldn't determine the sender's domain","error");return;}
    try{
      const tk=localStorage.getItem("sdr_token")||"";
      const r=await fetch("/api/email/rules?accountEmail="+encodeURIComponent(accountEmail),{headers:{Authorization:"Bearer "+tk}});
      const rules=await r.json().catch(()=>[]);
      const blocking=(Array.isArray(rules)?rules:[]).filter(x=>(x.action==="delete"||["junk","trash","deleted"].includes(x.targetFolder))&&(x.conditions||[]).some(c=>c.field==="from"&&(c.value||"").toLowerCase().includes(domain)));
      if(!blocking.length){addToast(`No block rule exists for ${domain} — nothing to remove`,"success");return;}
      await Promise.all(blocking.map(x=>fetch("/api/email/rules/"+x.id,{method:"DELETE",headers:{Authorization:"Bearer "+tk}})));
      addToast(`Mail from ${domain} will never be auto-blocked again`,"success");
    }catch(e){addToast("Couldn't update: "+e.message,"error");}
  };
  const junkNotJunk=async(thread,msg)=>{
    const senderEmail=(msg?.from||thread.from||"").trim();
    const accountEmail=thread.accountEmail;
    try{
      await moveEmailTo(thread,msg,"inbox");
      if(senderEmail)await removeSweepRuleFor(accountEmail,senderEmail);
    }catch(e){addToast("Couldn't mark as not junk: "+e.message,"error");}
  };
  // ── Rules engine (P3 #15) ──
  // General if/then auto-filing rules — the standing-rule successor to
  // Sweep's single-sender case. Persists via /api/email/rules and is
  // enforced going forward by the sync pipeline (mailFilterRules.js).
  const RULE_FIELD_LABELS={from:"From",to:"To",subject:"Subject",body:"Body"};
  const RULE_OP_LABELS={contains:"contains",equals:"is exactly",startsWith:"starts with"};
  const RULE_FOLDER_OPTIONS=[
    {id:"inbox",label:"Inbox"},{id:"focused",label:"Focused"},{id:"sent",label:"Sent Items"},
    {id:"drafts",label:"Drafts"},{id:"outbox",label:"Outbox"},{id:"deleted",label:"Deleted Items"},
    {id:"archive",label:"Archive"},{id:"junk",label:"Junk Mail"},
  ];
  const loadRules=async()=>{
    if(!compose.from)return;
    setRulesLoading(true);
    try{
      const r=await fetch("/api/email/rules?accountEmail="+encodeURIComponent(compose.from),{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setRulesList(Array.isArray(d)?d:[]);
    }catch(e){addToast("Couldn't load rules: "+e.message,"error");}
    setRulesLoading(false);
  };
  const openRulesModal=()=>{setRulesModal(true);loadRules();};
  const newRuleDraft=()=>({name:"",matchType:"all",conditions:[{field:"from",op:"contains",value:""}],action:"moveToFolder",targetFolder:"",markAsRead:false,alsoCleanExisting:false});
  const startNewRule=()=>setRuleEditor(newRuleDraft());
  const startEditRule=(rule)=>setRuleEditor({id:rule._id,name:rule.name,matchType:rule.matchType,conditions:rule.conditions.map(c=>({...c})),action:rule.action,targetFolder:rule.targetFolder||"",markAsRead:!!rule.markAsRead,alsoCleanExisting:false});
  const saveRuleEditor=async()=>{
    const e=ruleEditor;
    if(!e.name.trim()){addToast("Give the rule a name","error");return;}
    if(!e.conditions.length||e.conditions.some(c=>!c.value.trim())){addToast("Every condition needs a value","error");return;}
    if(e.action==="moveToFolder"&&!e.targetFolder.trim()){addToast("Pick a target folder","error");return;}
    try{
      const body={accountEmail:compose.from,name:e.name.trim(),matchType:e.matchType,conditions:e.conditions,action:e.action,targetFolder:e.action==="moveToFolder"?e.targetFolder.trim():undefined,markAsRead:e.markAsRead,alsoCleanExisting:e.alsoCleanExisting};
      const url=e.id?("/api/email/rules/"+e.id):"/api/email/rules";
      const method=e.id?"PATCH":"POST";
      const r=await fetch(url,{method,headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify(body)});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast((e.id?"Rule updated":"Rule created")+(d.updatedExisting?` — ${d.updatedExisting} existing message${d.updatedExisting>1?"s":""} updated now`:""),"success");
      setRuleEditor(null);
      await loadRules();
      if(d.updatedExisting){await loadFolderItems();await refreshUnreadCounts();}
    }catch(err){addToast("Couldn't save rule: "+err.message,"error");}
  };
  const toggleRuleEnabled=async(rule)=>{
    try{
      const r=await fetch("/api/email/rules/"+rule._id,{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({enabled:!rule.enabled})});
      if(!r.ok)throw new Error((await r.json()).error);
      await loadRules();
    }catch(e){addToast("Couldn't update rule: "+e.message,"error");}
  };
  const deleteRule=async(rule)=>{
    if(!window.confirm(`Delete the rule "${rule.name}"?`))return;
    try{
      const r=await fetch("/api/email/rules/"+rule._id,{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Rule deleted","success");
      await loadRules();
    }catch(e){addToast("Couldn't delete rule: "+e.message,"error");}
  };
  // Rules run in ascending order, first match wins — so reordering here
  // directly changes which rule fires when more than one would otherwise
  // match. Optimistic UI update, reverted by reloading if the save fails.
  const moveRuleOrder=async(rule,direction)=>{
    const idx=rulesList.findIndex(r=>r._id===rule._id);
    const swapIdx=idx+direction;
    if(idx<0||swapIdx<0||swapIdx>=rulesList.length)return;
    const reordered=[...rulesList];
    [reordered[idx],reordered[swapIdx]]=[reordered[swapIdx],reordered[idx]];
    setRulesList(reordered);
    try{
      const r=await fetch("/api/email/rules/reorder",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({orderedIds:reordered.map(x=>x._id)})});
      if(!r.ok)throw new Error((await r.json()).error);
    }catch(e){addToast("Couldn't reorder: "+e.message,"error");await loadRules();}
  };
  // ── Inbox Zero: Sweep management ──
  // Sweep rules are the simpler, single-sender standing rule (vs. Rules'
  // multi-condition matching) — same enforcement pipeline (mailFilterRules.js
  // at sync time), same backend model (SweepRule), just a narrower shape.
  const loadSweepRules=async()=>{
    if(!compose.from)return;
    setSweepLoading(true);
    try{
      const r=await fetch("/api/email/sweep?accountEmail="+encodeURIComponent(compose.from),{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setSweepRules(Array.isArray(d)?d:[]);
    }catch(e){addToast("Couldn't load Sweep rules: "+e.message,"error");}
    setSweepLoading(false);
  };
  const openInboxZeroModal=()=>{setInboxZeroModal(true);loadSweepRules();loadRules();};
  const newSweepDraft=()=>({senderEmail:"",action:"delete",targetFolder:"",alsoCleanExisting:false});
  const startNewSweep=()=>setSweepEditor(newSweepDraft());
  const saveSweepEditor=async()=>{
    const e=sweepEditor;
    const senderEmail=e.senderEmail.trim();
    if(!senderEmail){addToast("Enter a sender email to sweep","error");return;}
    if(e.action==="moveToFolder"&&!e.targetFolder.trim()){addToast("Pick a target folder","error");return;}
    try{
      const body={accountEmail:compose.from,senderEmail,action:e.action,targetFolder:e.action==="moveToFolder"?e.targetFolder.trim():undefined,alsoCleanExisting:e.alsoCleanExisting};
      const r=await fetch("/api/email/sweep",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify(body)});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast("Sweep rule saved"+(e.alsoCleanExisting&&d.updatedExisting?` — ${d.updatedExisting} existing message${d.updatedExisting>1?"s":""} moved now`:""),"success");
      setSweepEditor(null);
      await loadSweepRules();
      if(d.updatedExisting){await loadFolderItems();await refreshUnreadCounts();}
    }catch(err){addToast("Couldn't save Sweep rule: "+err.message,"error");}
  };
  const deleteSweepRule=async(rule)=>{
    if(!window.confirm(`Stop sweeping mail from ${rule.senderEmail}?`))return;
    try{
      const r=await fetch("/api/email/sweep/"+rule.id,{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(!r.ok)throw new Error((await r.json()).error);
      addToast("Sweep rule removed","success");
      await loadSweepRules();
    }catch(e){addToast("Couldn't remove Sweep rule: "+e.message,"error");}
  };
  const toggleReadFor=async(thread,msg)=>{
    try{
      await patchMessage(msg.id,{isRead:!msg.isRead});
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't update: "+e.message,"error");}
  };
  // Explicit mark-read/mark-unread (not a toggle) — shared by the ribbon's
  // ribbon Mark as Read/Unread buttons, the Ctrl/Cmd+Q / Ctrl/Cmd+U
  // shortcuts, and works the same whether one message or a multi-selection
  // is active, mirroring the existing multi-select "Mark Read" bulk-toolbar
  // button's behavior.
  const markSelectionRead=async(isReadValue)=>{
    const ids=multiSel.length?multiSel:(selectedItem?.messages?.[0]?[selectedItem.messages[0].id]:[]);
    if(!ids.length)return;
    // A multi-selection can mix already-read and unread messages, so Undo
    // needs each id's own prior state rather than just flipping the new
    // value — otherwise undoing "Mark as read" on a mixed selection would
    // incorrectly mark everything unread, including ones that were already
    // read before this action.
    const priorById={};
    listItems.forEach(t=>(t.messages||[]).forEach(m=>{if(ids.includes(m.id))priorById[m.id]=!!m.isRead;}));
    try{
      await Promise.all(ids.map(id=>patchMessage(id,{isRead:isReadValue})));
      if(multiSel.length)setMultiSel([]);
      await loadFolderItems();await refreshUnreadCounts();
      addToast(isReadValue?"Marked as read":"Marked as unread","success",{label:"Undo",onClick:async()=>{
        try{
          await Promise.all(ids.map(id=>patchMessage(id,{isRead:priorById[id]!==undefined?priorById[id]:!isReadValue})));
          await loadFolderItems();await refreshUnreadCounts();
        }catch(e){addToast("Couldn't undo: "+e.message,"error");}
      }});
    }catch(e){addToast("Couldn't update: "+e.message,"error");}
  };
  // Start/Pause/Resume/Delete for a campaign, addressed by id rather than
  // closed over `selectedItem` — same endpoints and same toast text as the
  // detail-pane buttons below (which now call these instead of duplicating
  // the fetch calls inline), so this is reachable from both the reading
  // pane AND the new right-click context menu on a campaign row.
  const campaignAction=async(id,action)=>{
    await fetch("/api/campaigns/"+id+"/"+action,{method:"POST"});
    if(selectedItem?.id===id)setSelectedItem(p=>({...p,status:(action==="start"||action==="resume")?"Active":"Paused"}));
    await loadAll();
    addToast(action==="start"?"Campaign started":action==="pause"?"Paused":"Resumed",action==="pause"?"info":"success");
  };
  const deleteCampaign=async(id)=>{
    await fetch("/api/campaigns/"+id,{method:"DELETE"});
    if(selectedItem?.id===id)setSelectedItem(null);
    await loadAll();
    addToast("Deleted","info");
  };
  // Sequence row actions for the embedded Sequences list in Workspace — call
  // the same REST endpoints the standalone Sequence Builder page already
  // uses (SequenceBuilder.js's deleteSequence/duplicateSequence/
  // runSequenceAction), so there's one source of truth for what these
  // actions do server-side, just invoked from this second surface too.
  const sequenceRowAction=async(id,action)=>{
    try{
      const r=await fetch("/api/sequences/"+id+"/"+action,{method:"POST",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(!r.ok)throw new Error("Request failed");
      await loadAll();
      addToast(action==="start"?"Sequence started":action==="pause"?"Sequence paused":"Sequence resumed","success");
    }catch(e){addToast("Couldn't update sequence: "+e.message,"error");}
  };
  const duplicateSequenceRow=async(id)=>{
    try{
      const r=await fetch("/api/sequences/"+id+"/duplicate",{method:"POST",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(!r.ok)throw new Error("Request failed");
      await loadAll();
      addToast("Sequence duplicated","success");
    }catch(e){addToast("Couldn't duplicate: "+e.message,"error");}
  };
  const deleteSequenceRow=async(id)=>{
    try{
      const r=await fetch("/api/sequences/"+id,{method:"DELETE",headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      if(!r.ok)throw new Error("Request failed");
      if(selectedItem?.id===id)setSelectedItem(null);
      await loadAll();
      addToast("Sequence deleted","info");
    }catch(e){addToast("Couldn't delete: "+e.message,"error");}
  };
  const toggleFlagFor=async(msg)=>{
    try{
      await patchMessage(msg.id,{isFlagged:!msg.isFlagged});
      await loadFolderItems();
    }catch(e){addToast("Couldn't flag: "+e.message,"error");}
  };
  const setCategoryFor=async(msg,category)=>{
    try{
      await patchMessage(msg.id,{category});
      await loadFolderItems();
      addToast(category?"Categorized":"Category removed","success");
    }catch(e){addToast("Couldn't categorize: "+e.message,"error");}
  };
  const downloadEmail=(msg,subject)=>{
    const lines=[`From: ${msg.fromName||""} <${msg.from||""}>`,`To: ${msg.to||""}`,msg.cc?`Cc: ${msg.cc}`:null,`Subject: ${subject||msg.subject||""}`,`Date: ${new Date(msg.sentAt||msg.receivedAt).toString()}`,"",(msg.body||"").replace(/<[^>]+>/g," ")].filter(Boolean);
    const blob=new Blob([lines.join("\n")],{type:"text/plain"});
    const a=document.createElement("a");
    a.href=URL.createObjectURL(blob);a.download=(subject||"email").replace(/[^\w\-]+/g,"_")+".txt";
    document.body.appendChild(a);a.click();document.body.removeChild(a);
  };
  // Real export — a genuine CSV of whatever's currently in view (mail
  // threads, leads, campaigns, etc., following the same fields getListItems
  // already surfaces for the active folder), not a fake "coming soon" toast.
  const exportCurrentFolder=()=>{
    const items=getListItems();
    if(!items.length){addToast("Nothing to export in "+activeFolder,"info");return;}
    let rows,header;
    if(isMail){
      header=["Subject","From","To","Date","Preview"];
      rows=items.map(t=>{const latest=t.messages?.[0]||{};return [t.subject,latest.from||t.from||"",latest.to||t.to||"",new Date(latest.sentAt||latest.receivedAt||0).toISOString(),(latest.preview||"").replace(/"/g,'""')];});
    }else{
      header=["Name","Email","Company","Title"];
      rows=items.map(x=>[x.name||`${x.first||""} ${x.last||""}`.trim(),x.email||"",x.company||"",x.title||""]);
    }
    const csv=[header,...rows].map(r=>r.map(c=>`"${String(c??"").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const a=document.createElement("a");
    a.href=URL.createObjectURL(blob);a.download=activeFolder+"_export_"+new Date().toISOString().slice(0,10)+".csv";
    document.body.appendChild(a);a.click();document.body.removeChild(a);
    addToast("Exported "+rows.length+" row"+(rows.length===1?"":"s"),"success");
  };
  // Real navigation back to the main app's Settings page — this popped-out
  // window has its own standalone route (no shared sidebar/page state with
  // the main App), so "going to Settings" means setting the page the main
  // app will land on and redirecting there.
  const goToSettings=()=>{
    localStorage.setItem("sdr_page","settings");
    window.location.href="/";
  };
  const printEmail=(msg,subject)=>{
    const w=window.open("","_blank","width=800,height=900");
    w.document.write(`<html><head><title>${subject||""}</title></head><body style="font-family:Calibri,sans-serif;padding:24px;">
      <h2>${subject||""}</h2>
      <p><b>From:</b> ${msg.fromName||""} &lt;${msg.from||""}&gt;<br/><b>To:</b> ${msg.to||""}<br/><b>Date:</b> ${new Date(msg.sentAt||msg.receivedAt).toLocaleString()}</p>
      <hr/>${msg.body||""}
    </body></html>`);
    w.document.close();w.print();
  };
  const openInNewWindow=(thread,msg)=>{
    const w=window.open("","_blank","width=800,height=900");
    w.document.write(`<html><head><title>${thread.subject||""}</title></head><body style="font-family:Calibri,sans-serif;padding:24px;">
      <h2>${thread.subject||""}</h2>
      <p><b>From:</b> ${msg.fromName||""} &lt;${msg.from||""}&gt;<br/><b>To:</b> ${msg.to||""}<br/><b>Date:</b> ${new Date(msg.sentAt||msg.receivedAt).toLocaleString()}</p>
      <hr/>${msg.body||""}
    </body></html>`);
    w.document.close();
  };

  const emptyFolderFor=async(accountEmail,folder)=>{
    const verb=folder==="trash"?"permanently delete everything in Trash":"move everything to Trash";
    if(!window.confirm(`Are you sure you want to ${verb}?`))return;
    try{
      const r=await fetch("/api/email/folders/empty",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({accountEmail,folder})});
      const d=await r.json();
      if(!r.ok)throw new Error(d.error);
      addToast("Done","success");
      await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Couldn't empty folder: "+e.message,"error");}
  };

  // Custom folder ids are hierarchical paths (e.g. "Drafts/Digital Engineering",
  // matching how both the IMAP and Graph engines encode nested folders) — this
  // groups the flat list back into a real tree for expand/collapse rendering.
  // Real folder names for our canonical ids vary by provider ("Inbox" vs
  // "INBOX" vs "inbox"), so match case-insensitively against common
  // variants to know when a custom-tracked path actually belongs nested
  // under a canonical row (e.g. "INBOX/123" belongs under Inbox) rather
  // than as a true standalone root folder.
  const CANONICAL_ALIASES={
    "inbox":"inbox","focused":"inbox",
    "sent":"sent","sent items":"sent","sentitems":"sent",
    "drafts":"drafts",
    "deleted items":"deleted","deleteditems":"deleted","trash":"deleted",
    "archive":"archive",
    "junk":"junk","junk mail":"junk","junkmail":"junk","junk email":"junk",
  };
  const buildFolderTree=(list)=>{
    const byPath={};
    list.forEach(f=>{byPath[f.id]={...f,children:[]};});
    const roots=[];
    const nestedUnderCanonical={}; // canonical id -> [tree nodes]
    list.forEach(f=>{
      const parts=f.id.split("/");
      if(parts.length>1){
        const parentPath=parts.slice(0,-1).join("/");
        if(byPath[parentPath])byPath[parentPath].children.push(byPath[f.id]);
        else roots.push(byPath[f.id]); // parent has no messages of its own yet — treat this as a root for now
      }else{
        roots.push(byPath[f.id]);
      }
    });
    // Pull out any root whose top-level segment is actually a canonical
    // folder's real name, so it renders nested under that row instead.
    const trueRoots=[];
    roots.forEach(node=>{
      const topSegment=node.id.split("/")[0].toLowerCase();
      const canonicalId=CANONICAL_ALIASES[topSegment];
      if(canonicalId&&node.id.includes("/")){
        // e.g. node.id === "INBOX/123" — nest it under the "inbox" row directly
        (nestedUnderCanonical[canonicalId]=nestedUnderCanonical[canonicalId]||[]).push(node);
      }else{
        trueRoots.push(node);
      }
    });
    return {roots:trueRoots,nestedUnderCanonical};
  };

  const toggleFolderExpand=(key)=>setExpandedFolders(p=>({...p,[key]:p[key]===false?true:false}));

  const renderFolderNode=(node,depth,accountEmail)=>{
    const key=accountEmail+":"+node.id;
    const isExp=expandedFolders[key]!==false; // default expanded
    const hasChildren=node.children&&node.children.length>0;
    const isSel=activeFolder===node.id&&compose.from===accountEmail;
    return(
      <div key={node.id}>
        <button onClick={()=>{setActiveFolder(node.id);setComposing(false);setSelectedItem(null);setCompose(p=>({...p,from:accountEmail}));}}
          onContextMenu={e=>{e.preventDefault();setFolderCtxMenu({x:e.clientX,y:e.clientY,accountEmail,folderId:node.id,label:node.label,canonical:false});}}
          onDragOver={e=>e.preventDefault()}
          onDrop={e=>handleFolderDrop(e,node.id)}
          className="ow-item"
          style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:`6px 10px 6px ${10+depth*14}px`,border:"none",background:isSel?C.sidebarSel:"transparent",color:isSel?C.sidebarSelText:C.text,cursor:"pointer",borderLeft:isSel?"3px solid "+C.sidebarSelBorder:"3px solid transparent",fontSize:13,fontFamily:"'Segoe UI',sans-serif",fontWeight:isSel?600:400}}>
          {hasChildren
            ? <span onClick={e=>{e.stopPropagation();toggleFolderExpand(key);}} style={{width:11,flexShrink:0,fontSize:9,color:C.textSub,transition:"transform .15s",transform:isExp?"rotate(90deg)":"rotate(0deg)",display:"inline-block",cursor:"pointer"}}>&#9656;</span>
            : <span style={{width:11,flexShrink:0}}/>}
          <span style={{flexShrink:0,display:"flex"}}><FolderIcon type="folder" color={isSel?C.sidebarSelText:C.text}/></span>
          <span style={{flex:1,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{node.label}</span>
        </button>
        {hasChildren&&isExp&&node.children.map(child=>renderFolderNode(child,depth+1,accountEmail))}
      </div>
    );
  };

  const loadFolderItems=async()=>{
    const isCustom=(customFolders[compose.from]||[]).some(f=>f.id===activeFolder);
    const backendFolder=FOLDER_API_MAP[activeFolder]||(isCustom?activeFolder:null);
    if(!backendFolder||!compose.from){setFolderItems([]);return;}
    setFolderLoading(true);
    try{
      const r=await fetch("/api/email/threads?folder="+encodeURIComponent(backendFolder)+"&accountEmail="+encodeURIComponent(compose.from),{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setFolderItems(Array.isArray(d)?d:[]);
    }catch(e){setFolderItems([]);}
    setFolderLoading(false);
  };

  useEffect(()=>{loadFolderItems();},[activeFolder,compose.from,customFolders]);

  // Cross-folder search — fetches and merges every folder a given scope
  // covers (see SEARCH_SCOPE_OPTIONS) whenever the Search contextual tab is
  // active with a non-empty query. "Current Folder" scope is deliberately
  // excluded here: it needs no extra fetch since folderItems (loaded above)
  // already holds exactly that data, and getListItems() below filters it in
  // place exactly like it always has. Debounced 300ms so fast typing doesn't
  // fire a fetch per keystroke.
  useEffect(()=>{
    if(!searchActive||!search.trim()||searchScope==="currentFolder"){setCrossFolderResults(null);return;}
    let cancelled=false;
    const timer=setTimeout(async()=>{
      setCrossFolderLoading(true);
      try{
        const folderIdsForAccount=(email)=>{
          const custom=(customFolders[email]||[]).map(f=>f.id);
          if(searchScope==="subfolders"){
            const prefix=activeFolder+"/";
            const nested=custom.filter(id=>id.startsWith(prefix));
            const self=FOLDER_API_MAP[activeFolder]||custom.includes(activeFolder)?[activeFolder]:[];
            return [...self,...nested];
          }
          // currentMailbox / allMailboxes / allOutlookItems — every canonical
          // + custom folder for the account, minus "deleted" unless the
          // Include Deleted Items option is on.
          const canonical=CANONICAL_SEARCH_FOLDERS.filter(f=>f!=="deleted"||includeDeleted);
          return [...canonical,...custom];
        };
        const accounts = (searchScope==="allMailboxes"||searchScope==="allOutlookItems")
          ? (senders.length?senders.map(s=>s.email):[compose.from]).filter(Boolean)
          : [compose.from].filter(Boolean);
        const fetches=[];
        accounts.forEach(email=>{
          folderIdsForAccount(email).forEach(folderId=>{
            const backendFolder=FOLDER_API_MAP[folderId]||folderId;
            fetches.push(
              fetch("/api/email/threads?folder="+encodeURIComponent(backendFolder)+"&accountEmail="+encodeURIComponent(email),{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}})
                .then(r=>r.json()).then(d=>Array.isArray(d)?d.map(t=>({...t,_accountEmail:email,_folderId:folderId})):[])
                .catch(()=>[])
            );
          });
        });
        const results=await Promise.all(fetches);
        if(cancelled)return;
        const merged=[];
        const seenIds=new Set();
        results.flat().forEach(t=>{
          const key=t._accountEmail+":"+(t.id!=null?t.id:JSON.stringify(t));
          if(seenIds.has(key))return;
          seenIds.add(key);
          merged.push(t);
        });
        setCrossFolderResults(merged);
      }catch(e){
        if(!cancelled)setCrossFolderResults([]);
      }
      if(!cancelled)setCrossFolderLoading(false);
    },300);
    return()=>{cancelled=true;clearTimeout(timer);};
  },[searchActive,search,searchScope,activeFolder,compose.from,customFolders,senders,includeDeleted]);

  const loadMeetings=useCallback(async()=>{
    try{
      const r=await fetch("/api/meetings",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setMeetings(Array.isArray(d)?d:[]);
    }catch(e){}
  },[]);
  useEffect(()=>{if(mainView==="calendar")loadMeetings();},[mainView,loadMeetings]);

  const loadTasks=useCallback(async()=>{
    try{
      const r=await fetch("/api/tasks",{headers:{Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")}});
      const d=await r.json();
      setTasksList(Array.isArray(d)?d:[]);
    }catch(e){}
  },[]);
  useEffect(()=>{if(mainView==="tasks")loadTasks();},[mainView,loadTasks]);

  // Global "create a calendar item / task right now" helpers — used by both
  // the New Items ribbon dropdown and the Ctrl+Shift+A / Ctrl+Shift+K
  // shortcuts, neither of which are inside CalendarView/TasksView's own
  // render tree, so they set the same lifted state those components read.
  const openNewMeetingNow=()=>{
    const start=new Date(),end=new Date(start.getTime()+30*60000);
    setEditingMeeting({start:start.toISOString(),end:end.toISOString(),title:"",attendees:[],location:"",description:""});
    setShowMeetingModal(true);
    setMainView("calendar");
  };
  const openNewTaskNow=()=>{
    setEditingTask({title:"",notes:"",dueDate:null,priority:"normal",status:"notStarted"});
    setShowTaskModal(true);
    setMainView("tasks");
  };

  // Live sync: poll for new mail every 45s so the Workspace behaves like a
  // real email client without requiring a manual refresh.
  useEffect(()=>{
    const iv=setInterval(()=>{loadFolderItems();refreshUnreadCounts();},45000);
    return ()=>clearInterval(iv);
  },[activeFolder,compose.from,senders]);

  const syncNow=async()=>{
    try{
      addToast("Syncing mailbox...","info");
      await fetch("/api/smtp/sync-now",{method:"POST",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({email:compose.from})});
      await loadFolderItems();
      await refreshUnreadCounts();
      addToast("Mailbox synced","success");
    }catch(e){addToast("Sync failed: "+e.message,"error");}
  };

  const openInPane=()=>{
    setCompose({from:senders[0]?.email||settings.senderEmail||"",to:"",cc:"",bcc:"",subject:"",body:"",id:null,importance:"normal",readReceiptRequested:false,threadId:null,inReplyTo:null,attachments:[]});
    setComposing(true);setReplyMode(null);setSelectedItem(null);
    setShowNewEmailPopup(false);
  };

  // Pop Out compose window: kept as a ref so the message listener below can
  // force-close it. The popup's own script already calls window.close() on
  // itself right after a successful send/schedule — this is a belt-and-
  // braces fallback for the rare case a browser silently refuses a
  // self-close, plus it's how the main tab learns to refresh its mail list
  // immediately instead of waiting for the next 45s poll.
  const popOutWinRef=useRef(null);
  useEffect(()=>{
    const onPopOutMessage=(e)=>{
      if(e.origin!==window.location.origin)return;
      if(e.data&&e.data.type==="sdr-popout-sent"){
        loadFolderItems();refreshUnreadCounts();
        try{if(popOutWinRef.current&&!popOutWinRef.current.closed)popOutWinRef.current.close();}catch(closeEx){}
      }
    };
    window.addEventListener("message",onPopOutMessage);
    return ()=>window.removeEventListener("message",onPopOutMessage);
  },[activeFolder,compose.from,customFolders]); // eslint-disable-line react-hooks/exhaustive-deps

  const newEmailClick=(e)=>{ const rect=e.currentTarget.getBoundingClientRect(); setPopupPos({x:rect.left,y:rect.bottom+4}); setShowNewEmailPopup(true); };
  const openInWindow=()=>{
    setShowNewEmailPopup(false);
    const w=window.open("","_blank","width=960,height=720,left=150,top=80,resizable=yes");
    popOutWinRef.current=w;
    if(!w)return;
    const sndr=senders.map(s=>`<option value="${s.email}">${s.name} (${s.email})</option>`).join("");
    const mtags=MERGE_TAGS.map(t=>`<button class="mtag" onmousedown="event.preventDefault();ins('${t}')">${t}</button>`).join("");
    const fontOptionsHtml=FONT_LIST.map(f=>`<option value="${f.value}">${f.label}</option>`).join("");
    const sizeOptionsHtml=SIZE_LIST.map(s=>`<option value="${s.value}"${s.value==="11pt"?" selected":""}>${s.label}</option>`).join("");
    const fontWhitelistJson=JSON.stringify(FONT_LIST.map(f=>f.value));
    const sizeWhitelistJson=JSON.stringify(SIZE_LIST.map(s=>s.value));
    const sizeNumbersJson=JSON.stringify(SIZE_LIST.map(s=>parseFloat(s.value)));
    // Contacts for this window's own To/Cc/Bcc autocomplete — same source
    // (leads) the inline Workspace composer suggests from, capped so the
    // popup's initial HTML doesn't balloon on a large contact list.
    const contactsJson=JSON.stringify((leads||[]).filter(l=>l.email).slice(0,500).map(l=>({name:l.name||"",email:l.email})));
    // Font label+value pairs (not just the whitelist above) so this popup's
    // own paste-cleanup port can match a pasted font-family against the
    // same list its FontClass Attributor whitelist already uses.
    const fontListJson=JSON.stringify(FONT_LIST.map(f=>({value:f.value,label:f.label})));
    w.document.write(`<!DOCTYPE html><html><head><title>New Message  SDR ReachOut</title><meta charset="utf-8"/>
<link href="https://cdn.quilljs.com/1.3.6/quill.snow.css" rel="stylesheet">
<script src="https://cdn.quilljs.com/1.3.6/quill.js"></script>
<style>
@import url('https://fonts.googleapis.com/css2?family=Segoe+UI:wght@400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',Calibri,sans-serif;background:#f8f9fb;display:flex;flex-direction:column;height:100vh;overflow:hidden;color:#1b1f2e}

/*  Title bar  */
.titlebar{background:linear-gradient(135deg,#0078d4,#106ebe);padding:0;display:flex;flex-direction:column;flex-shrink:0;user-select:none;box-shadow:0 2px 8px rgba(0,120,212,.3)}
.titlebar-top{display:flex;align-items:center;gap:8px;padding:5px 0 5px 12px;min-height:32px}
.titlebar span{color:#fff;font-size:13px;font-weight:500;flex:1;text-align:center}
.qat{display:flex;gap:2px;align-items:center}
.qat button{background:rgba(255,255,255,.1);border:none;border-radius:3px;padding:3px 8px;cursor:pointer;color:#fff;font-size:12px;transition:background .1s}
.qat button:hover{background:rgba(255,255,255,.25)}
.winctrl{display:flex;align-items:stretch;margin-left:4px}
.winctrl button{width:46px;height:32px;background:transparent;border:none;color:#fff;cursor:pointer;font-size:14px;display:flex;align-items:center;justify-content:center;transition:background .12s}
.winctrl button:hover{background:rgba(255,255,255,.15)}
.winctrl button.close:hover{background:#c42b1c}

/*  Ribbon tabs — rounded tab tops + a floating rounded ribbon panel below,
    matching the main Workspace window's ribbon redesign (RibbonPrimitives.js
    / OutlookWorkspace.js's own ribbon strip). This pop-out compose window is
    a separate plain-HTML document (no React here), so the same "rounded,
    professional" look is hand-mirrored in this CSS rather than shared via
    import — kept in step manually since the two can't literally share code. */
.ribbon-tabs{background:#f3f2f1;border-bottom:1px solid #d1cec9;display:flex;align-items:center;padding:4px 8px 0;flex-shrink:0}
.rtab{padding:7px 16px;border:none;border-radius:10px 10px 0 0;background:transparent;border-bottom:2.5px solid transparent;color:#4a5078;font-size:12px;cursor:pointer;font-family:'Segoe UI',sans-serif;font-weight:400;margin-bottom:-1px;transition:all .12s;letter-spacing:.1px}
.rtab:hover{background:rgba(0,0,0,.05);color:#1b1f2e}
.rtab.active{border-bottom:2.5px solid #0078d4;color:#0078d4;font-weight:600;background:#fff;box-shadow:0 -2px 8px rgba(0,0,0,.05)}

/*  Ribbon — a floating white rounded card on the .ribbon-tabs strip's gray
    tray, not a flush full-bleed bar. */
.ribbon{background:#fff;border:1px solid #e2e6f0;border-radius:14px;display:flex;align-items:stretch;padding:7px 10px 5px;flex-shrink:0;min-height:66px;margin:8px 10px;overflow-x:auto;box-shadow:0 2px 10px rgba(0,0,0,.06)}
.rgroup{display:flex;flex-direction:column;padding:0 9px;border-right:1px solid #e2e6f099;min-width:44px}
.rgroup:last-child{border-right:none}
.rgroup-btns{display:flex;gap:1px;align-items:center;justify-content:center;align-content:center;flex:1;padding-bottom:2px;flex-wrap:wrap}
.rgroup-label{font-size:8px;color:#8890b0;text-align:center;border-top:1px solid #eef1f899;padding-top:2px;white-space:nowrap;text-transform:uppercase;letter-spacing:.4px;font-weight:500}

/*  Ribbon buttons  */
.rbtn{background:transparent;border:none;border-radius:9px;padding:3px 6px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:2px;min-width:36px;font-family:'Segoe UI',sans-serif;color:#1b1f2e;transition:all .12s}
.rbtn:hover{background:#f0f4ff;color:#0078d4;box-shadow:0 1px 4px rgba(0,0,0,.08)}
.rbtn:active{background:#ebf3fb}
.rbtn.active{background:#ebf3fb;color:#0078d4}
.rbtn.danger{color:#a4262c}
.rbtn.danger:hover{background:#fde7e9;color:#a4262c}
.rbtn .icon{font-size:18px;line-height:1}
.rbtn.lg .icon{font-size:22px}
.rbtn.sm{min-width:24px;padding:2px 4px;height:22px;justify-content:center;border-radius:6px}
.rbtn.sm .icon{font-size:13px;line-height:1}
.rbtn .lbl{font-size:9.5px;color:#4a5078;white-space:nowrap;text-align:center;line-height:1.2;font-weight:500}
.rbtn:hover .lbl{color:#0078d4}
.rbtn.danger .lbl,.rbtn.danger:hover .lbl{color:#a4262c}

/*  Send button  */
.sendbtn{background:#fff;color:#0078d4;border:1px solid #c0c0c0;border-radius:6px;padding:8px 14px;font-weight:600;cursor:pointer;font-family:'Segoe UI',sans-serif;font-size:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;min-width:60px;transition:all .15s}
.sendbtn:hover{background:#ebf3fb;border-color:#0078d4}
.sendbtn:active{background:#dbe9fa}
.sendbtn .icon{display:flex;align-items:center;justify-content:center;line-height:1}
.sendbtn .lbl{font-size:12px;font-weight:600;color:#0078d4}

/*  Fields  */
.fields{border-bottom:1px solid #e2e6f0;flex-shrink:0;background:#fff}
.frow{display:flex;align-items:center;border-bottom:1px solid #f0f4ff;min-height:32px;transition:background .1s;position:relative}
.frow:hover{background:#fafbff}
.frow label{min-width:68px;padding:5px 12px;font-size:12px;font-weight:600;color:#4a5078;cursor:pointer;flex-shrink:0;border-right:1px solid #f0f4ff;letter-spacing:.1px}
.frow input,.frow select{border:none;outline:none;padding:5px 10px;flex:1;font-size:13px;font-family:'Segoe UI',sans-serif;color:#1b1f2e;background:transparent}
.frow input::placeholder{color:#8890b0}
/*  Recipient (To/Cc/Bcc) contact suggestions — Outlook-style autocomplete,
    filtered from this tenant's contacts as you type. Mirrors the same
    dropdown in the main Workspace compose panel (React side); this is the
    plain-DOM version for this standalone Pop Out window. */
.rcpt-menu{position:absolute;top:100%;left:68px;right:0;background:#fff;border:1px solid #d1cec9;border-radius:4px;box-shadow:0 6px 16px rgba(0,0,0,.15);z-index:60;max-height:220px;overflow-y:auto}
.rcpt-item{padding:6px 10px;cursor:pointer;display:flex;flex-direction:column;gap:1px}
.rcpt-item.active,.rcpt-item:hover{background:#ebf3fb}
.rcpt-item .nm{font-size:12.5px;color:#1b1f2e;font-weight:500}
.rcpt-item .em{font-size:11px;color:#8890b0}

/*  Merge tag bar  */
.mtbar{padding:6px 12px;border-bottom:1px solid #e2e6f0;background:#f8f9fb;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.mtag-toggle{background:#fff;color:#4a5078;border:1px solid #d1cec9;border-radius:8px;padding:5px 10px;font-size:11px;cursor:pointer;font-family:'Segoe UI',sans-serif;font-weight:600;transition:all .1s}
.mtag-toggle:hover{background:#ebf3fb;color:#0078d4;border-color:#c7dff7}
.mtag-menu{position:absolute;top:calc(100% + 4px);left:0;background:#fff;border:1px solid #d1cec9;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,.15);padding:6px;display:flex;flex-direction:column;gap:2px;z-index:50;min-width:200px}
.mtag{background:none;color:#1b1f2e;border:none;border-radius:7px;padding:6px 10px;font-size:12px;cursor:pointer;font-family:'Consolas',monospace;font-weight:500;transition:all .1s;text-align:left}
.mtag:hover{background:#ebf3fb;color:#0078d4}
.aibtn{background:linear-gradient(135deg,#0078d4,#5c2d91);color:#fff;border:none;border-radius:5px;padding:5px 12px;font-size:11px;cursor:pointer;margin-left:auto;font-family:'Segoe UI',sans-serif;font-weight:600;box-shadow:0 2px 6px rgba(0,120,212,.3);transition:all .15s}
.aibtn:hover{box-shadow:0 4px 12px rgba(0,120,212,.4);transform:translateY(-1px)}

/*  Editor  */
.editor{flex:1;display:flex;flex-direction:column;overflow:hidden;background:#fff}
.ql-container.ql-snow{border:none!important;flex:1;overflow:auto}
.ql-editor{font-family:Calibri,'Segoe UI',sans-serif;font-size:14px;min-height:200px;line-height:1.7;padding:20px 24px;color:#1b1f2e}
.ql-editor.ql-blank::before{color:#8890b0;font-style:normal;left:24px;font-size:14px}

/*  Signature  */
.sig{padding:8px 24px;border-top:1px solid #e2e6f0;font-size:12px;color:#4a5078;flex-shrink:0;background:#f8f9fb;line-height:1.6}

/*  Status bar  */
.statusbar{background:#f0f4ff;border-top:1px solid #e2e6f0;padding:4px 16px;font-size:11px;color:#4a5078;display:flex;justify-content:space-between;align-items:center;flex-shrink:0}

/*  Notification  */
.notify{position:fixed;bottom:48px;right:20px;background:linear-gradient(135deg,#0078d4,#106ebe);color:#fff;padding:10px 18px;border-radius:8px;font-size:12px;font-family:'Segoe UI',sans-serif;display:none;z-index:999;box-shadow:0 6px 20px rgba(0,120,212,.4);font-weight:500}

/*  Attachment bar  */
.attach-bar{padding:5px 12px;border-bottom:1px solid #e2e6f0;background:#fff;display:flex;gap:6px;flex-wrap:wrap;min-height:0;flex-shrink:0}
.attach-item{background:#f0f4ff;border:1px solid #e2e6f0;border-radius:5px;padding:4px 10px;font-size:11px;display:flex;align-items:center;gap:6px;color:#1b1f2e;font-weight:500}
.attach-item button{background:none;border:none;cursor:pointer;color:#8890b0;font-size:13px;padding:0 2px;transition:color .1s}
.attach-item button:hover{color:#a4262c}

</style></head><body>

<div id="notify" class="notify"></div>

<!-- Title bar with QAT -->
<div class="titlebar">
  <div class="titlebar-top">
    <div class="qat">
      <span style="font-size:16px;margin-right:4px">✉️</span>
      <button onclick="saveDraft()" title="Save (Ctrl+S)">💾</button>
      <button onclick="document.execCommand('undo')" title="Undo">↶</button>
      <button onclick="document.execCommand('redo')" title="Redo">↷</button>
    </div>
    <span id="wtitle">Untitled  Message (HTML)</span>
    <div style="display:flex;align-items:center;gap:6px;margin-right:4px">
      <div style="font-size:11px;color:rgba(255,255,255,.75);font-family:monospace" id="wc-title">Words: 0</div>
    </div>
    <div class="winctrl">
      <!-- The old "Minimize" button here called window.resizeTo(800,600) —
           a real API, but it only shrinks the window's pixel size in
           place; no browser exposes a script-triggerable real minimize
           (that's an OS-level action, only ever triggered by the user's
           own click on the browser's real native title bar). Since this
           is a genuine window.open() popup, it already has that real
           native title bar with a real, working minimize button above
           this one — removed the broken custom one rather than leave it
           doing the wrong thing. Maximize stays: Fullscreen is a real,
           different capability script genuinely can trigger. -->
      <button title="Maximize" onclick="document.fullscreenElement?document.exitFullscreen():document.documentElement.requestFullscreen()">▢</button>
      <button class="close" title="Close" onclick="window.close()">✕</button>
    </div>
  </div>
</div>

<!-- Ribbon Tabs -->
<div class="ribbon-tabs">
  <button class="rtab active" id="tab-message" onclick="showTab('message')">Message</button>
  <button class="rtab" id="tab-insert" onclick="showTab('insert')">Insert</button>
  <button class="rtab" id="tab-options" onclick="showTab('options')">Options</button>
  <button class="rtab" id="tab-format" onclick="showTab('format')">Format Text</button>
  <button class="rtab" id="tab-review" onclick="showTab('review')">Review</button>
  <button class="rtab" id="tab-help" onclick="showTab('help')">Help</button>
  <div style="flex:1"></div>
  <input placeholder="Tell me what you want to do..." style="border:1px solid #d1cec9;border-radius:3px;padding:3px 10px;font-size:11px;width:200px;outline:none;margin:4px 0"/>
</div>

<!-- Ribbon Content -->
<div class="ribbon" id="ribbon-message">
  <!-- Send -->
  <div style="padding:2px 8px 0;display:flex;align-items:center;border-right:1px solid #e1dfdd">
    <button class="sendbtn" onclick="doSend()"><span class="icon"><svg width="24" height="24" viewBox="0 0 16 16" fill="#0078d4"><path d="M4 2.3 13.5 8 4 13.7Z"/></svg></span><span class="lbl">Send</span></button>
  </div>
  <!-- Clipboard -->
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="doPaste()" title="Paste"><span class="icon">📋</span><span class="lbl">Paste</span></button>
      <div style="display:flex;flex-direction:column;gap:1px">
        <button class="rbtn" onclick="document.execCommand('cut')" title="Cut"><span class="icon" style="font-size:13px">✂️</span><span class="lbl">Cut</span></button>
        <button class="rbtn" onclick="document.execCommand('copy')" title="Copy"><span class="icon" style="font-size:13px">📄</span><span class="lbl">Copy</span></button>
        <button class="rbtn" title="Format Painter"><span class="icon" style="font-size:13px">🖌️</span><span class="lbl">Format</span></button>
      </div>
    </div>
    <div class="rgroup-label">Clipboard</div>
  </div>
  <!-- Basic Text -->
  <div class="rgroup">
    <div class="rgroup-btns" style="flex-direction:column;justify-content:center;gap:3px;width:270px">
      <div style="display:flex;gap:3px;align-items:center">
        <select onchange="quill.format('font',this.value||false)" style="font-size:11px;border:1px solid #d1cec9;border-radius:2px;padding:2px;height:22px;width:108px" title="Font Name">
          <option value="">Calibri</option>
          ${fontOptionsHtml}
        </select>
        <select onchange="quill.format('size',this.value||false)" style="font-size:11px;border:1px solid #d1cec9;border-radius:2px;padding:2px;height:22px;width:52px" title="Font Size">
          <option value="">11</option>
          ${sizeOptionsHtml}
        </select>
        <button class="rbtn sm" onclick="bumpSize(1)" title="Increase Font"><span class="icon" style="font-size:13px">A+</span></button>
        <button class="rbtn sm" onclick="bumpSize(-1)" title="Decrease Font"><span class="icon" style="font-size:13px">A-</span></button>
      </div>
      <div style="display:flex;gap:2px">
        <button class="rbtn sm" onclick="quill.format('bold',!quill.getFormat().bold)" title="Bold"><span class="icon" style="font-size:14px;font-weight:700">B</span></button>
        <button class="rbtn sm" onclick="quill.format('italic',!quill.getFormat().italic)" title="Italic"><span class="icon" style="font-size:14px;font-style:italic">I</span></button>
        <button class="rbtn sm" onclick="quill.format('underline',!quill.getFormat().underline)" title="Underline"><span class="icon" style="font-size:14px;text-decoration:underline">U</span></button>
        <button class="rbtn sm" onclick="quill.format('strike',!quill.getFormat().strike)" title="Strikethrough"><span class="icon" style="font-size:14px;text-decoration:line-through">S</span></button>
        <button class="rbtn sm" onclick="quill.format('color',prompt('Color (hex):','#000000')||false)" title="Font Color"><span class="icon" style="font-size:13px">A</span></button>
        <button class="rbtn sm" onclick="quill.format('background',prompt('Highlight color:','#ffff00')||false)" title="Highlight"><span class="icon" style="font-size:13px">🖍️</span></button>
      </div>
      <div style="display:flex;gap:2px">
        <button class="rbtn sm" onclick="quill.format('list','bullet')" title="Bullets"><span class="icon" style="font-size:13px">•</span></button>
        <button class="rbtn sm" onclick="quill.format('list','ordered')" title="Numbering"><span class="icon" style="font-size:13px">🔢</span></button>
        <button class="rbtn sm" onclick="quill.format('align','left')" title="Align Left"><span class="icon" style="font-size:13px">⬅️</span></button>
        <button class="rbtn sm" onclick="quill.format('align','center')" title="Center"><span class="icon" style="font-size:13px">↔️</span></button>
        <button class="rbtn sm" onclick="quill.format('align','right')" title="Align Right"><span class="icon" style="font-size:13px">➡️</span></button>
        <button class="rbtn sm" onclick="quill.format('indent','+1')" title="Indent"><span class="icon" style="font-size:13px">⇥</span></button>
      </div>
    </div>
    <div class="rgroup-label">Basic Text</div>
  </div>
  <!-- Names -->
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">📕</span><span class="lbl">Address Book</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">✔️</span><span class="lbl">Check Names</span></button>
    </div>
    <div class="rgroup-label">Names</div>
  </div>
  <!-- Include -->
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="attachFile()" title="Attach File"><span class="icon">📎</span><span class="lbl">Attach File</span></button>
      <button class="rbtn lg" onclick="insertSig()" title="Signature"><span class="icon">✍️</span><span class="lbl">Signature</span></button>
    </div>
    <div class="rgroup-label">Include</div>
  </div>
  <!-- Tags -->
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="setImp('high');notify('High Importance set')" id="btn-high" title="High Importance"><span class="icon">❗</span><span class="lbl">High Importance</span></button>
      <button class="rbtn lg" onclick="setImp('low');notify('Low Importance set')" id="btn-low" title="Low Importance"><span class="icon">⬇️</span><span class="lbl">Low Importance</span></button>
      <div style="display:flex;flex-direction:column;gap:1px">
        <button class="rbtn" onclick="notify('Follow Up flag set')"><span class="icon" style="font-size:13px">🚩</span><span class="lbl">Follow Up</span></button>
        <button class="rbtn" onclick="notify('Categorized')"><span class="icon" style="font-size:13px">🏷️</span><span class="lbl">Categorize</span></button>
      </div>
    </div>
    <div class="rgroup-label">Tags</div>
  </div>
  <!-- AI -->
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="aiDraft()" title="AI Draft"><span class="icon">✨</span><span class="lbl">AI Draft</span></button>
    </div>
    <div class="rgroup-label">AI</div>
  </div>
  <!-- Cc Bcc -->
  <div class="rgroup">
    <div class="rgroup-btns" style="display:grid;grid-template-columns:1fr 1fr;gap:2px;width:88px">
      <button class="rbtn sm" onclick="toggleRow('cc-row')" title="Show Cc field"><span class="icon" style="font-size:12px">Cc</span></button>
      <button class="rbtn sm" onclick="toggleRow('bcc-row')" title="Show Bcc field"><span class="icon" style="font-size:12px">Bcc</span></button>
      <button class="rbtn sm" onclick="saveDraft()" title="Save Draft"><span class="icon" style="font-size:13px">💾</span></button>
      <button class="rbtn sm danger" onclick="window.close()" title="Discard"><span class="icon" style="font-size:13px">🗑️</span></button>
    </div>
    <div class="rgroup-label">Actions</div>
  </div>
</div>

<!-- Insert Tab -->
<div class="ribbon" id="ribbon-insert" style="display:none">
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="attachFile()"><span class="icon">📎</span><span class="lbl">Attach File</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">📧</span><span class="lbl">Outlook Item</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">👤</span><span class="lbl">Contact</span></button>
    </div>
    <div class="rgroup-label">Include</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="insertImg()"><span class="icon">🖼️</span><span class="lbl">Pictures</span></button>
      <button class="rbtn lg" disabled title="Not built yet — browsers can't capture the screen without an explicit permission prompt" style="opacity:.45;cursor:default"><span class="icon">📸</span><span class="lbl">Screenshot</span></button>
    </div>
    <div class="rgroup-label">Illustrations</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="insertLink()"><span class="icon">🔗</span><span class="lbl">Hyperlink</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">🔖</span><span class="lbl">Bookmark</span></button>
    </div>
    <div class="rgroup-label">Links</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="insertSig()"><span class="icon">✍️</span><span class="lbl">Signature</span></button>
      <button class="rbtn lg" onclick="insertDate()"><span class="icon">📅</span><span class="lbl">Date & Time</span></button>
      <button class="rbtn lg" onclick="insertTable()"><span class="icon">📊</span><span class="lbl">Table</span></button>
    </div>
    <div class="rgroup-label">Text</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns" style="position:relative">
      <button class="rbtn lg" onclick="toggleMergeMenu(event)"><span class="icon">🏷️</span><span class="lbl">Merge Tag</span></button>
      <div id="mtag-menu-insert" class="mtag-menu" style="display:none;top:100%">${mtags}</div>
    </div>
    <div class="rgroup-label">Merge Tags</div>
  </div>
</div>

<!-- Options Tab -->
<div class="ribbon" id="ribbon-options" style="display:none">
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="schedSend()"><span class="icon">⏱️</span><span class="lbl">Delay Delivery</span></button>
      <button class="rbtn lg" onclick="schedSend()"><span class="icon">🕑</span><span class="lbl">Schedule Send</span></button>
    </div>
    <div class="rgroup-label">Delivery</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" id="btn-rr" onclick="toggleRR()" title="Honored by Outlook/Exchange recipients; often ignored by Gmail and other consumer clients"><span class="icon">📬</span><span class="lbl">Read Receipt</span></button>
      <button class="rbtn lg" disabled title="Not a separate option — enabling Read Receipt above also requests a delivery receipt on providers that support it" style="opacity:.45;cursor:default"><span class="icon">📪</span><span class="lbl">Delivery Receipt</span></button>
    </div>
    <div class="rgroup-label">Tracking</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">🔒</span><span class="lbl">Encrypt</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">✍️</span><span class="lbl">Sign</span></button>
    </div>
    <div class="rgroup-label">Security</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" id="btn-opt-high" onclick="setImp('high')"><span class="icon">❗</span><span class="lbl">High</span></button>
      <button class="rbtn lg" id="btn-opt-normal" onclick="setImp('normal')"><span class="icon">➖</span><span class="lbl">Normal</span></button>
      <button class="rbtn lg" id="btn-opt-low" onclick="setImp('low')"><span class="icon">⬇️</span><span class="lbl">Low</span></button>
    </div>
    <div class="rgroup-label">Importance</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">🗳️</span><span class="lbl">Voting Buttons</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">✅</span><span class="lbl">Approval</span></button>
    </div>
    <div class="rgroup-label">Voting</div>
  </div>
</div>

<!-- Format Text Tab -->
<div class="ribbon" id="ribbon-format" style="display:none">
  <div class="rgroup">
    <div class="rgroup-btns" style="flex-direction:column;justify-content:center;gap:3px;width:270px">
      <div style="display:flex;gap:3px;align-items:center">
        <select onchange="quill.format('font',this.value||false)" style="font-size:11px;border:1px solid #d1cec9;border-radius:2px;padding:2px;height:22px;width:108px" title="Font Name">
          <option value="">Calibri</option>
          ${fontOptionsHtml}
        </select>
        <select onchange="quill.format('size',this.value||false)" style="font-size:11px;border:1px solid #d1cec9;border-radius:2px;padding:2px;height:22px;width:52px" title="Font Size">
          <option value="">11</option>
          ${sizeOptionsHtml}
        </select>
      </div>
      <div style="display:flex;gap:3px;align-items:center">
        <input id="manualSize" type="number" min="1" max="400" step="0.5" placeholder="pt" style="font-size:11px;border:1px solid #d1cec9;padding:2px;height:22px;width:44px;border-radius:2px" title="Type an exact point size"/>
        <button class="rbtn sm" onclick="applyManualSize()" title="Apply the typed point size to the selected text"><span class="icon" style="font-size:10px">Set</span></button>
        <button class="rbtn sm" onclick="quill.format('bold',!quill.getFormat().bold)" title="Bold"><span class="icon" style="font-size:14px;font-weight:700">B</span></button>
        <button class="rbtn sm" onclick="quill.format('italic',!quill.getFormat().italic)" title="Italic"><span class="icon" style="font-size:14px;font-style:italic">I</span></button>
        <button class="rbtn sm" onclick="quill.format('underline',!quill.getFormat().underline)" title="Underline"><span class="icon" style="font-size:14px;text-decoration:underline">U</span></button>
        <button class="rbtn sm" onclick="quill.format('strike',!quill.getFormat().strike)" title="Strikethrough"><span class="icon" style="font-size:14px;text-decoration:line-through">S</span></button>
        <button class="rbtn sm" onclick="quill.removeFormat(quill.getSelection()?.index||0,quill.getSelection()?.length||0)" title="Clear Formatting"><span class="icon" style="font-size:13px">✖</span></button>
      </div>
    </div>
    <div class="rgroup-label">Font</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns" style="flex-direction:column;justify-content:center;gap:3px">
      <div style="display:flex;gap:2px">
        <button class="rbtn sm" onclick="quill.format('list','bullet')" title="Bullets"><span class="icon" style="font-size:13px">•</span></button>
        <button class="rbtn sm" onclick="quill.format('list','ordered')" title="Numbering"><span class="icon" style="font-size:13px">🔢</span></button>
        <button class="rbtn sm" onclick="quill.format('indent','-1')" title="Decrease Indent"><span class="icon" style="font-size:13px">⇤</span></button>
        <button class="rbtn sm" onclick="quill.format('indent','+1')" title="Increase Indent"><span class="icon" style="font-size:13px">⇥</span></button>
      </div>
      <div style="display:flex;gap:2px">
        <button class="rbtn sm" onclick="quill.format('align','left')" title="Align Left"><span class="icon" style="font-size:13px">⬅️</span></button>
        <button class="rbtn sm" onclick="quill.format('align','center')" title="Center"><span class="icon" style="font-size:13px">↔️</span></button>
        <button class="rbtn sm" onclick="quill.format('align','right')" title="Align Right"><span class="icon" style="font-size:13px">➡️</span></button>
        <button class="rbtn sm" onclick="quill.format('align','justify')" title="Justify"><span class="icon" style="font-size:13px">☰</span></button>
      </div>
    </div>
    <div class="rgroup-label">Paragraph</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="quill.format('header',1)" title="Heading 1"><span class="icon" style="font-size:15px;font-weight:700">H1</span><span class="lbl">Heading 1</span></button>
      <button class="rbtn lg" onclick="quill.format('header',2)" title="Heading 2"><span class="icon" style="font-size:14px;font-weight:700">H2</span><span class="lbl">Heading 2</span></button>
      <button class="rbtn lg" onclick="quill.format('header',false)" title="Normal Text"><span class="icon" style="font-size:16px">¶</span><span class="lbl">Normal</span></button>
      <button class="rbtn lg" onclick="quill.format('blockquote',true)" title="Quote"><span class="icon" style="font-size:17px">❝</span><span class="lbl">Quote</span></button>
      <button class="rbtn lg" onclick="quill.format('code-block',true)" title="Code"><span class="icon" style="font-size:13px">&lt;/&gt;</span><span class="lbl">Code</span></button>
    </div>
    <div class="rgroup-label">Styles</div>
  </div>
</div>

<!-- Review Tab -->
<div class="ribbon" id="ribbon-review" style="display:none">
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" disabled title="Not built yet — browsers' native spellcheck (red underline) is already active in the editor" style="opacity:.45;cursor:default"><span class="icon">ABC</span><span class="lbl">Spell Check</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">📖</span><span class="lbl">Grammar</span></button>
    </div>
    <div class="rgroup-label">Proofing</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="doTranslate()"><span class="icon">🌐</span><span class="lbl">Translate</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">🗣️</span><span class="lbl">Language</span></button>
    </div>
    <div class="rgroup-label">Language</div>
  </div>
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="doWordCount()"><span class="icon">🔢</span><span class="lbl">Word Count</span></button>
      <button class="rbtn lg" disabled title="Not built yet" style="opacity:.45;cursor:default"><span class="icon">♿</span><span class="lbl">Accessibility</span></button>
    </div>
    <div class="rgroup-label">Review</div>
  </div>
</div>

<!-- Help Tab -->
<div class="ribbon" id="ribbon-help" style="display:none">
  <div class="rgroup">
    <div class="rgroup-btns">
      <button class="rbtn lg" onclick="window.open('https://support.microsoft.com/outlook')"><span class="icon">❓</span><span class="lbl">Help</span></button>
      <button class="rbtn lg" onclick="notify('Feedback submitted  thank you!')"><span class="icon">💬</span><span class="lbl">Feedback</span></button>
      <button class="rbtn lg" onclick="notify('SDR ReachOut Platform v1.0  Enterprise Edition')"><span class="icon">ℹ️</span><span class="lbl">About</span></button>
    </div>
    <div class="rgroup-label">Help</div>
  </div>
</div>

<!-- Fields -->
<div class="fields">
  <div class="frow"><label>From</label><select id="frm" style="font-size:12px">${sndr||`<option value="${settings.senderEmail||""}">${settings.sdrName} (${settings.senderEmail||""})</option>`}</select></div>
  <div class="frow" id="to-row"><label onclick="document.getElementById('to').focus()">To</label><input id="to" placeholder="recipient@company.com; separate multiple with ; or ," oninput="upd();rcptInput('to')" onkeydown="rcptKeyDown('to',event)" onblur="rcptBlur('to')"/></div>
  <div class="frow" id="cc-row" style="display:none"><label>Cc</label><input id="cc" placeholder="cc@company.com; separate multiple with ; or ," oninput="rcptInput('cc')" onkeydown="rcptKeyDown('cc',event)" onblur="rcptBlur('cc')"/></div>
  <div class="frow" id="bcc-row" style="display:none"><label>Bcc</label><input id="bcc" placeholder="bcc@company.com; separate multiple with ; or ," oninput="rcptInput('bcc')" onkeydown="rcptKeyDown('bcc',event)" onblur="rcptBlur('bcc')"/></div>
  <div class="frow"><label onclick="document.getElementById('sub').focus()">Subject</label><input id="sub" placeholder="Subject" style="font-size:14px;font-weight:500" oninput="upd()" onfocus="setFoc('subject')" onclick="setFoc('subject')"/></div>
</div>

<!-- Merge tag bar -->
<div class="mtbar">
  <div style="position:relative">
    <button class="mtag-toggle" onclick="toggleMergeMenu(event)" id="mtag-toggle">🏷️ Insert Merge Tag <span style="font-size:9px">▾</span></button>
    <div id="mtag-menu" class="mtag-menu" style="display:none">${mtags}</div>
  </div>
  <span style="font-size:10px;color:#8890b0"> into <strong id="flbl">subject line</strong></span>
  <button class="aibtn" onclick="aiDraft()">✨ AI Draft</button>
</div>

<!-- Attachment bar -->
<div class="attach-bar" id="attach-bar"></div>

<!-- Editor -->
<div class="editor" onclick="setFoc('body')" onmousedown="setFoc('body')">
  <div id="editor" style="flex:1"></div>
</div>

<div class="sig" id="sig-bar"><strong>${settings.sdrName||"Anji Reddy"}</strong>  ${settings.company||"SDR ReachOut"}  ${senders[0]?.email||settings.senderEmail||""}</div>
<div class="statusbar">
  <span id="stxt">Ready to compose</span>
  <span id="wc-display">Words: 0</span>
  <span>SDR ReachOut Platform</span>
</div>
<script>
// Surface ANY uncaught error instead of failing silently — if a click does
// nothing, this makes the actual JS error visible in the status bar and
// as a toast, rather than leaving no trace of what went wrong.
window.onerror=function(msg,src,line,col,err){
  try{
    var st=document.getElementById('stxt');
    if(st)st.textContent='Script error: '+msg+' (line '+line+')';
    var el=document.getElementById('notify');
    if(el){el.textContent='Error: '+msg;el.style.display='block';el.style.background='#a4262c';setTimeout(function(){el.style.display='none';},6000);}
  }catch(reportEx){}
  return false;
};
var quill,NL=String.fromCharCode(10);
var lf='subject';
var attachments=[];
var imp='normal';
var rr=false;
var draftId=null;
var SIZES=${sizeNumbersJson};
var autoSaveTimer=null;
// Outlook-style To/Cc/Bcc contact autocomplete — same contacts + same
// behavior as the inline Workspace compose panel (React side); this is
// the plain-DOM version for this standalone Pop Out window. See
// rcptInput/rcptKeyDown/rcptBlur below.
var CONTACTS=${contactsJson};
var rcptState=null; // {field, items, activeIdx} while a dropdown is open

// ---- Paste cleanup (this pop-out window only) ---------------------------
// Simplified vanilla-JS port of client/src/richtext/pasteClean.js for this
// window's standalone CDN-loaded Quill instance, which has no access to the
// app's bundled React modules. This popup's Quill only registers the Font
// (class) and Size (style) custom Attributors above -- no underlinestyle,
// texteffect, lineheight, pshading, pborder, parabefore/paraafter -- so
// there's no "keep/merge/match" mode selector here (this popup has no Paste
// Options UI), just always-preserve-formatting cruft stripping plus the
// same font-family / CSS-underline conversion-survival fix the shared
// engine uses: a pasted style="font-family:..." or
// style="text-decoration:underline" (exactly how Word/Google Docs express
// these) is silently dropped by Quill's HTML-to-Delta conversion unless
// translated into a form its registered Attributors actually match (a
// ql-font-<value> class for font; a real <u> tag for underline).
var PASTE_FONT_LIST=${fontListJson};
var PASTE_ALLOWED_STYLE_PROPS=['font-family','font-size','font-weight','font-style','text-decoration','text-decoration-line','text-decoration-style','text-decoration-thickness','color','background-color','text-align','padding-left','margin-left','margin-top','margin-bottom','line-height','border','text-shadow','-webkit-text-stroke'];
var PASTE_KEPT_ATTRS=['style','class','href','src','alt','width','height','colspan','rowspan','target'];
var PASTE_PT_TO_PX=96/72,PASTE_IN_TO_PX=96,PASTE_CM_TO_PX=96/2.54,PASTE_MM_TO_PX=96/25.4,PASTE_EM_TO_PX=16;
var PASTE_MAX_MARGIN_PX=12,PASTE_MAX_LH_PX=40,PASTE_MAX_LH_UNITLESS=2.2,PASTE_MAX_LH_PCT=220;
// NOTE ON DOUBLE BACKSLASHES BELOW: this whole block is literal text inside
// the outer w.document.write(\`...\`) template literal in openInWindow()
// above -- NOT a separate module -- so it goes through the OUTER template
// literal's own string-escape processing once at build time before it ever
// reaches the popup as source text. A single "\\d"/"\\s"/"\\/" here would
// have its backslash silently dropped by that outer processing (confirmed
// empirically), landing in the popup as the bare, meaningless character
// (e.g. a literal "d" instead of the \\d digit-class escape) rather than a
// working regex -- exactly the kind of bug this file's own pre-existing
// Word Count feature already has (its "\\s" in the same template literal
// context lands as a literal "s", so it strips the letter s instead of
// whitespace -- confirmed against the live production bundle, pre-existing,
// not introduced here). Every backslash meant to survive into the popup's
// actual regex/string literals is therefore doubled here on purpose.
function pasteLengthToPx(numStr,unit){
  var n=parseFloat(numStr);
  if(!isFinite(n))return null;
  if(unit==='px')return n;
  if(unit==='pt')return n*PASTE_PT_TO_PX;
  if(unit==='in')return n*PASTE_IN_TO_PX;
  if(unit==='cm')return n*PASTE_CM_TO_PX;
  if(unit==='mm')return n*PASTE_MM_TO_PX;
  if(unit==='em')return n*PASTE_EM_TO_PX;
  return null;
}
function pasteCapLength(raw,maxPx){
  var m=/^(-?[\\d.]+)\\s*(px|pt|in|cm|mm|em)?$/.exec(raw.trim());
  if(!m)return raw;
  var unit=m[2]||'px';
  var px=pasteLengthToPx(m[1],unit);
  if(px===null||px<=maxPx)return raw;
  var scale=px/parseFloat(m[1]);
  return (Math.round((maxPx/scale)*100)/100)+unit;
}
function pasteCapLineHeight(raw){
  var trimmed=raw.trim();
  var pct=/^(-?[\\d.]+)%$/.exec(trimmed);
  if(pct){var n=parseFloat(pct[1]);return n>PASTE_MAX_LH_PCT?(PASTE_MAX_LH_PCT+'%'):raw;}
  var unitless=/^(-?[\\d.]+)$/.exec(trimmed);
  if(unitless){var n2=parseFloat(unitless[1]);return n2>PASTE_MAX_LH_UNITLESS?String(PASTE_MAX_LH_UNITLESS):raw;}
  return pasteCapLength(raw,PASTE_MAX_LH_PX);
}
function pasteStripCruft(html){
  return String(html)
    .replace(/<!--[\\s\\S]*?-->/g,'')
    .replace(/<\\/?(?:o|w|v|m):[a-zA-Z0-9]+(?:\\s[^>]*)?>/gi,'')
    .replace(/<style[\\s\\S]*?<\\/style>/gi,'')
    .replace(/<script[\\s\\S]*?<\\/script>/gi,'')
    .replace(/<link[^>]*>/gi,'')
    .replace(/<meta[^>]*>/gi,'')
    .replace(/<xml[\\s\\S]*?<\\/xml>/gi,'')
    .replace(/<!\\[if[\\s\\S]*?\\]>/gi,'')
    .replace(/<!\\[endif\\]>/gi,'');
}
function pasteGetStyleProp(styleAttr,prop){
  if(!styleAttr)return '';
  var decls=styleAttr.split(';');
  for(var i=0;i<decls.length;i++){
    var idx=decls[i].indexOf(':');
    if(idx===-1)continue;
    if(decls[i].slice(0,idx).trim().toLowerCase()===prop)return decls[i].slice(idx+1).trim().toLowerCase();
  }
  return '';
}
function pasteMatchFont(styleAttr){
  var raw=pasteGetStyleProp(styleAttr,'font-family');
  if(!raw)return null;
  var first=raw.split(',')[0].trim().replace(/^["']|["']$/g,'').toLowerCase();
  if(!first)return null;
  for(var i=0;i<PASTE_FONT_LIST.length;i++){
    var f=PASTE_FONT_LIST[i];
    if(f.label.toLowerCase()===first||f.value===first.replace(/\\s+/g,''))return f;
  }
  return null;
}
function pasteDetectUnderline(styleAttr){
  if(!styleAttr)return false;
  var shorthand=pasteGetStyleProp(styleAttr,'text-decoration');
  var line=pasteGetStyleProp(styleAttr,'text-decoration-line');
  return /(^|\\s)underline(\\s|$)/.test(shorthand)||/(^|\\s)underline(\\s|$)/.test(line);
}
function pasteCleanStyleAttr(el){
  var style=el.getAttribute('style');
  if(!style)return;
  var kept=[];
  style.split(';').forEach(function(decl){
    var idx=decl.indexOf(':');
    if(idx===-1)return;
    var prop=decl.slice(0,idx).trim().toLowerCase();
    var val=decl.slice(idx+1).trim();
    if(!prop||!val)return;
    if(PASTE_ALLOWED_STYLE_PROPS.indexOf(prop)===-1)return;
    if(prop==='margin-top'||prop==='margin-bottom')val=pasteCapLength(val,PASTE_MAX_MARGIN_PX);
    else if(prop==='line-height')val=pasteCapLineHeight(val);
    kept.push(prop+': '+val);
  });
  if(kept.length)el.setAttribute('style',kept.join('; '));
  else el.removeAttribute('style');
}
function pasteIsBlankParagraph(el){
  var text=el.textContent.replace(/ /g,' ').trim();
  return !text&&!el.querySelector('img,br')&&el.children.length===0;
}
function pasteCleanNode(root){
  var toRemove=root.querySelectorAll('style,script,meta,link,title,xml,head');
  Array.prototype.slice.call(toRemove).forEach(function(el){if(el.parentNode)el.parentNode.removeChild(el);});
  Array.prototype.slice.call(root.querySelectorAll('*')).forEach(function(el){
    Array.prototype.slice.call(el.attributes).forEach(function(attr){
      if(PASTE_KEPT_ATTRS.indexOf(attr.name.toLowerCase())===-1)el.removeAttribute(attr.name);
    });
    var rawStyle=el.getAttribute('style');
    if(rawStyle){
      var fontMatch=pasteMatchFont(rawStyle);
      if(fontMatch){
        el.classList.add('ql-font-'+fontMatch.value);
        // Leaving both the injected class and the raw font-family style on
        // the same element makes Quill's clipboard matcher drop BOTH
        // (confirmed on the shared engine -- see pasteClean.js), not just
        // ignore the now-redundant style, so it must be removed here too.
        if(el.style)el.style.removeProperty('font-family');
      }
      if(pasteDetectUnderline(rawStyle)){
        var wrapper=document.createElement('u');
        while(el.firstChild)wrapper.appendChild(el.firstChild);
        el.appendChild(wrapper);
        if(el.style){
          el.style.removeProperty('text-decoration');
          el.style.removeProperty('text-decoration-line');
          el.style.removeProperty('text-decoration-style');
          el.style.removeProperty('text-decoration-thickness');
        }
      }
    }
    pasteCleanStyleAttr(el);
    var cls=el.getAttribute('class');
    if(cls){
      var tokens=cls.split(/\\s+/).filter(function(t){return t&&t.indexOf('ql-')===0;});
      if(tokens.length)el.setAttribute('class',tokens.join(' '));
      else el.removeAttribute('class');
    }
  });
  Array.prototype.slice.call(root.querySelectorAll('span')).forEach(function(el){
    if(!el.getAttribute('style')&&!el.getAttribute('class')&&el.parentNode){
      while(el.firstChild)el.parentNode.insertBefore(el.firstChild,el);
      el.parentNode.removeChild(el);
    }
  });
  Array.prototype.slice.call(root.querySelectorAll('p,div')).forEach(function(el){
    if(pasteIsBlankParagraph(el)&&el.parentNode)el.parentNode.removeChild(el);
  });
}
function pasteNormalizeBlanks(html){
  var out=html.replace(/<p>\\s*<\\/p>/g,'');
  out=out.replace(/(?:<p><br><\\/p>\\s*){3,}/g,'<p><br></p><p><br></p>');
  return out;
}
function cleanPastedHtmlPopup(rawHtml){
  if(!rawHtml)return '';
  var stripped=pasteStripCruft(rawHtml);
  try{
    var container=document.createElement('div');
    container.innerHTML=stripped;
    pasteCleanNode(container);
    return pasteNormalizeBlanks(container.innerHTML);
  }catch(e){
    console.error('cleanPastedHtmlPopup: DOM cleanup failed, falling back to regex-only cleanup',e);
    return pasteNormalizeBlanks(stripped);
  }
}
function pasteEscapeHtml(str){
  return String(str||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}
// Registered on quill.container (an ANCESTOR of quill.root, where Quill's
// own clipboard module listens without capture) with capture:true, so this
// always runs first and can preventDefault+stopPropagation before Quill's
// own uncleaned paste handling -- same technique as the shared
// RichTextEditor.js's makeNativePasteHandler.
function handlePopupPaste(e){
  var cd=e.clipboardData||window.clipboardData;
  if(!cd)return;
  var html=cd.getData('text/html');
  var text=cd.getData('text/plain');
  if(!html&&!text)return;
  e.preventDefault();
  e.stopPropagation();
  var sel=quill.getSelection(true);
  var idx=sel?sel.index:quill.getLength();
  var len=sel?sel.length:0;
  try{
    var cleaned=html?cleanPastedHtmlPopup(html):pasteEscapeHtml(text||'').replace(/\\n/g,'<br>');
    if(len)quill.deleteText(idx,len,'user');
    quill.clipboard.dangerouslyPasteHTML(idx,cleaned,'user');
  }catch(err){
    console.error('Compose window paste handler failed, falling back to plain text',err);
    try{
      if(len)quill.deleteText(idx,len,'user');
      quill.clipboard.dangerouslyPasteHTML(idx,pasteEscapeHtml(text||'').replace(/\\n/g,'<br>'),'user');
    }catch(err2){
      console.error('Compose window paste handler: plain-text fallback also failed',err2);
    }
  }
}

function initApp(){
try{
  if(typeof Quill==='undefined')throw new Error('Quill failed to load from CDN (check your network/ad-blocker)');
  try{
    var FontClass=Quill.import('attributors/class/font');
    FontClass.whitelist=${fontWhitelistJson};
    Quill.register(FontClass,true);
    var SizeStyle=Quill.import('attributors/style/size');
    SizeStyle.whitelist=${sizeWhitelistJson};
    Quill.register(SizeStyle,true);
  }catch(fex){ /* falls back to Quill's default small/large/huge sizes */ }
  quill=new Quill('#editor',{theme:'snow',placeholder:'Write your message here...',modules:{toolbar:false}});
  quill.container.addEventListener('paste',handlePopupPaste,true);
  document.getElementById('sub').addEventListener('focus',function(){setFoc('subject');});
  quill.on('selection-change',function(r){if(r)setFoc('body');});
  quill.on('text-change',function(){
    var text=quill.getText().trim();
    // NOTE: \\s doubled deliberately -- see the paste-cleanup block's
    // comment above initApp() for why a single backslash here would be
    // silently dropped by the outer template literal at build time (this
    // was in fact a pre-existing bug in this exact line before this fix:
    // it was splitting words on literal "s" characters, not whitespace).
    var words=text?text.split(/\\s+/).length:0;
    document.getElementById('wc-display').textContent='Words: '+words+' | Chars: '+text.length;
    var wct=document.getElementById('wc-title');if(wct)wct.textContent='Words: '+words;
    upd();
    clearTimeout(autoSaveTimer);
    autoSaveTimer=setTimeout(function(){saveDraft(true);},3000);
  });
  document.addEventListener('keydown',function(e){
    if(e.ctrlKey||e.metaKey){
      if(e.key==='Enter'){e.preventDefault();doSend();}
      else if(e.key==='s'){e.preventDefault();saveDraft();}
      else if(e.key==='k'){e.preventDefault();insertLink();}
    }
  });
  quill.root.addEventListener('keydown',function(e){
    if(e.key!=='Enter')return;
    var sel=quill.getSelection();if(!sel)return;
    var text=quill.getText();
    var ls=text.lastIndexOf(NL,sel.index-1)+1;
    var lt=text.slice(ls,sel.index).trim();
    if(lt==='---'||lt==='___'){e.preventDefault();quill.deleteText(ls,sel.index-ls);quill.clipboard.dangerouslyPasteHTML(ls,'<hr style="border:none;border-top:2px solid #d1cec9;margin:14px 0;display:block;width:100%"/><p><br></p>');setTimeout(function(){quill.setSelection(ls+2,0);},10);}
    else if(lt==='==='){e.preventDefault();quill.deleteText(ls,sel.index-ls);quill.clipboard.dangerouslyPasteHTML(ls,'<hr style="border:none;border-top:3px solid #0078d4;margin:14px 0;display:block;width:100%"/><p><br></p>');setTimeout(function(){quill.setSelection(ls+2,0);},10);}
    else if(lt==='***'){e.preventDefault();quill.deleteText(ls,sel.index-ls);quill.clipboard.dangerouslyPasteHTML(ls,'<hr style="border:none;border-top:2px dashed #a19f9d;margin:14px 0;display:block;width:100%"/><p><br></p>');setTimeout(function(){quill.setSelection(ls+2,0);},10);}
  },true);
}catch(initEx){
  // Surface the failure instead of leaving a silent blank editor — if the
  // rich-text editor genuinely can't load (CDN blocked, script error), the
  // user should see why rather than stare at empty white space.
  console.error('Compose editor failed to initialize:',initEx);
  var ed=document.getElementById('editor');
  if(ed)ed.innerHTML='<div style="padding:24px;color:#a4262c;font-family:\\'Segoe UI\\',sans-serif;font-size:13px;line-height:1.6">The rich-text editor failed to load'+(initEx&&initEx.message?' ('+initEx.message+')':'')+'.<br/>Try closing this window and using Pop Out again, or compose in the main workspace window instead.</div>';
  var st=document.getElementById('stxt');if(st)st.textContent='Editor failed to load';
}
} // end initApp
// Tab switching
function showTab(name){
  ['message','insert','options','format','review','help'].forEach(function(t){
    document.getElementById('ribbon-'+t).style.display=t===name?'flex':'none';
    document.getElementById('tab-'+t).classList.toggle('active',t===name);
  });
}

// Notifications
function notify(msg,duration){
  var el=document.getElementById('notify');
  el.textContent=msg;el.style.display='block';
  setTimeout(function(){el.style.display='none';},duration||2500);
}

// Focus tracking
function setFoc(f){lf=f;var el=document.getElementById('flbl');if(el){el.textContent=f==='body'?'email body':'subject line';}}

// Merge tag dropdown — one toggle handler shared by every ".mtag-menu"
// instance (the persistent bar and the Insert tab both use it).
function toggleMergeMenu(e){
  e.stopPropagation();
  var menu=e.currentTarget.nextElementSibling;
  if(!menu||!menu.classList.contains('mtag-menu'))return;
  var willOpen=menu.style.display==='none';
  document.querySelectorAll('.mtag-menu').forEach(function(m){m.style.display='none';});
  if(willOpen)menu.style.display='flex';
}
document.addEventListener('click',function(){document.querySelectorAll('.mtag-menu').forEach(function(m){m.style.display='none';});});

// Merge tag insert
function ins(tag){
  document.querySelectorAll('.mtag-menu').forEach(function(m){m.style.display='none';});
  if(lf==='subject'){
    var i=document.getElementById('sub');
    var s=i.selectionStart||0,e=i.selectionEnd||0;
    i.value=i.value.slice(0,s)+tag+i.value.slice(e);
    i.focus();i.setSelectionRange(s+tag.length,s+tag.length);
  }else{
    var r=quill.getSelection(true);
    var idx=r?r.index:quill.getLength();
    quill.insertText(idx,tag);quill.setSelection(idx+tag.length);
  }
}

// Title update
function upd(){
  var t=document.getElementById('to').value;
  var s=document.getElementById('sub').value;
  document.getElementById('wtitle').textContent=(s||'Untitled')+'  Message (HTML)';
  document.title=(s||'Untitled')+'  SDR ReachOut';
}

// Toggle rows
function toggleRow(id){
  var r=document.getElementById(id);
  r.style.display=r.style.display==='none'?'flex':'none';
}

// Importance
function setImp(l){
  imp=l;
  ['high','low'].forEach(function(k){var b=document.getElementById('btn-'+k);if(b)b.classList.toggle('active',imp===k);});
  ['high','normal','low'].forEach(function(k){var b=document.getElementById('btn-opt-'+k);if(b)b.classList.toggle('active',imp===k);});
  document.getElementById('stxt').textContent='Importance: '+l.charAt(0).toUpperCase()+l.slice(1);
}

// Read receipt request — real flag, sent as readReceiptRequested on send/draft/schedule.
// Honored by Outlook/Exchange recipients; Gmail and other consumer clients commonly ignore it.
function toggleRR(){
  rr=!rr;
  var b=document.getElementById('btn-rr');if(b)b.classList.toggle('active',rr);
  notify(rr?'Read receipt will be requested':'Read receipt request removed');
  document.getElementById('stxt').textContent=rr?'Read receipt requested':'Ready to compose';
}

// Manual/preset font size, via the Quill size Attributor registered in initApp().
function bumpSize(dir){
  if(!quill)return;
  var cur=(quill.getFormat()||{}).size;
  var curNum=cur?parseFloat(cur):11;
  var idx=SIZES.indexOf(curNum);
  if(idx<0){
    idx=0;
    for(var i=1;i<SIZES.length;i++){if(Math.abs(SIZES[i]-curNum)<Math.abs(SIZES[idx]-curNum))idx=i;}
  }
  var next=SIZES[Math.min(SIZES.length-1,Math.max(0,idx+dir))];
  quill.format('size',next+'pt');
}
function applyManualSize(){
  var v=parseFloat(document.getElementById('manualSize').value);
  if(!v||v<=0){notify('Enter a valid point size');return;}
  var px=v+'pt';
  try{
    var SizeStyle=Quill.import('attributors/style/size');
    if(SizeStyle.whitelist&&SizeStyle.whitelist.indexOf(px)===-1)SizeStyle.whitelist.push(px);
  }catch(ex){}
  quill.format('size',px);
  notify('Font size set to '+v+'pt');
}

// Real draft persistence — posts to the same /api/email/draft endpoint the
// in-pane compose editor uses. draftId is remembered after the first save so
// later saves update that row instead of creating a duplicate draft.
async function saveDraft(silent){
  if(!quill)return;
  var to=document.getElementById('to').value;
  var sub=document.getElementById('sub').value;
  var body=quill.root.innerHTML;
  if(!to&&!sub&&(!body||body==='<p><br></p>')){if(!silent)notify('Nothing to save yet');return;}
  var from=document.getElementById('frm').value;
  var cc=document.getElementById('cc')?document.getElementById('cc').value:'';
  var bcc=document.getElementById('bcc')?document.getElementById('bcc').value:'';
  try{
    var tk=localStorage.getItem("sdr_token")||"";
    var r=await fetch("/api/email/draft",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({id:draftId,to:to,cc:cc,bcc:bcc,subject:sub,body:body,senderEmail:from,importance:imp,readReceiptRequested:rr})});
    var d=await r.json().catch(function(){return {};});
    if(!r.ok)throw new Error(d.error||'Save failed');
    if(d.id)draftId=d.id;
    if(!silent)notify('Draft saved at '+new Date().toLocaleTimeString());
    document.getElementById('stxt').textContent='Draft saved at '+new Date().toLocaleTimeString();
  }catch(ex){
    if(!silent)notify("Couldn't save draft: "+ex.message);
    document.getElementById('stxt').textContent='Save failed: '+ex.message;
  }
}

// Real send-later scheduling — posts to /api/email/schedule, the same real
// background-poller-backed endpoint the in-pane compose editor uses.
async function schedSend(){
  if(!quill)return;
  var to=document.getElementById('to').value;
  var sub=document.getElementById('sub').value;
  var body=quill.root.innerHTML;
  if(!to){notify('Enter a recipient first');return;}
  if(!sub){notify('Enter a subject first');return;}
  if(!body||body==='<p><br></p>'){notify('Write a message first');return;}
  var d=prompt('Schedule send  Enter date and time (YYYY-MM-DD HH:MM):');
  if(!d)return;
  var when=new Date(d.replace(' ','T'));
  if(isNaN(when.getTime())||when.getTime()<=Date.now()){notify('Enter a valid future date/time as YYYY-MM-DD HH:MM');return;}
  var from=document.getElementById('frm').value;
  var cc=document.getElementById('cc')?document.getElementById('cc').value:'';
  var bcc=document.getElementById('bcc')?document.getElementById('bcc').value:'';
  document.getElementById('stxt').textContent='Scheduling...';
  try{
    var tk=localStorage.getItem("sdr_token")||"";
    var r=await fetch("/api/email/schedule",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({to:to,cc:cc,bcc:bcc,subject:sub,body:body,senderEmail:from,fromName:${JSON.stringify(settings.sdrName||"")},importance:imp,readReceiptRequested:rr,sendAt:when.toISOString(),source:'compose'})});
    var dd=await r.json().catch(function(){return {};});
    if(!r.ok)throw new Error(dd.error||'Schedule failed');
    notify('Scheduled for '+when.toLocaleString());
    document.getElementById('stxt').textContent='Scheduled for '+when.toLocaleString();
    try{if(window.opener&&!window.opener.closed)window.opener.postMessage({type:'sdr-popout-sent'},window.location.origin);}catch(pmEx){}
    setTimeout(function(){window.close();},900);
  }catch(ex){
    notify("Couldn't schedule: "+ex.message);
    document.getElementById('stxt').textContent='Schedule failed: '+ex.message;
  }
}

// Paste
function doPaste(){
  navigator.clipboard.readText().then(function(t){
    var idx=quill.getSelection(true)?.index||quill.getLength();
    quill.insertText(idx,t);
    notify('Pasted from clipboard');
  }).catch(function(){document.execCommand('paste');});
}

// Attach file
function attachFile(){
  var i=document.createElement('input');i.type='file';i.multiple=true;
  i.onchange=function(){
    Array.from(i.files).forEach(function(f){
      attachments.push(f);
      var bar=document.getElementById('attach-bar');
      var item=document.createElement('div');item.className='attach-item';
      item.innerHTML=' '+f.name+' ('+Math.round(f.size/1024)+'KB) <button onclick="this.parentElement.remove()" title="Remove" aria-label="Remove" style="cursor:pointer">&times;</button>';
      bar.appendChild(item);
    });
    notify(i.files.length+' file(s) attached');
  };
  i.click();
}

// Insert image
function insertImg(){
  var i=document.createElement('input');i.type='file';i.accept='image/*';
  i.onchange=function(){
    var r=new FileReader();
    r.onload=function(e){
      var idx=quill.getSelection(true)?.index||quill.getLength();
      quill.insertEmbed(idx,'image',e.target.result);
      notify('Image inserted');
    };
    r.readAsDataURL(i.files[0]);
  };
  i.click();
}

// Insert link
function insertLink(){
  var url=prompt('Enter URL:','https://');
  if(url){
    var text=prompt('Link text:',url);
    var idx=quill.getSelection(true)?.index||quill.getLength();
    quill.insertText(idx,text||url,{link:url});
    notify('Link inserted');
  }
}

// Insert date
function insertDate(){
  var idx=quill.getSelection(true)?.index||quill.getLength();
  quill.insertText(idx,new Date().toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'}));
  notify('Date inserted');
}

// Insert table
function insertTable(){
  var rows=parseInt(prompt('Number of rows:','3'))||3;
  var cols=parseInt(prompt('Number of columns:','3'))||3;
  var html='<table border="1" style="border-collapse:collapse;width:100%">';
  for(var r=0;r<rows;r++){html+='<tr>';for(var c=0;c<cols;c++){html+='<td style="padding:6px 10px;border:1px solid #ccc">'+(r===0?'<strong>Header '+(c+1)+'</strong>':'Cell')+'</td>';}html+='</tr>';}
  html+='</table><p><br></p>';
  var idx=quill.getSelection(true)?.index||quill.getLength();
  quill.clipboard.dangerouslyPasteHTML(idx,html);
  notify('Table inserted ('+rows+''+cols+')');
}

// Insert signature
function insertSig(){
  var sig='<br><br>-- <br><strong>${(settings.sdrName||"Anji Reddy").replace(/'/g,"\\'")}</strong><br>${(settings.company||"SDR ReachOut").replace(/'/g,"\\'")}  ${(senders[0]?.email||settings.senderEmail||"").replace(/'/g,"\\'")}';
  var idx=quill.getLength();
  quill.clipboard.dangerouslyPasteHTML(idx,sig);
  notify('Signature inserted');
}

// Translate
function doTranslate(){
  var text=quill.getText().trim();
  if(!text){notify('No text to translate');return;}
  var lang=prompt('Translate to (e.g. es, fr, de, hi):','es');
  if(lang)notify('Translation feature requires API key configuration');
}

// Word count dialog
function doWordCount(){
  var text=quill.getText().trim();
  var words=text?text.split(/\\s+/).length:0;
  alert('Word Count\\n\\nWords: '+words+'\\nCharacters (no spaces): '+text.replace(/\\s/g,'').length+'\\nCharacters (with spaces): '+text.length+'\\nParagraphs: '+text.split('\\n\\n').length+'\\nLines: '+text.split('\\n').length);
}

// AI Draft
async function aiDraft(){
  document.getElementById('stxt').textContent='AI generating...';
  notify('Generating AI draft...');
  var to=document.getElementById('to').value;
  try{
    var r=await fetch('/api/ai/sequence-step',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({lead:{email:to,first:to.split('@')[0],name:to,title:'',company:''},settings:{sdrName:'${(settings.sdrName||"Andrew").replace(/'/g,"\\'")}',company:'${(settings.company||"SDR ReachOut").replace(/'/g,"\\'")}',valueProp:'${(settings.valueProp||"AI-powered QA engineering").replace(/'/g,"\\'")}'},stepLabel:'Fresh outreach',stepDay:'Now',stepDesc:'Email',prevEmails:'',isFollowUp:false})});
    var d=await r.json();
    if(d.subject)document.getElementById('sub').value=d.subject;
    if(d.body)quill.clipboard.dangerouslyPasteHTML(d.body);
    document.getElementById('stxt').textContent='AI draft ready';
    notify('AI draft generated!');
    upd();
  }catch(e){document.getElementById('stxt').textContent='AI error: '+e.message;notify('AI error: '+e.message);}
}

// ---- Recipient (To/Cc/Bcc) autocomplete ----------------------------------
// Mirrors the same behavior as the inline Workspace compose panel: the
// current (last) recipient token — everything typed since the previous ;
// or , — is matched against CONTACTS by name/email as you type; picking a
// suggestion, or just finishing a full address yourself and pressing
// space, inserts "<email>; " and leaves the field ready for the next
// recipient, same as Outlook desktop. The backend already accepts either
// ; or , (server.js's normalizeRecipients), so either separator works.
var EMAIL_LIKE_RE=/^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
function rcptToken(val){
  var parts=String(val||'').split(/[,;]/);
  return parts[parts.length-1].replace(/^\\s+/,'');
}
function rcptMatches(token){
  var q=token.trim().toLowerCase();
  if(!q)return [];
  return CONTACTS.filter(function(c){return c.email&&((c.name||'').toLowerCase().indexOf(q)>-1||c.email.toLowerCase().indexOf(q)>-1);}).slice(0,6);
}
function rcptCloseMenu(){
  var m=document.getElementById('rcpt-menu');
  if(m)m.parentNode.removeChild(m);
  rcptState=null;
}
function rcptRender(){
  rcptCloseMenu();
  if(!rcptState||!rcptState.items.length)return;
  var row=document.getElementById(rcptState.field+'-row');
  if(!row)return;
  var menu=document.createElement('div');
  menu.id='rcpt-menu';menu.className='rcpt-menu';
  rcptState.items.forEach(function(c,i){
    var item=document.createElement('div');
    item.className='rcpt-item'+(i===rcptState.activeIdx?' active':'');
    item.onmousedown=function(e){e.preventDefault();rcptInsert(rcptState.field,c.email);};
    item.onmouseenter=function(){rcptState.activeIdx=i;rcptRender();};
    var nm=document.createElement('div');nm.className='nm';nm.textContent=c.name||c.email;
    item.appendChild(nm);
    if(c.name){var em=document.createElement('div');em.className='em';em.textContent=c.email;item.appendChild(em);}
    menu.appendChild(item);
  });
  row.appendChild(menu);
}
function rcptInsert(field,email){
  var el=document.getElementById(field);
  var val=el.value||'';
  var token=rcptToken(val);
  var before=val.slice(0,val.length-token.length);
  el.value=before+email+'; ';
  rcptCloseMenu();
  el.focus();
  if(field==='to')upd();
}
function rcptInput(field){
  var el=document.getElementById(field);
  var token=rcptToken(el.value);
  var matches=token?rcptMatches(token):[];
  rcptState=matches.length?{field:field,items:matches,activeIdx:0}:null;
  rcptRender();
}
function rcptKeyDown(field,e){
  var el=document.getElementById(field);
  var val=el.value||'';
  if(e.key===' '){
    var token=rcptToken(val);
    if(EMAIL_LIKE_RE.test(token.trim())){
      e.preventDefault();
      var before=val.slice(0,val.length-token.length);
      el.value=before+token.trim()+'; ';
      rcptCloseMenu();
      if(field==='to')upd();
      return;
    }
  }
  if(rcptState&&rcptState.field===field&&rcptState.items.length){
    if(e.key==='ArrowDown'){e.preventDefault();rcptState.activeIdx=(rcptState.activeIdx+1)%rcptState.items.length;rcptRender();return;}
    if(e.key==='ArrowUp'){e.preventDefault();rcptState.activeIdx=(rcptState.activeIdx-1+rcptState.items.length)%rcptState.items.length;rcptRender();return;}
    if(e.key==='Enter'||e.key==='Tab'){e.preventDefault();rcptInsert(field,rcptState.items[rcptState.activeIdx].email);return;}
    if(e.key==='Escape'){rcptCloseMenu();return;}
  }
}
function rcptBlur(field){
  setTimeout(function(){if(rcptState&&rcptState.field===field)rcptCloseMenu();},150);
}

// Send
async function doSend(){
  var to=document.getElementById('to').value;
  var sub=document.getElementById('sub').value;
  var body=quill.root.innerHTML;
  var from=document.getElementById('frm').value;
  var cc=document.getElementById('cc')?.value||'';
  var bcc=document.getElementById('bcc')?.value||'';
  if(!to){notify('Please enter a recipient');return;}
  if(!sub){notify('Please enter a subject');return;}
  if(!body||body==='<p><br></p>'){notify('Please write a message');return;}
  document.getElementById('stxt').textContent='Sending...';
  notify('Sending email...');
  try{
    var tk=localStorage.getItem("sdr_token")||"";
    var attB64=await Promise.all((attachments||[]).map(function(f){return new Promise(function(resolve){var fr=new FileReader();fr.onload=function(){resolve({filename:f.name,contentType:f.type||"application/octet-stream",size:f.size,contentBase64:fr.result.split(",")[1]});};fr.readAsDataURL(f);});}));
    var r=await fetch("/api/email/send",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({from,to,cc,bcc,subject:sub,body,importance:imp,readReceiptRequested:rr,senderEmail:from,fromName:${JSON.stringify(settings.sdrName||"")},attachments:attB64})});
    var d=await r.json().catch(function(){return {};});
    if(!r.ok)throw new Error(d.error);
    document.getElementById('stxt').textContent='Sent successfully!';
    notify('Email sent successfully!');
    if(draftId){try{await fetch("/api/email/thread/"+draftId,{method:"DELETE",headers:{"Authorization":"Bearer "+tk}});}catch(delEx){}}
    // Tell the main Workspace tab the send is done (it refreshes its list
    // and force-closes this window as a fallback), then close ourselves —
    // whichever path actually takes effect, the window still goes away.
    try{if(window.opener&&!window.opener.closed)window.opener.postMessage({type:'sdr-popout-sent'},window.location.origin);}catch(pmEx){}
    setTimeout(function(){window.close();},900);
  }catch(e){
    document.getElementById('stxt').textContent='Failed: '+e.message;
    notify('Send failed: '+e.message);
  }
}

// Redundant, explicit click bindings for the ribbon tabs — belt-and-braces
// alongside the inline onclick attributes already on these buttons, in
// case anything ever interferes with inline handlers specifically.
['message','insert','options','format','review','help'].forEach(function(t){
  var b=document.getElementById('tab-'+t);
  if(b)b.addEventListener('click',function(){showTab(t);});
});

// Initialize now, directly — not via the CDN script's onload attribute.
// The Quill <script src> tag above is a classic blocking script, so by the
// time the browser parses and runs this inline script (which comes after
// it in the document), Quill is already loaded. Calling initApp() here
// removes any dependency on load-event timing.
initApp();
</script></body></html>`);
    w.document.close();
  };

  const doReply=(msg,mode)=>{
    setCompose(p=>({
      ...p,
      from:msg.accountEmail||senders[0]?.email||settings.senderEmail||"",
      to:mode==="forward"?"":(msg.direction==="out"?msg.to:msg.from)||"",
      cc:mode==="replyAll"?msg.cc||"":"",
      subject:mode==="forward"?"Fwd: "+(msg.subject||""):(msg.subject||"").startsWith("Re:")?(msg.subject||""):"Re: "+(msg.subject||""),
      body:"",threadId:msg.threadId||selectedItem?.id,inReplyTo:msg.id,attachments:[],
      importance:"normal",readReceiptRequested:false,id:null,
    }));
    setComposing(true);setReplyMode(mode);
  };

  // Outlook-style To/Cc/Bcc autocomplete: as you type, the last "token"
  // (everything since the previous ; or , separator) is matched against
  // your contacts by name or email; picking a match — or just finishing a
  // full address yourself and pressing space — inserts "<email>; " and
  // leaves the field ready for the next recipient, same as Outlook desktop.
  // The backend already accepts either ; or , (see sendEmailCore's
  // normalizeRecipients), so either separator these produce sends fine.
  const getRecipientToken=(val)=>{
    const parts=String(val||"").split(/[,;]/);
    return parts[parts.length-1].replace(/^\s+/,"");
  };
  const EMAIL_LIKE_RE=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const searchRecipientMatches=(token)=>{
    const q=token.trim().toLowerCase();
    if(!q)return [];
    return leads.filter(l=>l.email&&((l.name||"").toLowerCase().includes(q)||l.email.toLowerCase().includes(q))).slice(0,6);
  };
  const insertRecipient=(field,email)=>{
    setCompose(p=>{
      const cur=p[field]||"";
      const token=getRecipientToken(cur);
      const before=cur.slice(0,cur.length-token.length);
      return {...p,[field]:before+email+"; "};
    });
    setRcptSuggest(null);
  };
  const handleRecipientChange=(field,val)=>{
    setCompose(p=>({...p,[field]:val}));
    const token=getRecipientToken(val);
    const matches=token?searchRecipientMatches(token):[];
    setRcptSuggest(matches.length?{field,items:matches,activeIdx:0}:prev=>(prev&&prev.field===field?null:prev));
  };
  const handleRecipientKeyDown=(field,e)=>{
    const val=compose[field]||"";
    if(e.key===" "){
      const token=getRecipientToken(val);
      if(EMAIL_LIKE_RE.test(token.trim())){
        e.preventDefault();
        const before=val.slice(0,val.length-token.length);
        setCompose(p=>({...p,[field]:before+token.trim()+"; "}));
        setRcptSuggest(null);
        return;
      }
    }
    if(rcptSuggest&&rcptSuggest.field===field&&rcptSuggest.items.length){
      if(e.key==="ArrowDown"){e.preventDefault();setRcptSuggest(s=>({...s,activeIdx:(s.activeIdx+1)%s.items.length}));return;}
      if(e.key==="ArrowUp"){e.preventDefault();setRcptSuggest(s=>({...s,activeIdx:(s.activeIdx-1+s.items.length)%s.items.length}));return;}
      if(e.key==="Enter"||e.key==="Tab"){
        e.preventDefault();
        insertRecipient(field,rcptSuggest.items[rcptSuggest.activeIdx].email);
        return;
      }
      if(e.key==="Escape"){setRcptSuggest(null);return;}
    }
  };

  const sendEmail=async()=>{
    if(!compose.to||!compose.subject||!compose.body){addToast("Fill in To, Subject and Body","error");return;}
    setSending(true);
    try{
      const tk=localStorage.getItem("sdr_token")||"";
      const r=await fetch("/api/email/send",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({...compose,senderEmail:compose.from||"",fromName:settings.sdrName})});
      const d=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(d.error);
      addToast("Email sent","success");
      // Sending a resumed draft should clear it out of Drafts, the way Outlook does.
      if(compose.id){try{await fetch("/api/email/thread/"+compose.id,{method:"DELETE",headers:{"Authorization":"Bearer "+tk}});}catch(delErr){}}
      setComposing(false);await loadFolderItems();await refreshUnreadCounts();
    }catch(e){addToast("Send failed: "+e.message,"error");}
    setSending(false);
  };

  // Real "send later" — creates a ScheduledEmail row on the server; the
  // scheduled-send poller there actually sends it once sendAt has passed.
  const scheduleSend=async(when)=>{
    if(!compose.to||!compose.subject||!compose.body){addToast("Fill in To, Subject and Body","error");return;}
    if(!when){addToast("Pick a date and time first","error");return;}
    const sendAtDate=new Date(when);
    if(isNaN(sendAtDate.getTime())||sendAtDate.getTime()<=Date.now()){addToast("Pick a time in the future","error");return;}
    setScheduling(true);
    try{
      const tk=localStorage.getItem("sdr_token")||"";
      const r=await fetch("/api/email/schedule",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({...compose,senderEmail:compose.from||"",fromName:settings.sdrName,sendAt:sendAtDate.toISOString(),source:"compose"})});
      const d=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(d.error);
      addToast("Scheduled for "+sendAtDate.toLocaleString(),"success");
      setShowScheduleMenu(false);setScheduleAt("");setComposing(false);
    }catch(e){addToast("Couldn't schedule: "+e.message,"error");}
    setScheduling(false);
  };

  // Real draft persistence — POSTs to the same /api/email/draft endpoint
  // the folder list already reads "drafts" from, so a saved draft actually
  // shows up in the Drafts folder rather than just claiming success.
  // Real draft persistence. Passing compose.id (set once a draft has been
  // saved or resumed) makes this an update instead of creating a new draft
  // row every time — without it, re-saving the same draft would silently
  // pile up duplicate rows in the Drafts folder.
  const saveDraftReal=async(silent)=>{
    if(!compose.to&&!compose.subject&&!compose.body){if(!silent)addToast("Nothing to save yet","info");return;}
    try{
      const tk=localStorage.getItem("sdr_token")||"";
      const r=await fetch("/api/email/draft",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({...compose,senderEmail:compose.from||""})});
      const d=await r.json().catch(()=>({}));
      if(!r.ok)throw new Error(d.error);
      if(d.id&&d.id!==compose.id)setCompose(p=>({...p,id:d.id}));
      if(!silent)addToast("Draft saved","success");
      if(activeFolder==="drafts")await loadFolderItems();
    }catch(e){if(!silent)addToast("Couldn't save draft: "+e.message,"error");}
  };

  // Resume an existing draft for editing — Outlook opens a saved draft
  // straight into the compose editor instead of a read-only reading pane.
  const resumeDraft=(thread)=>{
    const msg=thread?.messages?.[0];
    if(!msg)return;
    setCompose({
      id:msg.id, from:msg.accountEmail||senders[0]?.email||settings.senderEmail||"",
      to:msg.to||"",cc:msg.cc||"",bcc:msg.bcc||"",subject:msg.subject||"",body:msg.body||"",
      importance:msg.importance||"normal",readReceiptRequested:!!msg.readReceiptRequested,
      threadId:msg.threadId||thread.id,inReplyTo:msg.inReplyTo||null,
      attachments:(msg.attachments||[]).map(a=>({filename:a.filename,contentType:a.contentType,size:a.size})),
    });
    setComposing(true);setReplyMode(null);setSelectedItem(null);
  };

  // Real periodic autosave while a message is being composed — saves
  // silently every 20s so work isn't lost, the same way Outlook autosaves
  // to Drafts. Skips the popup compose window (separate DOM, not this state).
  useEffect(()=>{
    if(!composing)return;
    const iv=setInterval(()=>{ if(compose.to||compose.subject||compose.body) saveDraftReal(true); },20000);
    return ()=>clearInterval(iv);
  },[composing,compose.to,compose.subject,compose.body,compose.id]);

  // Writing Voice Guard check — piggybacks on the same 20s cadence as
  // autosave above (no need for a tighter loop; this is a background hint,
  // not a live-as-you-type validator). Cheap heuristic-only call, no AI
  // involved, so this never depends on Anthropic API availability/credits.
  // Reads compose.from/compose.body via a ref (kept fresh below) rather than
  // as effect dependencies, so the interval doesn't reset on every keystroke
  // — a dependency-array version would either never wait the full 20s while
  // someone's actively typing, or (if body were left out of the deps) read
  // a stale, frozen-at-mount draft on every tick.
  const composeLiveRef=useRef(compose);
  useEffect(()=>{composeLiveRef.current=compose;},[compose]);
  useEffect(()=>{
    if(!composing)return;
    const check=async()=>{
      const c=composeLiveRef.current;
      if(!c.from)return;
      const wordCount=(c.body||"").replace(/<[^>]+>/g," ").trim().split(/\s+/).filter(Boolean).length;
      if(wordCount<15)return;
      try{
        const tk=localStorage.getItem("sdr_token")||"";
        const r=await fetch("/api/writing-voice/check",{method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+tk},body:JSON.stringify({accountEmail:c.from,body:c.body})});
        const d=await r.json();
        setVoiceGuardFlags(d.flags||[]);
      }catch(e){}
    };
    check();
    const iv=setInterval(check,20000);
    return ()=>clearInterval(iv);
  },[composing]);
  const voiceGuardKey=voiceGuardFlags.join("|");
  const showVoiceGuard=voiceGuardFlags.length>0&&voiceGuardDismissed!==voiceGuardKey;

  // Quick Steps — real, just configurable: the target address for "To
  // Manager"/"Team Email" is asked once and remembered per-browser (there's
  // no per-org "manager"/"team distro" concept in the data model yet), then
  // reused on every future click without asking again.
  const quickStepCompose=(kind)=>{
    const key="sdr_quickstep_"+kind;
    let addr=localStorage.getItem(key);
    if(!addr){
      addr=(window.prompt(`Email address to use for "${kind==="manager"?"To Manager":"Team Email"}" quick step:`)||"").trim();
      if(!addr){addToast("No address set — quick step canceled","info");return;}
      localStorage.setItem(key,addr);
    }
    setCompose(p=>({...p,id:null,from:senders[0]?.email||settings.senderEmail||"",to:addr,cc:"",bcc:"",subject:"",body:"",threadId:null,inReplyTo:null,attachments:[]}));
    setComposing(true);setReplyMode(null);setSelectedItem(null);
    addToast("Composing to "+addr+" (change anytime via Settings > this quick step will re-ask if you clear it)","info");
  };

  const aiDraftPane=async()=>{
    const lead=leads.find(l=>l.email===compose.to)||leads[0];
    if(!lead){addToast("Add a recipient first","error");return;}
    setAiLoading(true);
    try{
      const r=await fetch("/api/ai/sequence-step",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({lead,settings,stepLabel:compose.inReplyTo?"Follow-up":"Fresh outreach",stepDay:"Now",stepDesc:"Email",prevEmails:"",isFollowUp:!!compose.inReplyTo})});
      const d=await r.json();
      setCompose(p=>({...p,subject:d.subject||p.subject,body:d.body||""}));
      addToast("AI draft ready","success");
    }catch(e){addToast("AI error","error");}
    setAiLoading(false);
  };

  const MAX_ATTACHMENT_BYTES=8*1024*1024;
  const handleAttachFiles=(fileList)=>{
    const files=Array.from(fileList||[]);
    files.forEach(f=>{
      if(f.size>MAX_ATTACHMENT_BYTES){addToast(f.name+" is too large (max 8MB)","error");return;}
      const reader=new FileReader();
      reader.onload=()=>{
        const b64=reader.result.split(",")[1];
        setCompose(p=>({...p,attachments:[...(p.attachments||[]),{filename:f.name,contentType:f.type||"application/octet-stream",size:f.size,contentBase64:b64}]}));
      };
      reader.readAsDataURL(f);
    });
  };
  const removeAttachment=(idx)=>setCompose(p=>({...p,attachments:(p.attachments||[]).filter((_,i)=>i!==idx)}));

  const insertMergeTag=(tag)=>{
    if(lastFocRef.current==="subject"){
      const inp=subjectRef.current;
      if(inp){const s=inp.selectionStart||0,e=inp.selectionEnd||0;const cur=compose.subject||"";setCompose(p=>({...p,subject:cur.slice(0,s)+tag+cur.slice(e)}));setTimeout(()=>{inp.focus();inp.setSelectionRange(s+tag.length,s+tag.length);},10);}
    }else{
      const q=quillRef.current?.getEditor?.();
      if(q){const rng=q.getSelection(true);const idx=rng?rng.index:q.getLength();q.insertText(idx,tag);q.setSelection(idx+tag.length);}
    }
  };

  // P3 #19 — these three close the small remaining feature gap between the
  // in-pane composer and the "Pop Out" window's Insert/Review tabs (Insert
  // Table, Insert Date & Time, Word Count), which otherwise only existed in
  // the pop-out. Purely additive: reuse the same quillRef the rest of the
  // in-pane compose toolbar already drives, so nothing existing changes.
  const insertDateTimeInPane=()=>{
    const q=quillRef.current?.getEditor?.();
    if(!q){addToast("Click into the email body first","error");return;}
    const idx=q.getSelection(true)?.index??q.getLength();
    q.insertText(idx,new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"}));
    addToast("Date inserted","success");
  };
  const insertTableInPane=()=>{
    const q=quillRef.current?.getEditor?.();
    if(!q){addToast("Click into the email body first","error");return;}
    const rowsStr=window.prompt("Number of rows:","3");
    if(rowsStr===null)return;
    const colsStr=window.prompt("Number of columns:","3");
    if(colsStr===null)return;
    const rows=parseInt(rowsStr)||3,cols=parseInt(colsStr)||3;
    let html='<table border="1" style="border-collapse:collapse;width:100%">';
    for(let r=0;r<rows;r++){html+="<tr>";for(let c=0;c<cols;c++){html+='<td style="padding:6px 10px;border:1px solid #ccc">'+(r===0?"<strong>Header "+(c+1)+"</strong>":"Cell")+"</td>";}html+="</tr>";}
    html+="</table><p><br></p>";
    const idx=q.getSelection(true)?.index??q.getLength();
    q.clipboard.dangerouslyPasteHTML(idx,html);
    addToast("Table inserted ("+rows+"×"+cols+")","success");
  };
  const showWordCountPane=()=>{
    const q=quillRef.current?.getEditor?.();
    const text=(q?q.getText():"").trim();
    const words=text?text.split(/\s+/).length:0;
    window.alert("Word Count\n\nWords: "+words+"\nCharacters (no spaces): "+text.replace(/\s/g,"").length+"\nCharacters (with spaces): "+text.length+"\nParagraphs: "+(text?text.split(/\n\n+/).length:0)+"\nLines: "+(text?text.split("\n").length:0));
  };

  const updateLeadStatus=async(id,status)=>{
    try{
      await fetch("/api/leads/"+id,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({leadStatus:status})});
      setLeads(p=>p.map(l=>l.id===id?{...l,leadStatus:status}:l));
      if(selectedItem?.id===id)setSelectedItem(p=>({...p,leadStatus:status}));
      addToast("Status: "+status,"success");
    }catch(e){}
  };

  const addNote=async(id)=>{
    if(!note.trim())return;
    const n={id:Date.now(),text:note,createdAt:new Date().toISOString(),author:settings.sdrName||"Anji Reddy"};
    const lead=leads.find(l=>l.id===id);
    const notes=[...(lead?.notes||[]),n];
    try{
      await fetch("/api/leads/"+id,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({notes})});
      setLeads(p=>p.map(l=>l.id===id?{...l,notes}:l));
      if(selectedItem?.id===id)setSelectedItem(p=>({...p,notes}));
      setNote("");addToast("Note added","success");
    }catch(e){}
  };

  // Workspace loads its own `leads` copy once on mount (loadAll below), and
  // ContactsPage.js independently loads/edits its own — so a status change,
  // note, or field edit made on one side can go stale on the other while
  // both stay open. The contact-context panel opens mid-session next to
  // whatever conversation the user happens to be reading, which is exactly
  // when that staleness is most likely to have crept in, so refresh just
  // that one contact's record (GET /api/leads/:id already exists) rather
  // than re-fetching or polling the whole list.
  const refreshContact=async(id)=>{
    try{
      const res=await fetch("/api/leads/"+id);
      if(!res.ok)return;
      const fresh=await res.json();
      if(!fresh||fresh.error)return;
      setLeads(p=>p.map(l=>l.id===id?{...l,...fresh,notes:fresh.notes||l.notes}:l));
    }catch(e){}
  };

  const createOpp=()=>{
    if(!newOpp.name){addToast("Opportunity name required","error");return;}
    const opp={id:Date.now(),...newOpp,createdAt:new Date().toISOString()};
    setOpps(p=>[...p,opp]);
    setNewOpp({name:"",company:"",value:"",stage:"Discovery",probability:""});
    setShowNewOpp(false);
    addToast("Opportunity created","success");
  };

  const toggleExpand=(section)=>setExpanded(p=>({...p,[section]:!p[section]}));

  const getListItems=()=>{
    const q=search.toLowerCase();
    if(activeFolder==="leads")return leads.filter(l=>(filterStatus==="all"||(l.leadStatus||"New")===filterStatus)&&((l.name||"").toLowerCase().includes(q)||(l.email||"").toLowerCase().includes(q)||(l.company||"").toLowerCase().includes(q)));
    // Contacts is the full directory (same underlying records as Leads,
    // deliberately not pipeline-staged the way Leads is — this mirrors the
    // Leads/Contacts split in the Salesforce Lightning CRM this app's
    // palette is modeled on), so it never applies filterStatus. Its search
    // predicate now matches company too — previously it only matched
    // name/email while Leads' otherwise-identical predicate also matched
    // company, so the same query returned different results depending on
    // which of the two views you searched from.
    if(activeFolder==="contacts")return leads.filter(l=>(l.name||"").toLowerCase().includes(q)||(l.email||"").toLowerCase().includes(q)||(l.company||"").toLowerCase().includes(q));
    if(activeFolder==="campaigns")return campaigns.filter(c=>(c.name||"").toLowerCase().includes(q));
    if(activeFolder==="sequences")return sequences.filter(s=>(s.name||"").toLowerCase().includes(q));
    const isCustomFolder=(customFolders[compose.from]||[]).some(f=>f.id===activeFolder);
    if(FOLDER_API_MAP[activeFolder]||isCustomFolder){
      // Cross-folder search (Subfolders / Current Mailbox / All Mailboxes /
      // All Outlook Items scopes) — the Search contextual tab's Scope group
      // below sets searchScope and the effect above fetches+merges every
      // folder that scope covers into crossFolderResults. "Current Folder"
      // scope deliberately skips this and falls through to the plain
      // per-folder filter beneath, exactly as it always worked.
      if(searchActive&&search.trim()&&searchScope!=="currentFolder"){
        if(!crossFolderResults)return []; // still loading (or fetch hasn't started yet)
        let items=crossFolderResults.filter(t=>(t.subject||"").toLowerCase().includes(q)||(t.to||"").toLowerCase().includes(q)||(t.from||"").toLowerCase().includes(q));
        if(searchRefine.unread)items=items.filter(t=>!t.isRead);
        if(searchRefine.flagged)items=items.filter(t=>t.isFlagged);
        if(searchRefine.important)items=items.filter(t=>(t.messages&&t.messages[0]&&t.messages[0].importance)==="high");
        return items;
      }
      const items=folderItems.filter(t=>(mailFilter!=="Unread"||!t.isRead)&&((t.subject||"").toLowerCase().includes(q)||(t.to||"").toLowerCase().includes(q)||(t.from||"").toLowerCase().includes(q)));
      const dir=sortDir==="asc"?1:-1;
      const sorted=items.slice().sort((a,b)=>{
        let r=0;
        if(sortBy==="sender")r=(a.from||"").localeCompare(b.from||"");
        else if(sortBy==="subject")r=(a.subject||"").localeCompare(b.subject||"");
        else r=new Date(a.latestAt||a.sentAt||a.receivedAt||0)-new Date(b.latestAt||b.sentAt||b.receivedAt||0);
        return r*dir;
      });
      return sorted;
    }
    if(activeFolder==="outbox")return []; // no queued-send concept yet; Resend sends synchronously
    if(activeFolder==="senderaccounts")return senders.filter(s=>(s.email||"").toLowerCase().includes(q));
    if(activeFolder==="templates")return templates.filter(t=>(t.name||"").toLowerCase().includes(q)||(t.subject||"").toLowerCase().includes(q));
    if(activeFolder==="contactlists")return contactFiles.filter(f=>(f.name||"").toLowerCase().includes(q));
    if(activeFolder==="opportunities")return opps.filter(o=>(o.name||"").toLowerCase().includes(q)||(o.company||"").toLowerCase().includes(q));
    return [];
  };

  const isLead=activeFolder==="leads"||activeFolder==="contacts";
  const isCamp=activeFolder==="campaigns";
  const isSeq=activeFolder==="sequences";
  const isOpp=activeFolder==="opportunities";
  const isMail=!isLead&&!isCamp&&!isSeq&&!isOpp&&activeFolder!=="contactlists"&&activeFolder!=="team"&&activeFolder!=="senderaccounts"&&activeFolder!=="templates";
  const listItems=getListItems();

  // The contact behind whatever conversation is currently open in the
  // reading pane, matched by email against the same `leads` list the
  // Leads/Contacts views already use (same match convention as
  // `leads.find(l=>l.email===compose.to)` elsewhere in this file). Powers
  // the collapsible contact-context panel beside the conversation — null
  // whenever nothing's open, the open item isn't mail, or the sender/
  // recipient address doesn't match any saved contact.
  const mailContact=useMemo(()=>{
    if(!isMail||!selectedItem)return null;
    const raw=(selectedItem.to||"").split(",")[0].trim().toLowerCase();
    if(!raw)return null;
    return leads.find(l=>(l.email||"").trim().toLowerCase()===raw)||null;
  },[isMail,selectedItem,leads]);
  const mailContactId=mailContact?.id;
  useEffect(()=>{ if(mailContactId)refreshContact(mailContactId); },[mailContactId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every branch below corresponds 1:1 to an entry in keyboardShortcuts.js's
  // WORKSPACE_SHORTCUTS (scope:"main") — named to match that list's labels,
  // so the registry file and this dispatch stay easy to cross-check as
  // shortcuts are added. The guard order/logic itself is unchanged from
  // before this refactor (still one sequential if-chain with early
  // returns) — only the inline blocks were pulled into named functions;
  // nothing about when/whether a shortcut fires was altered.
  useEffect(()=>{
    const selectAllMessages=()=>setMultiSel(listItems.map(t=>t.messages?.[0]?.id).filter(Boolean));
    const sendComposedEmail=()=>sendEmail();
    const discardCompose=()=>setComposing(false);
    const saveCurrentDraft=()=>saveDraftReal();
    const focusSearchBox=()=>searchInputRef.current?.focus();
    const triggerSendReceive=()=>{if(effectiveOffline){addToast("Cannot sync while offline","error");return;}syncNow();};
    const newEmail=()=>openInPane();
    const deleteSelectedMessage=()=>deleteEmail(selectedItem,selectedItem.messages[0]);
    const replySelected=()=>doReply(selectedItem.messages[0],"reply");
    const replyAllSelected=()=>doReply(selectedItem.messages[0],"replyAll");
    const forwardSelected=()=>doReply(selectedItem.messages[0],"forward");
    const moveListSelection=(key)=>{
      const idx=selectedItem?listItems.findIndex(t=>t.id===selectedItem.id):-1;
      const nextIdx=key==="arrowdown"?(idx<0?0:Math.min(idx+1,listItems.length-1)):(idx<0?0:Math.max(idx-1,0));
      setSelectedItem(listItems[nextIdx]);
    };

    const onKeyDown=(e)=>{
      const tag=(document.activeElement?.tagName||"").toLowerCase();
      const isEditable=tag==="input"||tag==="textarea"||tag==="select"||document.activeElement?.isContentEditable;
      const mod=e.ctrlKey||e.metaKey;
      const key=e.key.toLowerCase();

      // Ctrl/Cmd+A - Select All
      if(mod&&!e.shiftKey&&key==="a"){
        if(isEditable||!isMail||mainView!=="mail")return; // let normal select-all-text behavior happen everywhere else
        e.preventDefault();
        selectAllMessages();
        return;
      }

      // Ctrl/Cmd+Enter - Send
      if(mod&&key==="enter"){
        if(!composing)return;
        e.preventDefault();
        sendComposedEmail();
        return;
      }

      // Escape - Discard Compose
      if(key==="escape"){
        if(!composing)return;
        e.preventDefault();
        discardCompose();
        return;
      }

      // Ctrl/Cmd+S - Save Draft (main window). Deliberately checked before
      // the isEditable return below — like Outlook, this should work while
      // typing in the subject/body fields, since that's exactly when
      // you'd want to save.
      if(mod&&!e.shiftKey&&key==="s"){
        if(!composing)return;
        e.preventDefault();
        saveCurrentDraft();
        return;
      }

      // Ctrl/Cmd+E - Search. Also checked before isEditable so it can pull
      // focus OUT of whatever field you're in and into the search box,
      // matching Outlook's "jump to Instant Search" behavior.
      if(mod&&!e.shiftKey&&key==="e"){
        if(mainView!=="mail")return;
        e.preventDefault();
        focusSearchBox();
        return;
      }

      // F9 - Send/Receive All. Not a text-entry key, so safe to handle
      // globally regardless of focus, same as Outlook.
      if(key==="f9"){
        if(mainView!=="mail")return;
        e.preventDefault();
        triggerSendReceive();
        return;
      }

      // Ctrl/Cmd+1/2/3/4 - switch between Mail/Calendar/People/Tasks. Digit
      // combos with a modifier held, same as F9 above — safe to handle
      // globally rather than gating on isEditable.
      if(mod&&!e.shiftKey&&(key==="1"||key==="2"||key==="3"||key==="4")){
        e.preventDefault();
        if(key==="1"){setMainView("mail");setActiveFolder("inbox");}
        else if(key==="2")setMainView("calendar");
        else if(key==="3"){localStorage.setItem("sdr_page","contacts");window.location.href="/";}
        else if(key==="4")setMainView("tasks");
        return;
      }

      if(isEditable)return; // everything below only applies when not typing in a field

      // Ctrl/Cmd+N - New Email, as its own Pop Out window (not the inline
      // compose pane) — openInWindow() always calls window.open() fresh,
      // so pressing this repeatedly opens one new blank compose window per
      // press, same as Outlook desktop. Kept even though it can't actually
      // fire in most browsers (see Alt+N below) in case it's ever running
      // somewhere that does pass the combo through — harmless either way.
      if(mod&&!e.shiftKey&&key==="n"){
        if(mainView!=="mail")return;
        e.preventDefault();
        openInWindow();
        return;
      }

      // Alt+N - same "new Pop Out compose window" action as Ctrl+N above,
      // but this is the one that actually works: Chrome/Edge/Firefox all
      // reserve Ctrl+N at the browser-chrome level for "open a new browser
      // window" and never let a page's preventDefault() override it, so
      // Ctrl+N alone opens a real new browser window instead of reaching
      // this handler. Alt+letter combos aren't reserved the same way, so
      // Alt+N is the one to actually use/teach for this.
      if(e.altKey&&!mod&&!e.shiftKey&&key==="n"){
        if(mainView!=="mail")return;
        e.preventDefault();
        openInWindow();
        return;
      }

      // Ctrl/Cmd+Shift+M - New Message (Outlook binds both Ctrl+N and
      // Ctrl+Shift+M to the same "new email" action)
      if(mod&&e.shiftKey&&key==="m"){
        if(mainView!=="mail")return;
        e.preventDefault();
        newEmail();
        return;
      }

      // Ctrl/Cmd+Shift+A - New Appointment. Global creation shortcut (like
      // the New Items ribbon entries it mirrors), so it works from any
      // mainView rather than being gated to Mail like the block below.
      if(mod&&e.shiftKey&&key==="a"){
        e.preventDefault();
        openNewMeetingNow();
        return;
      }

      // Ctrl/Cmd+Shift+K - New Task. Same global-shortcut reasoning as above.
      if(mod&&e.shiftKey&&key==="k"){
        e.preventDefault();
        openNewTaskNow();
        return;
      }

      if(!isMail||mainView!=="mail"||composing)return;

      // Ctrl/Cmd+Q - Mark as Read
      if(mod&&!e.shiftKey&&key==="q"&&(selectedItem?.messages?.[0]||multiSel.length)){
        e.preventDefault();
        markSelectionRead(true);
        return;
      }

      // Ctrl/Cmd+U - Mark as Unread
      if(mod&&!e.shiftKey&&key==="u"&&(selectedItem?.messages?.[0]||multiSel.length)){
        e.preventDefault();
        markSelectionRead(false);
        return;
      }

      // Ctrl/Cmd+Shift+V - Move to folder picker
      if(mod&&e.shiftKey&&key==="v"&&selectedItem?.messages?.[0]){
        e.preventDefault();
        setShowMoveMenu(true);
        return;
      }

      // Delete/Backspace - Delete
      if((key==="delete"||key==="backspace")&&selectedItem?.messages?.[0]){
        e.preventDefault();
        deleteSelectedMessage();
        return;
      }

      // Ctrl/Cmd+R - Reply
      if(mod&&!e.shiftKey&&key==="r"&&selectedItem?.messages?.[0]){
        e.preventDefault();
        replySelected();
        return;
      }

      // Ctrl/Cmd+Shift+R - Reply All
      if(mod&&e.shiftKey&&key==="r"&&selectedItem?.messages?.[0]){
        e.preventDefault();
        replyAllSelected();
        return;
      }

      // Ctrl/Cmd+F - Forward (Ctrl+F is Outlook's forward shortcut in the reading pane)
      if(mod&&!e.shiftKey&&key==="f"&&selectedItem?.messages?.[0]){
        e.preventDefault();
        forwardSelected();
        return;
      }

      // Arrow Up/Down - Move Selection
      if((key==="arrowdown"||key==="arrowup")&&listItems.length){
        e.preventDefault();
        moveListSelection(key);
        return;
      }
    };
    window.addEventListener("keydown",onKeyDown);
    return ()=>window.removeEventListener("keydown",onKeyDown);
  },[listItems,isMail,mainView,composing,selectedItem,multiSel]);

  const sig="<p style='margin-top:20px;padding-top:8px;border-top:1px solid #d1cec9;font-size:12px;color:#605e5c'><strong>"+(settings.sdrName||"Anji Reddy")+"</strong><br/>"+(settings.company||"SDR ReachOut")+"<br/>"+(compose.from||"")+"</p>";

  // Runtime fn/disabled for every QAT_COMMANDS entry — rebuilt each render
  // (cheap) so it always reflects current selection/composing/offline state,
  // same as how the ribbon buttons below compute their own `disabled` props.
  const hasSelectedMsg=!!selectedItem?.messages?.[0];
  const qatActions={
    newEmail:{fn:openInPane,disabled:mainView!=="mail"},
    reply:{fn:()=>doReply(selectedItem.messages[0],"reply"),disabled:!hasSelectedMsg},
    replyAll:{fn:()=>doReply(selectedItem.messages[0],"replyAll"),disabled:!hasSelectedMsg},
    forward:{fn:()=>doReply(selectedItem.messages[0],"forward"),disabled:!hasSelectedMsg},
    delete:{fn:()=>deleteEmail(selectedItem,selectedItem.messages[0]),disabled:!hasSelectedMsg},
    archive:{fn:()=>moveEmailTo(selectedItem,selectedItem.messages[0],"archive"),disabled:!hasSelectedMsg},
    flag:{fn:()=>toggleFlagFor(selectedItem.messages[0]),disabled:!hasSelectedMsg},
    markRead:{fn:()=>markSelectionRead(true),disabled:!(hasSelectedMsg||multiSel.length)},
    sync:{fn:syncNow,disabled:effectiveOffline},
    save:{fn:()=>saveDraftReal(),disabled:!composing},
    print:{fn:()=>printEmail(selectedItem.messages[0],selectedItem.subject),disabled:!hasSelectedMsg},
  };

  return(
    <div className="ow-workspace-root" style={{display:"flex",flexDirection:"column",height:"100vh",width:"100vw",background:C.ribbon,overflow:"hidden",fontFamily:"'Segoe UI',Calibri,sans-serif",fontSize:13}}>
      <style>{"@keyframes owspin{to{transform:rotate(360deg)}}.ow-item:hover{background:#f8f7f6!important}.ql-container.ql-snow{background:#fff!important;border-color:#d1cec9!important;color:#201f1e;min-height:150px}.ql-editor{color:#201f1e!important;font-family:Calibri,sans-serif;font-size:14px;min-height:150px;line-height:1.6}.ql-editor.ql-blank::before{color:#a19f9d;font-style:normal}"+
        /* Accessibility pass (Workspace UX audit item 8): visible keyboard
           focus rings app-wide. :focus-visible only fires for keyboard/
           programmatic focus, not mouse clicks, so this changes nothing
           for mouse users and adds no visual change to any existing
           click-driven interaction — purely additive for keyboard-only
           and screen-reader users. */
        ".ow-workspace-root button:focus-visible,.ow-workspace-root [tabindex]:focus-visible,.ow-workspace-root a:focus-visible,.ow-workspace-root input:focus-visible,.ow-workspace-root select:focus-visible,.ow-workspace-root textarea:focus-visible{outline:2px solid #0078d4!important;outline-offset:1px!important;border-radius:2px}"
      }</style>

      {mainView==="calendar"&&(
        <div style={{position:"fixed",inset:0,zIndex:500,background:"#fff",display:"flex",flexDirection:"column"}}>
          <CalendarView C={C} onBack={()=>setMainView("mail")} meetings={meetings} setMeetings={setMeetings}
            calViewMode={calViewMode} setCalViewMode={setCalViewMode} calDate={calDate} setCalDate={setCalDate}
            showMeetingModal={showMeetingModal} setShowMeetingModal={setShowMeetingModal}
            editingMeeting={editingMeeting} setEditingMeeting={setEditingMeeting} addToast={addToast}/>
        </div>
      )}

      {mainView==="tasks"&&(
        <div style={{position:"fixed",inset:0,zIndex:500,background:"#fff",display:"flex",flexDirection:"column"}}>
          <TasksView C={C} onBack={()=>setMainView("mail")} tasks={tasksList} setTasks={setTasksList}
            taskFilter={taskFilter} setTaskFilter={setTaskFilter}
            showTaskModal={showTaskModal} setShowTaskModal={setShowTaskModal}
            editingTask={editingTask} setEditingTask={setEditingTask} addToast={addToast}/>
        </div>
      )}

      {/*  New Email popup  */}
      {showNewEmailPopup&&(
        <ContextMenu open={showNewEmailPopup} onClose={()=>setShowNewEmailPopup(false)} x={popupPos.x} y={popupPos.y} width={240}>
          <div style={{padding:"4px 0"}}>
            {[
              {icon:"layoutRight",title:"Open in Reading Pane",sub:"Compose within workspace",action:openInPane},
              {icon:"popOut",title:"Open in New Window",sub:"Full Outlook-style compose window",action:openInWindow},
            ].map((opt,i)=>(
              <button key={i} onClick={opt.action}
                style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"10px 16px",border:"none",background:"transparent",cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",textAlign:"left"}}
                onMouseEnter={e=>e.currentTarget.style.background="#f3f2f1"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <span style={{width:32,height:32,borderRadius:6,background:C.accentL,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <RIcon name={opt.icon} size={16} color={C.accent}/>
                </span>
                <div>
                  <p style={{fontSize:13,fontWeight:500,color:C.text,marginBottom:2}}>{opt.title}</p>
                  <p style={{fontSize:11,color:C.textSub}}>{opt.sub}</p>
                </div>
              </button>
            ))}
          </div>
        </ContextMenu>
      )}

      {/*  Offline Banner  */}
      {effectiveOffline&&(
        <div style={{background:"#a4262c",padding:"5px 16px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:"#fff",flexShrink:0}}/>
          <span style={{fontSize:12,color:"#fff",fontWeight:600,fontFamily:"'Segoe UI',sans-serif",flex:1}}>
             Working Offline  {queue.length} action{queue.length!==1?"s":""} queued  Changes saved locally
          </span>
          <button onClick={()=>setShowQueue(!showQueue)}
            style={{background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",color:"#fff",fontSize:11,padding:"2px 10px",borderRadius:3,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}>
            View Queue
          </button>
          <button onClick={toggleOffline}
            style={{background:"#fff",border:"none",color:"#a4262c",fontSize:11,padding:"2px 10px",borderRadius:3,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif",fontWeight:600}}>
            Go Online
          </button>
        </div>
      )}

      {/*  Title Bar  */}
      <div style={{background:effectiveOffline?"#8a8886":C.nav,padding:"0",display:"flex",flexDirection:"column",flexShrink:0,userSelect:"none",transition:"background .3s"}}>
        {/* Top row */}
        <div style={{padding:"4px 12px 4px 12px",display:"flex",alignItems:"center",gap:10,minHeight:34}}>
          {/* Brand */}
          <div style={{display:"flex",alignItems:"center",gap:7,paddingRight:8,marginRight:2,borderRight:"1px solid rgba(255,255,255,.25)"}}>
            <RIcon name="mail" size={16} color={C.navText}/>
            <span style={{fontSize:13,fontWeight:600,color:C.navText,letterSpacing:".2px",whiteSpace:"nowrap"}}>SDR ReachOut</span>
          </div>
          {/* Quick Access Toolbar (P3 #18) — real customization: which
              commands show and in what order, persisted to localStorage.
              Every command now has a real onClick (Reply/Forward/Save
              previously rendered but did nothing when clicked). */}
          <div style={{display:"flex",alignItems:"center",gap:2,position:"relative"}}>
            {qatCommandIds.map(id=>{
              const cmd=QAT_COMMANDS.find(c=>c.id===id);
              if(!cmd)return null;
              const action=qatActions[id]||{};
              return(
                <button key={id} title={cmd.label} aria-label={cmd.label} disabled={action.disabled} onClick={action.fn}
                  style={{background:"rgba(255,255,255,.12)",border:"none",borderRadius:3,padding:"4px 6px",cursor:action.disabled?"default":"pointer",color:C.navText,display:"flex",alignItems:"center",justifyContent:"center",transition:"background .12s",opacity:action.disabled?.4:1}}
                  onMouseEnter={e=>{if(!action.disabled)e.currentTarget.style.background="rgba(255,255,255,.24)";}} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.12)"}>
                  <RIcon name={cmd.icon} size={13} color={C.navText}/>
                </button>
              );
            })}
            <button title="Customize Quick Access Toolbar" aria-label="Customize Quick Access Toolbar" onClick={()=>setShowQatCustomize(v=>!v)}
              style={{background:"rgba(255,255,255,.12)",border:"none",borderRadius:3,padding:"4px 4px",cursor:"pointer",color:C.navText,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.24)"} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,.12)"}>
              &#9662;
            </button>
            {showQatCustomize&&(
              <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:"100%",left:0,marginTop:4,background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:4,boxShadow:"0 4px 14px rgba(0,0,0,.25)",zIndex:200,minWidth:220,padding:"6px 0",fontFamily:"'Segoe UI',sans-serif"}}>
                <div style={{padding:"4px 12px 6px",fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".3px"}}>Customize Quick Access Toolbar</div>
                {QAT_COMMANDS.map(cmd=>{
                  const checked=qatCommandIds.includes(cmd.id);
                  return(
                    <label key={cmd.id}
                      style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",fontSize:12,color:C.text,cursor:"pointer"}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <input type="checkbox" checked={checked} onChange={()=>setQatCommandIds(p=>checked?p.filter(id=>id!==cmd.id):[...p,cmd.id])}/>
                      <RIcon name={cmd.icon} size={13} color={C.text}/>
                      {cmd.label}
                    </label>
                  );
                })}
                <div style={{height:1,background:C.ribbonBorder,margin:"4px 0"}}/>
                <button onClick={()=>setQatCommandIds(QAT_DEFAULT_IDS)}
                  style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 12px",fontSize:12,cursor:"pointer",color:C.accent}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Reset to Default</button>
                <button onClick={()=>setShowQatCustomize(false)}
                  style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 12px",fontSize:12,cursor:"pointer",color:C.textSub}}
                  onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Close</button>
              </div>
            )}
          </div>
          <span style={{flex:1}}/>
          {/* Search box now lives centered in the ribbon tab bar below,
              styled like Outlook's own Instant Search box — see the
              "Ribbon tab bar" block just after the title bar. */}

          {/* Window controls */}
          <div style={{display:"flex",alignItems:"stretch",flexShrink:0}}>
            <button onClick={()=>{if(document.fullscreenElement){document.exitFullscreen();}window.resizeTo(800,600);}} title="Minimize" aria-label="Minimize window"
              style={{width:42,height:32,background:"transparent",border:"none",color:C.navText,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.15)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <RIcon name="minimize" size={12} color={C.navText}/>
            </button>
            <button onClick={()=>{if(document.fullscreenElement){document.exitFullscreen();}else{document.documentElement.requestFullscreen();}}} title="Maximize" aria-label="Maximize window"
              style={{width:42,height:32,background:"transparent",border:"none",color:C.navText,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,.15)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <RIcon name="maximize" size={11} color={C.navText}/>
            </button>
            <button onClick={()=>window.close()} title="Close" aria-label="Close window"
              style={{width:42,height:32,background:"transparent",border:"none",color:C.navText,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
              onMouseEnter={e=>e.currentTarget.style.background="#c42b1c"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <RIcon name="close" size={12} color={C.navText}/>
            </button>
          </div>
        </div>
      </div>

      {!isMobile&&(
      <>
      {/*  Ribbon tab bar — sits on the app's own light-gray base (C.sidebar)
          so the rounded white ribbon panel below reads as a floating card
          rather than a flush continuation, matching the ribbon's
          rounded/professional pass. */}
      <div style={{background:C.sidebar,borderBottom:"1px solid "+C.ribbonBorder,display:"flex",alignItems:"center",padding:"4px 10px 0",flexShrink:0}}>
        {showFileMenu&&<FileMenu onClose={()=>setShowFileMenu(false)} settings={settings} addToast={addToast} onLogout={onLogout} onOpenSettings={onOpenSettings}/>}
        {RIBBON_TABS.map(tab=>{
          const isFile=tab.id==="file";
          const isActive=ribbonTab===tab.id;
          return (
            <RibbonTabButton key={tab.id} label={tab.label} isFile={isFile} isActive={isActive}
              onClick={()=>{if(isFile){setShowFileMenu(true);return;}setRibbonTab(tab.id);}}/>
          );
        })}
        {/* Contextual "Search" tab — only appears once the search box (below)
            is engaged, exactly like Outlook's own Search Tools tab, and is
            auto-selected so its Scope/Refine/Options groups are what you see
            first. */}
        {searchActive&&(
          <RibbonTabButton key="search" label="Search" isActive={ribbonTab==="search"} onClick={()=>setRibbonTab("search")}/>
        )}
        {/* Search box — centered in the ribbon per request, styled after
            Outlook's Instant Search box: a scope-selector segment, an icon+
            input segment, and a go/submit segment. */}
        <div style={{flex:1,display:"flex",justifyContent:"center",paddingBottom:6,minWidth:0}}>
          <div style={{display:"flex",alignItems:"stretch",height:26,maxWidth:460,width:"100%",background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:WR.md,overflow:"hidden",boxShadow:"0 1px 3px rgba(0,0,0,.06)"}}>
            <button ref={scopeBtnRef} onClick={()=>setShowScopeMenu(v=>!v)} title="Search scope"
              style={{display:"flex",alignItems:"center",gap:4,background:showScopeMenu?C.ribbonActive:"transparent",border:"none",borderRight:"1px solid "+C.ribbonBorder,padding:"0 8px",cursor:"pointer",fontSize:11,color:C.textSub,whiteSpace:"nowrap",fontFamily:"'Segoe UI',sans-serif"}}>
              {(SEARCH_SCOPE_OPTIONS.find(o=>o.id===searchScope)||SEARCH_SCOPE_OPTIONS[2]).label}
              <span style={{fontSize:8}}>&#9662;</span>
            </button>
            <span style={{display:"flex",alignItems:"center",padding:"0 6px",color:C.textMuted,flexShrink:0}}>
              <RIcon name="search" size={12} color={C.textMuted}/>
            </span>
            <input ref={searchInputRef} value={search}
              onChange={e=>setSearch(e.target.value)}
              onFocus={()=>{if(!searchActive){preSearchRibbonTabRef.current=ribbonTab;}setSearchActive(true);setRibbonTab("search");}}
              placeholder="Search"
              style={{flex:1,minWidth:0,border:"none",outline:"none",fontSize:12,color:C.text,fontFamily:"'Segoe UI',sans-serif",background:"transparent"}}/>
            {crossFolderLoading&&<span style={{display:"flex",alignItems:"center",padding:"0 4px"}}><Sp size={11} color={C.accent}/></span>}
            <button title="More search options"
              style={{display:"flex",alignItems:"center",background:"transparent",border:"none",borderLeft:"1px solid "+C.ribbonBorder,padding:"0 6px",cursor:"pointer",color:C.textMuted,fontSize:8}}>
              &#9662;
            </button>
            <button title="Search" onClick={()=>{preSearchRibbonTabRef.current=ribbonTab==="search"?preSearchRibbonTabRef.current:ribbonTab;setSearchActive(true);setRibbonTab("search");searchInputRef.current?.focus();}}
              style={{display:"flex",alignItems:"center",background:C.accent,border:"none",padding:"0 9px",cursor:"pointer",color:"#fff"}}>
              <RIcon name="chevronRight" size={11} color="#fff"/>
            </button>
          </div>
          <RibbonMenu open={showScopeMenu} anchorRef={scopeBtnRef} onClose={()=>setShowScopeMenu(false)} minWidth={170}>
            {SEARCH_SCOPE_OPTIONS.map(opt=>(
              <button key={opt.id} disabled={opt.disabled}
                title={opt.disabled?"Not built yet":undefined}
                onClick={()=>{setSearchScope(opt.id);setShowScopeMenu(false);}}
                style={{display:"flex",alignItems:"center",gap:8,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 10px",fontSize:12,color:opt.disabled?C.textMuted:C.text,cursor:opt.disabled?"not-allowed":"pointer",opacity:opt.disabled?.5:1,borderRadius:WR.sm,fontFamily:"'Segoe UI',sans-serif"}}
                onMouseEnter={e=>{if(!opt.disabled)e.currentTarget.style.background=C.ribbonHover;}} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <span style={{width:14,flexShrink:0,color:C.accent,fontSize:12}}>{searchScope===opt.id?"✓":""}</span>
                {opt.label}
              </button>
            ))}
          </RibbonMenu>
        </div>
        {composing&&(
          <button onClick={sendEmail} disabled={sending}
            style={{marginBottom:6,background:sending?C.accent+"cc":C.accent,color:"#fff",border:"none",borderRadius:WR.md,padding:"6px 20px",fontSize:12,fontWeight:700,cursor:sending?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:7,fontFamily:"'Segoe UI',sans-serif",boxShadow:"0 2px 6px rgba(0,0,0,.15)",flexShrink:0}}>
            {sending?<><Sp size={11} color="#fff"/> Sending...</>:<><RIcon name="send" size={13} color="#fff"/> Send</>}
          </button>
        )}
      </div>

      {/*  Ribbon content — a floating white rounded card on the sidebar-gray
          tray (set on the tab bar above), rather than a flat full-bleed
          strip. This is the main visible "rounded corners / professional"
          change the ribbon redesign asks for; RGroup/RBtn/RibbonTabButton
          in RibbonPrimitives.js pick up the same radius scale so the
          buttons and tabs inside/above this card match it.  */}
      <div style={{background:"#fff",border:"1px solid "+C.ribbonBorder,borderRadius:WR.lg,boxShadow:"0 2px 10px rgba(0,0,0,.06)",margin:"8px 10px",display:"flex",alignItems:"stretch",padding:"6px 10px 4px",flexShrink:0,minHeight:60,maxHeight:76,overflowX:"auto"}}>
        {ribbonTab==="home"&&(
          <RibbonOverflowRow>
            <RGroup label="New">
              <RBtn icon="mailNew" label="New Email" large onClick={newEmailClick}/>
              <div style={{display:"flex",flexDirection:"column",gap:1}}>
                <RBtn icon="campaign" label="Campaign" onClick={()=>setActiveFolder("campaigns")}/>
                <RBtn icon="sequence" label="Sequence" onClick={()=>setActiveFolder("sequences")}/>
                <RBtn icon="person" label="Contact" onClick={()=>setActiveFolder("contacts")}/>
              </div>
              {/* New Items dropdown — Outlook groups every creatable item type
                  behind one split button here. The three shortcuts above stay
                  exactly as they were (nothing removed); this adds the same
                  destinations plus Template in one menu. Appointment/Meeting
                  and Task now open the real Calendar/Tasks features (P3 #16);
                  Contact Group stays a disabled placeholder since there's no
                  Groups feature at all in this app — same "visible but
                  honest" pattern already used for Rules/Favorites/Folder
                  Properties below. */}
              <RibbonDropdownButton minWidth={160}
                trigger={({toggle})=><RBtn icon="chevronRight" label="New Items" onClick={toggle}/>}>
                {(close)=>(<>
                    {[
                      {label:"Email Message",fn:()=>{close();openInPane();}},
                      {label:"Campaign",fn:()=>{close();setActiveFolder("campaigns");}},
                      {label:"Sequence",fn:()=>{close();setActiveFolder("sequences");}},
                      {label:"Contact",fn:()=>{close();setActiveFolder("contacts");}},
                      {label:"Template",fn:()=>{close();setActiveFolder("templates");}},
                    ].map(item=>(
                      <button key={item.label} onClick={item.fn}
                        style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{item.label}</button>
                    ))}
                    <div style={{height:1,background:C.ribbonBorder,margin:"4px 0"}}/>
                    {[
                      {label:"Appointment",fn:()=>{close();openNewMeetingNow();}},
                      {label:"Meeting",fn:()=>{close();openNewMeetingNow();}},
                      {label:"Task",fn:()=>{close();openNewTaskNow();}},
                    ].map(item=>(
                      <button key={item.label} onClick={item.fn}
                        style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{item.label}</button>
                    ))}
                    <button disabled title="Not built yet — no Groups feature in this app"
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"not-allowed",color:C.textMuted,borderRadius:WR.sm}}>Contact Group</button>
                </>)}
              </RibbonDropdownButton>
            </RGroup>
            <RGroup label="Delete">
              <RBtn icon="trash" label="Delete" danger onClick={()=>selectedItem?.messages?.[0]&&deleteEmail(selectedItem,selectedItem.messages[0])} disabled={!selectedItem||!isMail}/>
              <RBtn icon="archive" label="Archive" onClick={()=>selectedItem?.messages?.[0]&&moveEmailTo(selectedItem,selectedItem.messages[0],"archive")} disabled={!selectedItem||!isMail}/>
              <RBtn icon="junk" label="Junk" onClick={()=>selectedItem?.messages?.[0]&&moveEmailTo(selectedItem,selectedItem.messages[0],"junk")} disabled={!selectedItem||!isMail}/>
            </RGroup>
            <RGroup label="Respond">
              <RBtn icon="reply" label="Reply" large onClick={()=>selectedItem?.messages?.[0]&&doReply(selectedItem.messages[0],"reply")} disabled={!selectedItem||!isMail}/>
              <RBtn icon="replyAll" label="Reply All" large onClick={()=>selectedItem?.messages?.[0]&&doReply(selectedItem.messages[0],"replyAll")} disabled={!selectedItem||!isMail}/>
              <RBtn icon="forward" label="Forward" large onClick={()=>selectedItem?.messages?.[0]&&doReply(selectedItem.messages[0],"forward")} disabled={!selectedItem||!isMail}/>
            </RGroup>
            {/* AI and Find used to sit at the tail end of this ribbon, after
                Quick Steps/Move/Tags — but both are used mid-conversation
                (drafting a reply, searching while reading), not as an
                afterthought once everything else is done, so they move up
                next to Respond. Every handler/disabled condition below is
                unchanged from before; only position moved. */}
            <RGroup label="AI">
              <RBtn icon="sparkle" label="AI Draft" large onClick={aiDraftPane} disabled={!composing||aiLoading}/>
              <RBtn icon="doc" label="Templates" onClick={()=>setActiveFolder("templates")}/>
            </RGroup>
            <RGroup label="Find">
              <RBtn icon="search" label="Search" onClick={()=>searchInputRef.current?.focus()}/>
              <RBtn icon="people" label="People" onClick={()=>setActiveFolder("contacts")}/>
              <RBtn icon="filter" label="Filter" active={mailFilter==="Unread"} onClick={()=>setMailFilter(p=>p==="Unread"?"All":"Unread")}/>
            </RGroup>
            <RGroup label="Print">
              <RBtn icon="print" label="Quick Print" onClick={()=>selectedItem?.messages?.[0]&&printEmail(selectedItem.messages[0],selectedItem.subject)} disabled={!selectedItem||!isMail}/>
            </RGroup>
            {/* Organize — Quick Steps, Move, and the message-action half of
                Tags (Mark Read/Unread, Follow Up, Complete) used to be three
                separate groups, each with its own caption eating ribbon
                width for only 3-4 buttons. Collapsed into one group (wraps
                to multiple rows via RGroup's flexWrap, same as any other
                group) — every button/handler/disabled condition below is
                byte-identical to before, just regrouped. Tags' compose-only
                items (Importance/Read Receipt) move to their own
                conditionally-shown group further down, since they only ever
                do anything while composing — see "Tags" below. */}
            <RGroup label="Organize">
              <RibbonDropdownButton minWidth={150} maxHeight={280}
                openState={[showMoveMenu,setShowMoveMenu]}
                onOpen={()=>{setShowMoveOtherFolders(false);setShowCategoryMenu(false);}}
                trigger={({toggle})=><RBtn icon="move" label="Move" onClick={toggle} disabled={!selectedItem||!isMail}/>}>
                {(close)=>(
                    !showMoveOtherFolders?(
                      <>
                        {["inbox","archive","junk","trash"].map(f=>(
                          <button key={f} onClick={()=>{close();selectedItem?.messages?.[0]&&moveEmailTo(selectedItem,selectedItem.messages[0],f);}}
                            style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm,textTransform:"capitalize"}}
                            onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{f}</button>
                        ))}
                        {/* Other Folder... — opens the full folder-tree picker below
                            (every canonical + custom IMAP folder on this account),
                            not just the four shortcuts above. Nothing above changes. */}
                        <div style={{height:1,background:C.ribbonBorder,margin:"4px 0"}}/>
                        <button onClick={()=>setShowMoveOtherFolders(true)}
                          style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.accent,borderRadius:WR.sm}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Other Folder&#8230;</button>
                      </>
                    ):(
                      <>
                        <button onClick={()=>setShowMoveOtherFolders(false)}
                          style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:11,fontWeight:600,cursor:"pointer",color:C.textSub,borderRadius:WR.sm}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>&#8249; Back</button>
                        {[
                          {id:"inbox",label:"Inbox"},{id:"focused",label:"Focused"},{id:"sent",label:"Sent Items"},
                          {id:"drafts",label:"Drafts"},{id:"outbox",label:"Outbox"},{id:"deleted",label:"Deleted Items"},
                          {id:"archive",label:"Archive"},{id:"junk",label:"Junk Mail"},
                        ].map(f=>(
                          <button key={f.id} onClick={()=>{close();setShowMoveOtherFolders(false);selectedItem?.messages?.[0]&&moveEmailTo(selectedItem,selectedItem.messages[0],f.id);}}
                            style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm}}
                            onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{f.label}</button>
                        ))}
                        {(customFolders[compose.from]||[]).length>0&&<div style={{height:1,background:C.ribbonBorder,margin:"4px 0"}}/>}
                        {(customFolders[compose.from]||[]).map(f=>(
                          <button key={f.id} onClick={()=>{close();setShowMoveOtherFolders(false);selectedItem?.messages?.[0]&&moveEmailTo(selectedItem,selectedItem.messages[0],f.id);}}
                            style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}
                            onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>{f.label}</button>
                        ))}
                      </>
                    )
                )}
              </RibbonDropdownButton>
              <RBtn icon="gear" label="Rules" onClick={openRulesModal}/>
              <RBtn icon="check" label="Inbox Zero" onClick={openInboxZeroModal}/>
              <RibbonDropdownButton minWidth={120}
                openState={[showCategoryMenu,setShowCategoryMenu]}
                onOpen={()=>setShowMoveMenu(false)}
                trigger={({toggle})=><RBtn icon="tag" label="Categorize" onClick={toggle} disabled={!selectedItem||!isMail}/>}>
                {(close)=>(<>
                    {Object.entries(CATEGORY_COLORS).map(([key,color])=>(
                      <button key={key} onClick={()=>{close();selectedItem?.messages?.[0]&&setCategoryFor(selectedItem.messages[0],key);}}
                        style={{display:"flex",alignItems:"center",gap:6,width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.text,borderRadius:WR.sm,textTransform:"capitalize"}}
                        onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <span style={{width:9,height:9,borderRadius:"50%",background:color,flexShrink:0}}/>{key}
                      </button>
                    ))}
                    <button onClick={()=>{close();selectedItem?.messages?.[0]&&setCategoryFor(selectedItem.messages[0],null);}}
                      style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"5px 8px",fontSize:12,cursor:"pointer",color:C.textSub,borderRadius:WR.sm}}
                      onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Clear category</button>
                </>)}
              </RibbonDropdownButton>
              <RBtn icon="person" label="To Manager" onClick={()=>quickStepCompose("manager")}/>
              <RBtn icon="people" label="Team Email" onClick={()=>quickStepCompose("team")}/>
              <RBtn icon="calendar" label="Meeting" onClick={()=>setMainView("calendar")}/>
              <RBtn icon="check" label="Mark Read" onClick={()=>markSelectionRead(true)} disabled={!isMail||(!selectedItem&&!multiSel.length)}/>
              <RBtn icon="dash" label="Mark Unread" onClick={()=>markSelectionRead(false)} disabled={!isMail||(!selectedItem&&!multiSel.length)}/>
              <RBtn icon="flag" label="Follow Up" active={!!selectedItem?.messages?.[0]?.isFlagged} onClick={()=>selectedItem?.messages?.[0]&&toggleFlagFor(selectedItem.messages[0])} disabled={!selectedItem||!isMail}/>
              <RBtn icon="check" label="Complete" onClick={async()=>{const m=selectedItem?.messages?.[0];if(!m)return;await patchMessage(m.id,{isFlagged:false,isRead:true});await loadFolderItems();await refreshUnreadCounts();addToast("Marked complete","success");}} disabled={!selectedItem||!isMail}/>
            </RGroup>
            {composing&&(
              <RGroup label="Tags">
                <RBtn icon="flag" label="High" active={compose.importance==="high"} onClick={()=>setCompose(p=>({...p,importance:"high"}))}/>
                <RBtn icon="dash" label="Normal" active={compose.importance==="normal"} onClick={()=>setCompose(p=>({...p,importance:"normal"}))}/>
                <RBtn icon="flag" label="Low" active={compose.importance==="low"} onClick={()=>setCompose(p=>({...p,importance:"low"}))}/>
                <RBtn icon="check" label="Read Receipt" active={!!compose.readReceiptRequested} title="Request a read receipt (honored by Outlook/Exchange recipients; often ignored by Gmail and other consumer clients)" onClick={()=>setCompose(p=>({...p,readReceiptRequested:!p.readReceiptRequested}))}/>
              </RGroup>
            )}
            {composing&&(
              <RGroup label="Insert">
                <RBtn icon="grid" label="Table" onClick={insertTableInPane}/>
                <RBtn icon="calendar" label="Date & Time" onClick={insertDateTimeInPane}/>
                <RBtn icon="infoCircle" label="Word Count" onClick={showWordCountPane}/>
              </RGroup>
            )}
            {composing&&(
              <RGroup label="Compose">
                <RBtn icon="save" label="Save Draft" onClick={saveDraftReal}/>
                <RBtn icon="popOut" label="Pop Out" onClick={()=>{openInWindow();setComposing(false);}}/>
                <RibbonDropdownButton minWidth={200}
                  openState={[showScheduleMenu,setShowScheduleMenu]}
                  trigger={({toggle})=><RBtn icon="clock" label="Schedule" onClick={toggle}/>}>
                    <div style={{padding:4,display:"flex",flexDirection:"column",gap:6}}>
                      <label style={{fontSize:11,color:C.textSub}}>Send at:</label>
                      <input type="datetime-local" value={scheduleAt} onChange={e=>setScheduleAt(e.target.value)}
                        style={{border:"1px solid "+C.ribbonBorder,borderRadius:WR.sm,padding:"4px 6px",fontSize:12}}/>
                      <button onClick={()=>scheduleSend(scheduleAt)} disabled={scheduling||!scheduleAt}
                        style={{background:C.accent,color:"#fff",border:"none",borderRadius:WR.sm,padding:"5px 8px",fontSize:12,cursor:scheduling?"not-allowed":"pointer",fontWeight:600}}>
                        {scheduling?"Scheduling...":"Confirm Schedule"}
                      </button>
                    </div>
                </RibbonDropdownButton>
                <RBtn icon="trash" label="Discard" danger onClick={()=>setComposing(false)}/>
              </RGroup>
            )}
          </RibbonOverflowRow>
        )}
        {ribbonTab==="send"&&(
          <RibbonOverflowRow>
            <RGroup label="Send & Receive">
              <RBtn icon="refreshDouble" label="Send/Receive All" large onClick={()=>{if(effectiveOffline){addToast("Cannot sync while offline","error");return;}syncNow();}}/>
              <RBtn icon="refresh" label="Update Folder" onClick={()=>{if(effectiveOffline){addToast("Cannot update while offline","error");return;}syncNow();}}/>
            </RGroup>
            <RGroup label="Preferences">
              <RBtn icon={effectiveOffline?"cloudOff":"cloudOff"} label="Work Offline" active={effectiveOffline} large onClick={toggleOffline}/>
              <RBtn icon="queue" label={`Queue (${queue.length})`} active={showQueue} onClick={()=>setShowQueue(!showQueue)}/>
            </RGroup>
            <RGroup label="Download">
              <RBtn icon="refresh" label="Sync Now" onClick={()=>{if(effectiveOffline){addToast("Cannot sync while offline","error");return;}syncNow();if(queue.length)syncQueue();}}/>
            </RGroup>
          </RibbonOverflowRow>
        )}
        {ribbonTab==="folder"&&(()=>{
          const isCanon=Object.prototype.hasOwnProperty.call(FOLDER_API_MAP,activeFolder);
          const noAccount=!compose.from;
          return(
            <RibbonOverflowRow>
              <RGroup label="New">
                <RBtn icon="plusCircle" label="New Folder" large disabled={noAccount} onClick={()=>createFolderFor(compose.from,isCanon?"":activeFolder)}/>
              </RGroup>
              <RGroup label="Actions">
                <RBtn icon="doc" label="Rename Folder" disabled={noAccount||isCanon} onClick={()=>renameFolderFor(compose.from,activeFolder)}/>
                <RBtn icon="trash" label="Delete Folder" disabled={noAccount||isCanon} onClick={()=>deleteFolderFor(compose.from,activeFolder)}/>
              </RGroup>
              <RGroup label="Clean Up">
                <RBtn icon="check" label="Mark All as Read" disabled={noAccount} onClick={()=>markAllReadFor(compose.from,activeFolder)}/>
                <RBtn icon="trash" label="Delete All" disabled={noAccount} onClick={()=>emptyFolderFor(compose.from,activeFolder)}/>
              </RGroup>
              <RGroup label="Favorites">
                <RBtn icon="plusCircle" label="Add to Favorites" disabled title="Not built yet"/>
              </RGroup>
              <RGroup label="Properties">
                <RBtn icon="infoCircle" label="Folder Properties" disabled title="Not built yet"/>
              </RGroup>
            </RibbonOverflowRow>
          );
        })()}
        {ribbonTab==="view"&&(
          <RibbonOverflowRow>
            <RGroup label="Reading Pane">
              <RBtn icon="layoutRight" label="Right" active={readingPanePos==="right"} onClick={()=>setReadingPanePos("right")}/>
              <RBtn icon="layoutBottom" label="Bottom" active={readingPanePos==="bottom"} onClick={()=>setReadingPanePos("bottom")}/>
              <RBtn icon="layoutOff" label="Off" active={readingPanePos==="off"} onClick={()=>setReadingPanePos("off")}/>
            </RGroup>
            <RGroup label="Folder Pane">
              <RBtn icon="layoutLeft" label="Normal" active={sidebarMode==="normal"} onClick={()=>setSidebarMode("normal")}/>
              <RBtn icon="layoutLeft" label="Minimized" active={sidebarMode==="minimized"} onClick={()=>setSidebarMode("minimized")}/>
              <RBtn icon="layoutOff" label="Off" active={sidebarMode==="off"} onClick={()=>setSidebarMode("off")}/>
            </RGroup>
            <RGroup label="Arrangement">
              <RBtn icon="calendar" label="By Date" active={sortBy==="date"} onClick={()=>setSortBy("date")}/>
              <RBtn icon="person" label="By Sender" active={sortBy==="sender"} onClick={()=>setSortBy("sender")}/>
              <RBtn icon="textLines" label="By Subject" active={sortBy==="subject"} onClick={()=>setSortBy("subject")}/>
              <RBtn icon="chevronRight" label={sortDir==="desc"?"Newest on Top":"Oldest on Top"} onClick={()=>setSortDir(d=>d==="desc"?"asc":"desc")}/>
            </RGroup>
          </RibbonOverflowRow>
        )}
        {ribbonTab==="file"&&(
          <RibbonOverflowRow>
            <RGroup label="New">
              <RBtn icon="mailNew" label="Email" large onClick={newEmailClick}/>
              <div style={{display:"flex",flexDirection:"column",gap:1}}>
                <RBtn icon="campaign" label="Campaign" onClick={()=>setActiveFolder("campaigns")}/>
                <RBtn icon="sequence" label="Sequence" onClick={()=>setActiveFolder("sequences")}/>
                <RBtn icon="doc" label="Template" onClick={()=>setActiveFolder("templates")}/>
              </div>
            </RGroup>
            <RGroup label="Import / Export">
              <RBtn icon="upload" label="Import CSV" onClick={()=>setActiveFolder("contactlists")}/>
              <RBtn icon="download" label="Export" onClick={exportCurrentFolder}/>
            </RGroup>
            <RGroup label="Account">
              <RBtn icon="person" label="Profile" onClick={goToSettings}/>
              <RBtn icon="gear" label="Settings" onClick={goToSettings}/>
              <RBtn icon="idCard" label="Accounts" onClick={()=>setActiveFolder("senderaccounts")}/>
            </RGroup>
          </RibbonOverflowRow>
        )}
        {ribbonTab==="help"&&(
          <RGroup label="Help">
            {/* Docs/Tutorials/Support removed — there's no real destination for them
                yet (no hosted docs site, no support inbox wired up), and a fake link
                would be worse than no button. About is real and stays. */}
            <RBtn icon="help" label="About" onClick={()=>addToast("SDR ReachOut Platform v1.0","info")}/>
          </RGroup>
        )}
        {ribbonTab==="search"&&(
          <>
            <RGroup label="Refine">
              <RBtn icon="mail" label="Unread" active={searchRefine.unread} onClick={()=>setSearchRefine(p=>({...p,unread:!p.unread}))}/>
              <RBtn icon="flag" label="Flagged" active={searchRefine.flagged} onClick={()=>setSearchRefine(p=>({...p,flagged:!p.flagged}))}/>
              <RBtn icon="!" label="Important" active={searchRefine.important} onClick={()=>setSearchRefine(p=>({...p,important:!p.important}))}/>
              {/* More — has-attachment/categorized/size filters aren't modeled
                  in this app's data yet, so this stays honestly disabled
                  rather than faking options with nothing behind them. */}
              <RBtn icon="▾" label="More" disabled title="Not built yet"/>
            </RGroup>
            <RGroup label="Scope">
              {SEARCH_SCOPE_OPTIONS.map(opt=>(
                <RBtn key={opt.id} icon="folder" label={opt.label} active={searchScope===opt.id} disabled={opt.disabled}
                  title={opt.disabled?"Not built yet":undefined}
                  onClick={()=>setSearchScope(opt.id)}/>
              ))}
            </RGroup>
            <RGroup label="Options">
              <RBtn icon="trash" label="Include Deleted Items" active={includeDeleted} onClick={()=>setIncludeDeleted(v=>!v)}/>
            </RGroup>
            <RGroup label="Close">
              <RBtn icon="close" label="Close Search" danger onClick={()=>{
                setSearchActive(false);
                setSearch("");
                setSearchRefine({unread:false,flagged:false,important:false});
                setIncludeDeleted(false);
                setCrossFolderResults(null);
                setRibbonTab(preSearchRibbonTabRef.current||"home");
              }}/>
            </RGroup>
          </>
        )}
      </div>
      </>
      )}

      {isMobile&&mainView==="mail"&&(
        <div style={{background:"#f3f2f1",borderBottom:"1px solid "+C.ribbonBorder,display:"flex",alignItems:"center",padding:"6px 10px",flexShrink:0,gap:8}}>
          <span style={{fontSize:14,fontWeight:600,color:C.text,flex:1}}>SDR ReachOut</span>
          {composing?(
            <button onClick={sendEmail} disabled={sending} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"6px 16px",fontSize:12,fontWeight:700,cursor:"pointer"}}>{sending?"Sending...":"Send"}</button>
          ):(
            <button onClick={newEmailClick} aria-label="New email" style={{background:C.accent,color:"#fff",border:"none",borderRadius:20,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><RIcon name="plus" size={17} color="#fff"/></button>
          )}
        </div>
      )}

      {/*  Main 3-pane layout  */}
      {/* minHeight:0 here (and on the reading/compose panes' own flex
          columns below) is load-bearing, not decorative — without it, a
          flex item's default min-height:auto means it won't shrink below
          its own content's natural height, so on a browser window short
          enough that the compose header/fields/merge-tag bar/attach row/
          formatting ribbon alone approach the viewport height, this whole
          row silently overflows past .ow-workspace-root's height:100vh +
          overflow:hidden instead of respecting it — pushing the actual
          message-body editor below the clipped boundary with no scrollbar
          to reach it. minHeight:0 lets flex:1 actually constrain height at
          every level, so the innermost overflow:"auto" editor wrapper
          (below) gets to do the scrolling it was always meant to do. */}
      <div style={{display:"flex",flex:1,overflow:"hidden",minHeight:0}}>

        {/*  Left navigation pane  */}
        {showSidebar&&(!isMobile||mobileView==="folders")&&(
          <div title={sidebarMinimized?"Folder Pane (Minimized) — see View > Folder Pane to expand":undefined}
            style={{width:isMobile?"100%":(sidebarMinimized?56:sidebarWidth),background:C.sidebar,borderRight:"1px solid "+C.sidebarBorder,display:"flex",flexDirection:"column",flexShrink:0,overflowY:"auto",overflowX:"hidden",paddingTop:6,paddingBottom:10,transition:sidebarMinimized?"width .12s":"none"}}>

            {/* Account section  one per sender, collapsible */}
            {senders.length===0&&(
              <div style={{padding:"10px 12px"}}>
                <div style={{background:"#fff4ce",border:"1px solid #d83b0133",borderRadius:4,padding:"8px 10px"}}>
                  <p style={{fontSize:11,fontWeight:700,color:"#d83b01",marginBottom:3}}> No accounts connected</p>
                  <p style={{fontSize:11,color:"#605e5c",lineHeight:1.5}}>Connect Outlook in Settings to activate live email sending.</p>
                </div>
              </div>
            )}
            {senders.map(s=>{
              const isExp=expanded[s.email]!==false;
              const isActive=s.email===compose.from;
              return(
                <div key={s.id} style={{marginBottom:6}}>
                  <div onClick={()=>setExpanded(p=>({...p,[s.email]:!isExp}))}
                    style={{padding:"7px 10px 7px 8px",fontSize:13,fontWeight:600,color:isActive?C.accent:C.text,display:"flex",alignItems:"center",gap:6,cursor:"pointer",borderLeft:isActive?"3px solid "+C.accent:"3px solid transparent",background:isActive?"#ebf3fb":"transparent"}}
                    onMouseEnter={e=>e.currentTarget.style.background=isActive?"#ebf3fb":"#e8e6e4"}
                    onMouseLeave={e=>e.currentTarget.style.background=isActive?"#ebf3fb":"transparent"}>
                    <span style={{fontSize:9,transition:"transform .15s",transform:isExp?"rotate(90deg)":"rotate(0deg)",display:"inline-block",color:C.textSub,flexShrink:0}}>&#9656;</span>
                    <span style={{flexShrink:0,display:"flex"}}><FolderIcon type="mailbox" color={isActive?C.accent:C.text}/></span>
                    <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{s.email}</span>
                  </div>
                  {isExp&&(()=>{
                    const {roots,nestedUnderCanonical}=buildFolderTree(customFolders[s.email]||[]);
                    return(
                      <>
                        {["inbox","focused","sent","drafts","outbox","deleted","archive","junk"].map(f=>{
                          const labels={"sent":"Sent Items","deleted":"Deleted Items","focused":"Focused","inbox":"Inbox","drafts":"Drafts","outbox":"Outbox","archive":"Archive","junk":"Junk Mail"};
                          const isSel=activeFolder===f&&compose.from===s.email;
                          const children=nestedUnderCanonical[f]||[];
                          const hasChildren=children.length>0;
                          const canonExpKey=s.email+":__canonical__:"+f;
                          const canonExp=expandedFolders[canonExpKey]!==false;
                          return(
                            <div key={f}>
                              <button onClick={()=>{setActiveFolder(f);setComposing(false);setSelectedItem(null);setCompose(p=>({...p,from:s.email}));}}
                                onContextMenu={e=>{e.preventDefault();setFolderCtxMenu({x:e.clientX,y:e.clientY,accountEmail:s.email,folderId:f,label:labels[f],canonical:true});}}
                                onDragOver={e=>e.preventDefault()}
                                onDrop={e=>handleFolderDrop(e,f)}
                                className="ow-item"
                                style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"6px 10px 6px 10px",border:"none",background:isSel?C.sidebarSel:"transparent",color:isSel?C.sidebarSelText:C.text,cursor:"pointer",borderLeft:isSel?"3px solid "+C.sidebarSelBorder:"3px solid transparent",fontSize:13,fontFamily:"'Segoe UI',sans-serif",fontWeight:isSel||(f==="inbox"&&unreadCounts[s.email]>0)?600:400}}>
                                {hasChildren
                                  ? <span onClick={e=>{e.stopPropagation();toggleFolderExpand(canonExpKey);}} style={{width:11,flexShrink:0,fontSize:9,color:C.textSub,transition:"transform .15s",transform:canonExp?"rotate(90deg)":"rotate(0deg)",display:"inline-block",cursor:"pointer"}}>&#9656;</span>
                                  : <span style={{width:11,flexShrink:0}}/>}
                                <span style={{flexShrink:0,display:"flex"}}><FolderIcon type={f} color={isSel?C.sidebarSelText:C.text}/></span>
                                <span style={{flex:1,textAlign:"left",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{labels[f]}</span>
                                {f==="inbox"&&unreadCounts[s.email]>0&&<Badge v={unreadCounts[s.email]}/>}
                              </button>
                              {hasChildren&&canonExp&&children.map(child=>renderFolderNode(child,1,s.email))}
                            </div>
                          );
                        })}
                        {roots.map(node=>renderFolderNode(node,0,s.email))}
                      </>
                    );
                  })()}
                  {isExp&&(
                    <button onClick={()=>createFolderFor(s.email)}
                      className="ow-item"
                      style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"6px 10px 6px 10px",border:"none",background:"transparent",color:C.accent,cursor:"pointer",fontSize:12,fontFamily:"'Segoe UI',sans-serif"}}>
                      <span style={{width:11,flexShrink:0}}/>
                      <span style={{flexShrink:0,display:"flex"}}><FolderIcon type="newFolder" color={C.accent}/></span>
                      <span>New Folder</span>
                    </button>
                  )}
                </div>
              );
            })}

            <div style={{height:1,background:C.sidebarBorder,margin:"4px 0"}}/>

            {/* Nav tree sections */}
            {NAV_TREE.map(section=>(
              <div key={section.section}>
                <button onClick={()=>toggleExpand(section.section)}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:6,padding:"5px 10px",border:"none",background:"transparent",color:C.text,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Segoe UI',sans-serif",textTransform:"uppercase",letterSpacing:".4px"}}>
                  <span style={{fontSize:9,transition:"transform .15s",transform:expanded[section.section]?"rotate(90deg)":"rotate(0deg)",display:"inline-block"}}></span>
                  <span style={{flex:1,textAlign:"left"}}>{section.section}</span>
                </button>
                {expanded[section.section]&&section.items.map(item=>(
                  <button key={item.id} onClick={()=>{setActiveFolder(item.id);setComposing(false);setSelectedItem(null);}}
                    className="ow-item"
                    style={{width:"100%",display:"flex",alignItems:"center",gap:7,padding:"5px 10px 5px 26px",border:"none",background:activeFolder===item.id?C.sidebarSel:"transparent",color:activeFolder===item.id?C.sidebarSelText:C.text,cursor:"pointer",borderLeft:activeFolder===item.id?"3px solid "+C.sidebarSelBorder:"3px solid transparent",fontSize:12,fontFamily:"'Segoe UI',sans-serif"}}>
                    <span style={{width:16,textAlign:"center"}}>{item.icon}</span>
                    <span style={{flex:1,textAlign:"left"}}>{item.label}</span>
                    {item.id==="leads"&&<Badge v={leads.length}/>}
                    {item.id==="contacts"&&<Badge v={leads.length}/>}
                    {item.id==="campaigns"&&<Badge v={campaigns.length} color={C.green}/>}
                    {item.id==="opportunities"&&<Badge v={opps.length} color={C.gold}/>}
                    {item.id==="sequences"&&<Badge v={sequences.length} color={C.purple}/>}
                    {item.id==="templates"&&<Badge v={templates.length} color={C.accent}/>}
                  </button>
                ))}
              </div>
            ))}

            {/* Bottom icons — Mail is this view itself; Contacts jumps to the
                real page elsewhere in the app (this workspace can run as a
                standalone popped-out window with no shared app state, so we hand
                off via localStorage + a reload rather than in-memory navigation,
                which works whether embedded or popped out). Calendar and Tasks
                are both real in-Workspace views (mainView switch, same pattern).
                More has no corresponding feature built yet, so it stays disabled
                rather than pretending to work. */}
            <div style={{position:"sticky",bottom:0,background:C.sidebar,marginTop:"auto",borderTop:"1px solid "+C.sidebarBorder,padding:"6px 8px",display:"flex",gap:4,justifyContent:"center"}}>
              {[
                {ic:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 6l10 7 10-7"/></svg>,id:"inbox",title:"Mail",fn:()=>{setMainView("mail");setActiveFolder("inbox");}},
                {ic:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>,id:"calendar",title:"Calendar",fn:()=>{setMainView("calendar");}},
                {ic:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3"/><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1"/><circle cx="17" cy="8" r="2.3"/><path d="M16.5 12.2A4.2 4.2 0 0 1 21 16.3V17" /></svg>,id:"contacts",title:"People",fn:()=>{localStorage.setItem("sdr_page","contacts");window.location.href="/";}},
                {ic:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="4" width="14" height="17" rx="2"/><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M8 13l2 2 4-4"/></svg>,id:"tasks",title:"Tasks",fn:()=>{setMainView("tasks");}},
                {ic:<svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>,id:"more",title:"More (not available yet)",disabled:true},
              ].map(b=>{
                const isActive=b.id==="calendar"?mainView==="calendar":b.id==="tasks"?mainView==="tasks":b.id==="inbox"?mainView==="mail"&&activeFolder===b.id:activeFolder===b.id;
                return(
                <button key={b.id} onClick={()=>{if(b.disabled){addToast(b.title,"info");return;}b.fn();}}
                  title={b.title}
                  style={{background:isActive&&!b.disabled?C.accentL:"transparent",border:"1px solid "+(isActive&&!b.disabled?C.accent:"transparent"),borderRadius:3,padding:"6px 8px",cursor:b.disabled?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:b.disabled?"#c0c0c0":(isActive?C.accent:C.textSub)}}>
                  {b.ic}
                </button>
              );})}
            </div>
          </div>
        )}

        {/* Folder pane <-> list pane splitter — only when there's a fixed-width
            folder pane actually next to something to drag it against; the
            minimized (icon-strip) state has a deliberately fixed width like
            real Outlook, so it isn't resizable. */}
        {showSidebar&&!isMobile&&!sidebarMinimized&&(
          <Splitter orientation="vertical" onDrag={delta=>setSidebarWidth(w=>Math.max(160,Math.min(450,w+delta)))}/>
        )}

        {/*  List + Reading pane group — flexDirection flips to "column" when
            View > Reading Pane is set to "Bottom", same panes and same
            props either way, just stacked instead of side-by-side  */}
        <div style={{display:"flex",flex:1,overflow:"hidden",minWidth:0,flexDirection:readingPanePos==="bottom"?"column":"row"}}>

        {/*  Center list pane  */}
        {(!isMobile||mobileView==="list")&&(
        <div style={{width:isMobile||readingPanePos==="bottom"?"100%":listPaneWidth,height:readingPanePos==="bottom"?listPaneHeight:undefined,borderRight:readingPanePos==="bottom"?"none":"1px solid "+C.border,borderBottom:readingPanePos==="bottom"?"1px solid "+C.border:"none",display:"flex",flexDirection:"column",flexShrink:0,background:C.list}}>
          {/* Pane header */}
          <div style={{padding:"7px 12px",borderBottom:"1px solid "+C.listBorder,background:"#f8f7f6",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              {isMobile&&(
                <button onClick={()=>setMobileView("folders")} aria-label="Back to folders" style={{border:"none",background:"transparent",color:C.accent,fontSize:18,cursor:"pointer",padding:"0 4px 0 0",lineHeight:1}}>&#8592;</button>
              )}
              <span style={{fontSize:13,fontWeight:600,color:C.text,textTransform:"capitalize"}}>{activeFolder.replace(/([A-Z])/g," $1")}</span>
            </div>
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              {/* Bug fix (Phase 5 terminology pass): this used to render on
                  both Leads and Contacts (isLead covers both), but
                  getListItems() only ever applied filterStatus on the Leads
                  branch — so picking a status on the Contacts view rendered
                  a dropdown that silently did nothing. Contacts is the full
                  directory, not the staged pipeline, so it never had a
                  status filter to apply; only show the control where it's
                  real. */}
              {activeFolder==="leads"&&(
                <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                  style={{border:"1px solid "+C.border,borderRadius:3,padding:"2px 6px",fontSize:11,color:C.text,background:"#fff",outline:"none"}}>
                  <option value="all">All Status</option>
                  {LEAD_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              )}
              <button onClick={()=>isMail?syncNow():loadAll()} style={{border:"none",background:"transparent",color:C.accent,fontSize:13,cursor:"pointer",display:"flex",alignItems:"center",padding:"2px 4px"}} title="Refresh" aria-label="Refresh"><RIcon name="refresh" size={15}/></button>
            </div>
          </div>
          {/* All / Unread tabs */}
          <div style={{display:"flex",borderBottom:"1px solid "+C.listBorder,padding:"0 8px",background:"#f8f7f6",flexShrink:0}}>
            {["All","Unread"].map(t=>(
              <button key={t} onClick={()=>setMailFilter(t)} style={{padding:"4px 12px",border:"none",background:"transparent",color:mailFilter===t?C.accent:C.textSub,fontSize:12,cursor:"pointer",borderBottom:mailFilter===t?"2px solid "+C.accent:"2px solid transparent",fontWeight:mailFilter===t?600:400,fontFamily:"'Segoe UI',sans-serif"}}>{t}</button>
            ))}
          </div>
          {multiSel.length>0&&isMail&&(
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:C.accentL,borderBottom:"1px solid "+C.listBorder,flexShrink:0}}>
              <span style={{fontSize:12,color:C.accent,fontWeight:600}}>{multiSel.length} selected</span>
              <span style={{flex:1}}/>
              <button onClick={()=>markSelectionRead(true)}
                style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 10px",fontSize:11,cursor:"pointer",color:C.text}}>Mark Read</button>
              <button onClick={()=>moveMessageIdsToFolder(multiSel,"archive")}
                style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 10px",fontSize:11,cursor:"pointer",color:C.text}}>Archive</button>
              <button onClick={()=>moveMessageIdsToFolder(multiSel,"trash")}
                style={{background:C.redL,border:"1px solid "+C.red+"33",borderRadius:4,padding:"4px 10px",fontSize:11,cursor:"pointer",color:C.red}}>Delete</button>
              <button onClick={()=>setMultiSel([])} style={{background:"none",border:"none",fontSize:11,color:C.textSub,cursor:"pointer"}}>Clear</button>
            </div>
          )}
          {/* List */}
          <div style={{flex:1,overflowY:"auto"}}>
            {listItems.length===0&&(
              <div style={{padding:"40px 16px",textAlign:"center"}}>
                <p style={{fontSize:36,color:C.textMuted,marginBottom:8}}></p>
                <p style={{fontSize:13,color:C.textSub}}>Nothing to show here.</p>
              </div>
            )}

            {/* Email threads */}
            {isMail&&listItems.map((t,tIdx)=>{
              const isSel=selectedItem?.id===t.id;
              const msgId=t.messages?.[0]?.id;
              const isMultiSel=multiSel.includes(msgId);
              const latest=t.messages?.[0];
              const unread=!t.isRead;
              const dispName=latest?.direction==="out"||t.folder==="sent"?(t.to||""):(latest?.fromName||latest?.from||t.from||"");
              const when=latest?.sentAt||latest?.receivedAt;
              return(
                <div key={t.id}
                  draggable
                  onDragStart={e=>{
                    const ids=isMultiSel?multiSel:[msgId].filter(Boolean);
                    e.dataTransfer.setData("text/plain",JSON.stringify(ids));
                    e.dataTransfer.effectAllowed="move";
                  }}
                  onClick={async(e)=>{
                    if(e.shiftKey&&lastClickIdxRef.current!=null){
                      const lo=Math.min(lastClickIdxRef.current,tIdx),hi=Math.max(lastClickIdxRef.current,tIdx);
                      const rangeIds=listItems.slice(lo,hi+1).map(x=>x.messages?.[0]?.id).filter(Boolean);
                      setMultiSel(rangeIds);
                      setSelectedItem(t);setComposing(false);
                      return;
                    }
                    if(e.ctrlKey||e.metaKey){
                      setMultiSel(p=>p.includes(msgId)?p.filter(x=>x!==msgId):[...p,msgId].filter(Boolean));
                      lastClickIdxRef.current=tIdx;
                      return;
                    }
                    lastClickIdxRef.current=tIdx;
                    setMultiSel([]);
                    // Drafts open straight into the compose editor for continued
                    // editing, the way Outlook does, instead of a read-only pane.
                    if(activeFolder==="drafts"||latest?.isDraft){resumeDraft(t);return;}
                    setSelectedItem(t);setComposing(false);
                    const unreadMsgs=(t.messages||[]).filter(m=>!m.isRead);
                    if(unreadMsgs.length){
                      await Promise.all(unreadMsgs.map(m=>fetch("/api/email/"+m.id,{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({isRead:true})})));
                      setFolderItems(p=>p.map(x=>x.id===t.id?{...x,isRead:true,messages:x.messages.map(m=>({...m,isRead:true}))}:x));
                      refreshUnreadCounts();
                    }
                  }}
                  className="ow-item"
                  onContextMenu={e=>{e.preventDefault();setEmailCtxMenu({x:e.clientX,y:e.clientY,thread:t,msg:latest});}}
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isMultiSel?C.accentL:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:(latest?.category?"3px solid "+CATEGORY_COLORS[latest.category]:"3px solid transparent"),display:"flex",gap:8,alignItems:"flex-start"}}>
                  <div style={{width:6,flexShrink:0,marginTop:5}}>{unread&&<span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:C.accent}}/>}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:6,marginBottom:2}}>
                      <span style={{fontSize:12,fontWeight:unread?700:400,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{dispName}</span>
                      <span style={{fontSize:10,color:C.textMuted,flexShrink:0}}>{when?new Date(when).toLocaleDateString():""}</span>
                    </div>
                    <p style={{fontSize:12,fontWeight:unread?600:400,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:2,display:"flex",alignItems:"center",gap:4}}>
                      {latest?.importance==="high"&&<span title="High importance" style={{color:C.red,fontWeight:700,flexShrink:0}}>!</span>}
                      {latest?.importance==="low"&&<span title="Low importance" style={{color:C.accent,flexShrink:0,display:"inline-flex",transform:"rotate(90deg)"}}><RIcon name="chevronRight" size={10}/></span>}
                      <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.subject}</span>
                    </p>
                    <p style={{fontSize:11,color:C.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{latest?.preview||latest?.body?.replace(/<[^>]+>/g,"").slice(0,60)||""}</p>
                  </div>
                  <button onClick={async(e)=>{e.stopPropagation();const nf=!t.isFlagged;await fetch("/api/email/"+(latest?.id||t.id),{method:"PATCH",headers:{"Content-Type":"application/json",Authorization:"Bearer "+(localStorage.getItem("sdr_token")||"")},body:JSON.stringify({isFlagged:nf})});setFolderItems(p=>p.map(x=>x.id===t.id?{...x,isFlagged:nf}:x));}}
                    title={t.isFlagged?"Unflag":"Flag"} aria-label={t.isFlagged?"Unflag message":"Flag message"} style={{background:"none",border:"none",cursor:"pointer",color:t.isFlagged?C.amber:C.textMuted,fontSize:13,flexShrink:0,padding:0}}>{t.isFlagged?"\u2605":"\u2606"}</button>
                </div>
              );
            })}

            {/* Leads / Contacts */}
            {isLead&&listItems.map(lead=>{
              const isSel=selectedItem?.id===lead.id;
              return(
                <div key={lead.id} onClick={()=>{setSelectedItem(lead);setComposing(false);}}
                  onContextMenu={e=>{e.preventDefault();setLeadCtxMenu({x:e.clientX,y:e.clientY,lead});}}
                  className="ow-item"
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{lead.name}</span>
                    <StatusBadge status={lead.leadStatus||"New"}/>
                  </div>
                  <p style={{fontSize:12,color:C.textSub,marginBottom:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.title}{lead.company?"  "+lead.company:""}</p>
                  <p style={{fontSize:11,color:C.textMuted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{lead.email}</p>
                </div>
              );
            })}

            {/* Campaigns */}
            {isCamp&&campaigns.map(c=>{
              const isSel=selectedItem?.id===c.id;
              const statusBucket=c.status==="Active"?"active":c.status==="Paused"?"paused":c.status==="Completed"?"completed":"draft";
              return(
                <div key={c.id} onClick={()=>{setSelectedItem(c);setComposing(false);}}
                  onContextMenu={e=>{e.preventDefault();setCampaignCtxMenu({x:e.clientX,y:e.clientY,campaign:c});}}
                  className="ow-item"
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{c.name}</span>
                    <StatusChip sm bucket={statusBucket} label={c.status}/>
                  </div>
                  <p style={{fontSize:12,color:C.textSub,marginBottom:1}}> {c.totalContacts||0} contacts  Sent: {c.sent||0}</p>
                  <p style={{fontSize:11,color:C.textMuted}}>Opens: {c.opened||0}  Replies: {c.replied||0}  Bounced: {c.bounced||0}</p>
                </div>
              );
            })}

            {/* Sequences */}
            {isSeq&&sequences.map(s=>{
              const isSel=selectedItem?.id===s.id;
              return(
                <div key={s.id} onClick={()=>{setSelectedItem(s);setComposing(false);}}
                  onContextMenu={e=>{e.preventDefault();setSeqCtxMenu({x:e.clientX,y:e.clientY,seq:s});}}
                  className="ow-item"
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                    <span style={{fontSize:10,color:C.accent,fontWeight:600}}>{s.steps?.length||7} steps</span>
                  </div>
                  <p style={{fontSize:12,color:C.textSub,marginBottom:1}}> {s.contactFileName||"No file"}</p>
                  <p style={{fontSize:11,color:C.textMuted}}>From: {s.fromEmail||""}  {s.totalContacts||0} contacts</p>
                </div>
              );
            })}

            {/* Opportunities */}
            {isOpp&&(
              <>
                <div style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder}}>
                  <button onClick={()=>setShowNewOpp(!showNewOpp)} style={{width:"100%",background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"6px 12px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}>+ New Opportunity</button>
                  {showNewOpp&&(
                    <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:5}}>
                      {[{ph:"Opportunity Name *",field:"name"},{ph:"Company",field:"company"},{ph:"Value ($)",field:"value"},{ph:"Probability (%)",field:"probability"}].map(f=>(
                        <input key={f.field} placeholder={f.ph} value={newOpp[f.field]} onChange={e=>setNewOpp(p=>({...p,[f.field]:e.target.value}))}
                          style={{border:"1px solid "+C.border,borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none"}}/>
                      ))}
                      <select value={newOpp.stage} onChange={e=>setNewOpp(p=>({...p,stage:e.target.value}))}
                        style={{border:"1px solid "+C.border,borderRadius:3,padding:"5px 8px",fontSize:12,color:C.text,outline:"none"}}>
                        {OPPS_STAGES.map(s=><option key={s}>{s}</option>)}
                      </select>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={createOpp} style={{flex:1,background:C.green,color:"#fff",border:"none",borderRadius:3,padding:"5px",fontSize:12,cursor:"pointer"}}>Create</button>
                        <button onClick={()=>setShowNewOpp(false)} style={{flex:1,background:"#f3f2f1",color:C.text,border:"1px solid "+C.border,borderRadius:3,padding:"5px",fontSize:12,cursor:"pointer"}}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                {listItems.map(o=>{
                  const isSel=selectedItem?.id===o.id;
                  const stageColor={Discovery:C.accent,Qualification:C.purple,Proposal:C.amber,Negotiation:C.gold,Won:C.green,Lost:C.red}[o.stage]||C.text;
                  return(
                    <div key={o.id} onClick={()=>{setSelectedItem(o);setComposing(false);}}
                      className="ow-item"
                      style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{o.name}</span>
                        <span style={{fontSize:10,color:stageColor,fontWeight:700,flexShrink:0}}>{o.stage}</span>
                      </div>
                      <p style={{fontSize:12,color:C.textSub}}>{o.company}  ${o.value||0}</p>
                      <p style={{fontSize:11,color:C.textMuted}}>Probability: {o.probability||0}%</p>
                    </div>
                  );
                })}
              </>
            )}

            {/* Contact Lists */}
            {activeFolder==="contactlists"&&listItems.map(f=>{
              const isSel=selectedItem?.id===f.id;
              return(
                <div key={f.id} onClick={()=>{setSelectedItem(f);setComposing(false);}}
                  className="ow-item"
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}> {f.name}</span>
                    <Badge v={f.count} color={C.green}/>
                  </div>
                  <p style={{fontSize:11,color:C.textMuted}}>{new Date(f.importedAt).toLocaleDateString()}</p>
                </div>
              );
            })}

            {/* Templates — reads the same shared repository (server.js's
                /api/templates, see emailTemplates.js) that Mail Merge,
                Sequences, and the main app's Templates page all read/write.
                Creating/editing still stays in those richer editors; here
                a template can be used directly — "New from template" opens
                a fresh compose, and "Use in reply" (shown once a
                conversation is selected) drops it into a reply — instead of
                only being viewable as before. */}
            {activeFolder==="templates"&&listItems.map(t=>{
              const isSel=selectedItem?.id===t.id;
              const catLabel=TEMPLATE_CATEGORIES.find(c=>c.id===(t.category||"general"))?.label||"General";
              return(
                <div key={t.id} onClick={()=>{setSelectedItem(t);setComposing(false);}}
                  className="ow-item"
                  style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer",background:isSel?C.listSel:"transparent",borderLeft:isSel?"3px solid "+C.accent:"3px solid transparent"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3,gap:8}}>
                    <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</span>
                    <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",borderRadius:99,background:C.bgHover,color:C.textSub,whiteSpace:"nowrap"}}>{catLabel}</span>
                  </div>
                  <p style={{fontSize:12,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:6}}>{t.subject}</p>
                  <div style={{display:"flex",gap:6}} onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>applyTemplateInWorkspace(t,"new")} style={{fontSize:11,fontWeight:600,padding:"4px 9px",borderRadius:4,border:"none",background:C.accent,color:"#fff",cursor:"pointer"}}>New Email</button>
                  </div>
                </div>
              );
            })}
            {activeFolder==="templates"&&listItems.length===0&&(
              <div style={{padding:"24px 16px",textAlign:"center"}}>
                <p style={{fontSize:12,color:C.textMuted,marginBottom:8}}>No templates yet.</p>
                <p style={{fontSize:11,color:C.textMuted}}>Save one here, from the Templates page, or from Mail Merge's Mailings tab — it'll show up in this list automatically.</p>
              </div>
            )}

            {/* Sender accounts */}
            {activeFolder==="senderaccounts"&&listItems.map(s=>(
              <div key={s.id} className="ow-item"
                style={{padding:"8px 12px",borderBottom:"1px solid "+C.listBorder,cursor:"pointer"}}>
                <p style={{fontSize:13,fontWeight:600,color:C.text}}>{s.name}</p>
                <p style={{fontSize:12,color:C.textSub}}>{s.email}</p>
                <p style={{fontSize:11,color:C.textMuted}}>{s.role}</p>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* List pane <-> Reading pane splitter — orientation follows the
            same Right/Bottom choice the panes themselves already follow,
            so dragging always moves the divider that's actually on screen. */}
        {showReadPane&&!isMobile&&(
          <Splitter orientation={readingPanePos==="bottom"?"horizontal":"vertical"}
            onDrag={delta=>{
              if(readingPanePos==="bottom"){setListPaneHeight(h=>Math.max(120,Math.min(700,h+delta)));}
              else{setListPaneWidth(w=>Math.max(220,Math.min(700,w+delta)));}
            }}/>
        )}

        {/*  Reading / Compose pane  */}
        {showReadPane&&(!isMobile||mobileView==="reading")&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:C.reading,minWidth:0,minHeight:0,position:"relative"}}>
            {isMobile&&(
              <button onClick={()=>{setMobileView("list");setSelectedItem(null);setComposing(false);}}
                style={{position:"absolute",top:8,left:8,zIndex:50,background:"#fff",border:"1px solid "+C.border,borderRadius:20,width:34,height:34,display:"flex",alignItems:"center",justifyContent:"center",color:C.accent,fontSize:18,cursor:"pointer",boxShadow:"0 1px 4px rgba(0,0,0,.15)"}}>&#8592;</button>
            )}

            {/*  Compose  */}
            {composing&&(
              // overflow:"auto" here (not "hidden") is the actual fix, not
              // just minHeight:0 above. minHeight:0 alone only raises the
              // threshold: the editor below still carries an explicit
              // minHeight:280 floor, so on a browser window short enough
              // that header+fields+merge-tag bar+attach row+ribbon toolbar
              // plus that 280px floor still exceed the available height,
              // the column would keep overflowing its parent regardless —
              // and with overflow:"hidden" that excess was simply
              // unreachable, no scrollbar, exactly the bug reported. "auto"
              // guarantees the whole compose pane can always be scrolled
              // into view no matter how short the window is, while still
              // looking and behaving completely unchanged whenever there's
              // enough room (the normal case) — the editor still expands
              // to fill the leftover flex space with no scrollbar showing.
              <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"auto",minHeight:0}}>
                <div style={{padding:"8px 16px",borderBottom:"1px solid "+C.border,background:"#f8f7f6",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                  <span style={{fontSize:14,fontWeight:600,color:C.text}}>{replyMode==="forward"?"Forward":replyMode?"Reply":"New Message"}</span>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>addToast("Draft saved","info")} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",color:C.text}}> Save</button>
                    <button onClick={()=>setComposing(false)} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",color:C.red}}> Discard</button>
                  </div>
                </div>
                <div style={{borderBottom:"1px solid "+C.border,flexShrink:0}}>
                  {[
                    {l:"From",f:"from",type:"select"},
                    {l:"To",f:"to",ph:"recipient@company.com; separate multiple with ; or ,",showCcBccLink:true},
                    ...(showCcBcc?[{l:"Cc",f:"cc",ph:"cc@company.com; separate multiple with ; or ,"},{l:"Bcc",f:"bcc",ph:"bcc@company.com; separate multiple with ; or ,"}]:[]),
                    {l:"Subject",f:"subject",ph:"Subject",bold:true},
                  ].map(row=>{
                    const isRecipientField=row.f==="to"||row.f==="cc"||row.f==="bcc";
                    return(
                    <div key={row.f} style={{display:"flex",alignItems:"center",borderBottom:"1px solid #f0efee",minHeight:30,position:"relative"}}>
                      <button style={{minWidth:58,padding:"6px 10px",border:"none",background:"transparent",color:C.textSub,fontSize:12,fontWeight:600,fontFamily:"'Segoe UI',sans-serif",cursor:"pointer",flexShrink:0}}>{row.l}</button>
                      {row.type==="select"?(
                        <select value={compose.from} onChange={e=>setCompose(p=>({...p,from:e.target.value}))}
                          style={{border:"none",background:"transparent",padding:"5px 6px",flex:1,fontSize:13,color:C.text,outline:"none",fontFamily:"'Segoe UI',sans-serif"}}>
                          {senders.map(s=><option key={s.id} value={s.email}>{s.name} ({s.email})</option>)}
                          {!senders.length&&<option value={settings.senderEmail||""}>{settings.sdrName} ({settings.senderEmail})</option>}
                        </select>
                      ):(
                        <input ref={row.f==="subject"?subjectRef:undefined}
                          value={compose[row.f]||""}
                          onChange={e=>isRecipientField?handleRecipientChange(row.f,e.target.value):setCompose(p=>({...p,[row.f]:e.target.value}))}
                          onKeyDown={isRecipientField?e=>handleRecipientKeyDown(row.f,e):undefined}
                          onBlur={isRecipientField?()=>setTimeout(()=>setRcptSuggest(s=>s&&s.field===row.f?null:s),150):undefined}
                          placeholder={row.ph} onFocus={()=>row.f==="subject"?setFoc("subject"):undefined} onClick={()=>row.f==="subject"?setFoc("subject"):undefined}
                          style={{border:"none",background:"transparent",padding:"5px 6px",flex:1,fontSize:row.bold?14:13,fontWeight:row.bold?500:400,color:C.text,outline:"none",fontFamily:"'Segoe UI',sans-serif"}}/>
                      )}
                      {/* Cc/Bcc start hidden (Gmail's convention) — this
                          link on the To row is the only way to reveal them
                          when they're not already showing. Once shown they
                          stay shown for the rest of this compose session;
                          there's no need to hide them again mid-draft. */}
                      {row.showCcBccLink&&!showCcBcc&&(
                        <button onClick={()=>setShowCcBcc(true)}
                          style={{background:"none",border:"none",color:C.accent,fontSize:11,fontWeight:600,cursor:"pointer",padding:"4px 12px",flexShrink:0,fontFamily:"'Segoe UI',sans-serif"}}>
                          Cc Bcc
                        </button>
                      )}
                      {/* Outlook-style contact suggestions — filtered from
                          this tenant's leads by name/email as you type the
                          current (last) recipient token. Mousedown (not
                          click) so it fires before the input's onBlur. */}
                      {isRecipientField&&rcptSuggest&&rcptSuggest.field===row.f&&rcptSuggest.items.length>0&&(
                        <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid "+C.border,borderRadius:4,boxShadow:"0 6px 16px rgba(0,0,0,.15)",zIndex:60,maxHeight:220,overflowY:"auto"}}>
                          {rcptSuggest.items.map((m,i)=>(
                            <div key={m.id||m.email}
                              onMouseDown={e=>{e.preventDefault();insertRecipient(row.f,m.email);}}
                              onMouseEnter={()=>setRcptSuggest(s=>s?{...s,activeIdx:i}:s)}
                              style={{padding:"6px 10px",cursor:"pointer",background:i===rcptSuggest.activeIdx?C.accentL:"#fff",display:"flex",flexDirection:"column",gap:1}}>
                              <span style={{fontSize:12.5,color:C.text,fontWeight:500}}>{m.name||m.email}</span>
                              {m.name&&<span style={{fontSize:11,color:C.textMuted}}>{m.email}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                {/* Insert Field — was a permanently-open row of 8 pill
                    buttons (one per merge tag) crowding this bar on every
                    single compose, whether or not anyone needed them right
                    then. Same tags, same insertMergeTag() call, now behind
                    one clean dropdown — consistent with Mail Merge's own
                    "Insert Merge Field" control (RibbonDropdownButton,
                    RibbonPrimitives.js), which this reuses directly. */}
                <div style={{padding:"4px 10px",borderBottom:"1px solid "+C.border,background:"#f8f7f6",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flexShrink:0}}>
                  <RibbonDropdownButton minWidth={190}
                    trigger={({toggle,open})=>(
                      <button onMouseDown={e=>e.preventDefault()} onClick={toggle}
                        style={{display:"flex",alignItems:"center",gap:5,background:open?C.accentL:"#fff",color:C.accent,border:"1px solid "+(open?C.accent:C.border),borderRadius:3,padding:"3px 10px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}>
                        Insert Field <span style={{fontSize:8}}>&#9662;</span>
                      </button>
                    )}>
                    {(close)=>(<>
                      {MERGE_TAGS.map(tag=>(
                        <button key={tag} onMouseDown={e=>{e.preventDefault();insertMergeTag(tag);close();}}
                          style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,width:"100%",textAlign:"left",background:"none",border:"none",padding:"6px 10px",fontSize:12,color:C.text,cursor:"pointer",borderRadius:WR.sm,fontFamily:"'Segoe UI',sans-serif"}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.ribbonHover} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <span>{MERGE_TAG_LABELS[tag]||tag}</span>
                          <span style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{tag}</span>
                        </button>
                      ))}
                    </>)}
                  </RibbonDropdownButton>
                  <span style={{fontSize:10,color:C.textMuted}}>Inserts into <strong style={{color:lastFocused==="body"?C.green:C.accent}}>{lastFocused==="body"?"email body":"subject line"}</strong></span>
                  <button onClick={aiDraftPane} disabled={aiLoading}
                    style={{marginLeft:"auto",background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"'Segoe UI',sans-serif",flexShrink:0}}>
                    {aiLoading?<><Sp size={10} color="#fff"/> Generating...</>:" AI Draft"}
                  </button>
                  <button onClick={voice.toggleListening} disabled={!voice.supported}
                    title={!voice.supported?"Voice dictation needs Chrome or Edge":(voice.listening?"Stop voice dictation":"Dictate this email by voice")}
                    style={{background:voice.listening?"#d13438":"#fff",color:voice.listening?"#fff":C.text,border:"1px solid "+(voice.listening?"#d13438":C.border),borderRadius:3,padding:"3px 10px",fontSize:11,cursor:voice.supported?"pointer":"not-allowed",display:"flex",alignItems:"center",gap:4,fontFamily:"'Segoe UI',sans-serif",flexShrink:0,opacity:voice.supported?1:0.5}}>
                    {voice.listening?<><span style={{width:7,height:7,borderRadius:"50%",background:"#fff",display:"inline-block",animation:"owspin 1s linear infinite"}}/> {voice.busy?"Updating…":"Listening"}</>:<>&#127908; Voice</>}
                  </button>
                </div>
                {voice.listening&&voice.interimText&&(
                  <div style={{padding:"4px 16px",borderBottom:"1px solid "+C.border,background:"#fff9e6",fontSize:11,color:C.textSub,fontStyle:"italic",flexShrink:0}}>
                    {voice.interimText}
                  </div>
                )}
                {showVoiceGuard&&(
                  <div style={{padding:"8px 16px",borderBottom:"1px solid "+C.border,background:"#fff4e0",display:"flex",alignItems:"flex-start",gap:8,flexShrink:0}}>
                    <span style={{fontSize:13,flexShrink:0}}>⚠</span>
                    <div style={{flex:1,fontSize:11,color:C.text}}>
                      <strong>This draft reads differently from your usual style:</strong>
                      <ul style={{margin:"4px 0 0",paddingLeft:16}}>
                        {voiceGuardFlags.map((f,i)=><li key={i} style={{marginBottom:2}}>{f}</li>)}
                      </ul>
                    </div>
                    <button onClick={()=>setVoiceGuardDismissed(voiceGuardKey)} aria-label="Dismiss writing voice hint"
                      style={{background:"none",border:"none",cursor:"pointer",color:C.textSub,fontSize:14,flexShrink:0,lineHeight:1,padding:2}}>&times;</button>
                  </div>
                )}
                <div style={{padding:"4px 10px",borderBottom:"1px solid "+C.border,background:"#fff",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",flexShrink:0}}>
                  <input ref={fileInputRef} type="file" multiple style={{display:"none"}} onChange={e=>{handleAttachFiles(e.target.files);e.target.value="";}}/>
                  <button onClick={()=>fileInputRef.current?.click()} style={{background:"#f3f2f1",border:"1px solid "+C.border,borderRadius:3,padding:"3px 10px",fontSize:11,cursor:"pointer",color:C.text,fontFamily:"'Segoe UI',sans-serif"}}> Attach File</button>
                  {(compose.attachments||[]).map((a,idx)=>(
                    <span key={idx} style={{background:C.accentL,color:C.accent,borderRadius:3,padding:"3px 8px",fontSize:11,display:"flex",alignItems:"center",gap:5}}>
                      {a.filename} <span style={{color:C.textMuted}}>({(a.size/1024).toFixed(0)}KB)</span>
                      <button onClick={()=>removeAttachment(idx)} aria-label={"Remove attachment"+(a?.filename?": "+a.filename:"")} style={{background:"none",border:"none",cursor:"pointer",color:C.red,fontSize:12,padding:0}}>&times;</button>
                    </span>
                  ))}
                </div>
                {/* Previously this row's only formatting control was a
                    manual point-size input — Quill's native toolbar (the
                    actual Bold/Italic/font-family/etc. controls) was hidden
                    via CSS and never replaced, so composing here had no
                    visible formatting UI at all beyond keyboard shortcuts.
                    The shared WordRibbon now provides the full Word-parity
                    set (the free-typed-size box included, so the old manual
                    "Set pt" input is redundant and removed). */}
                <div style={{background:"#F8F7F6",borderBottom:"1px solid "+C.border,padding:"2px 4px",overflowX:"auto",flexShrink:0}}>
                  <WordRibbon getQuill={getQuill} showStyles formatMarksOn={formatMarksOn} onToggleFormatMarks={()=>setFormatMarksOn(v=>!v)} addToast={addToast} docFilename="email-message" onFindReplace={()=>setShowFindReplace(true)}/>
                </div>
                <div style={{flex:1,overflow:"auto",display:"flex",flexDirection:"column",minHeight:280}}>
                  <div onClickCapture={()=>setFoc("body")} onMouseDownCapture={()=>setFoc("body")} style={{flex:1,display:"flex",flexDirection:"column"}}>
                    <RichTextEditor ref={quillRef} value={compose.body||"<p><br></p>"} onChange={val=>setCompose(p=>({...p,body:val}))} formatMarksOn={formatMarksOn} placeholder="Write your message here..." style={{flex:1}}/>
                  </div>
                  {/* Signature preview + quoted "Original conversation"
                      thread — previously always dumped as plain text right
                      under the editor (a wall of the sender's own name/
                      company/email followed by a raw copy of the original
                      message). Neither is actually part of compose.body —
                      sendEmail() posts compose.body as-is and never
                      concatenates sig or the quoted thread onto it — so
                      collapsing this behind a toggle changes nothing about
                      what gets sent, only what's forced into view while
                      writing. Collapsed by default (Gmail's "•••" convention
                      for trimmed quoted content); one click reveals both. */}
                  {!showQuoted?(
                    <button onClick={()=>setShowQuoted(true)} title="Show signature and quoted conversation"
                      style={{alignSelf:"flex-start",display:"flex",alignItems:"center",gap:4,margin:"6px 16px 14px",padding:"4px 12px",background:"#f3f2f1",border:"1px solid "+C.border,borderRadius:14,fontSize:13,color:C.textSub,cursor:"pointer",letterSpacing:1,lineHeight:1}}>
                      &bull;&bull;&bull;
                    </button>
                  ):(
                    <div style={{padding:"6px 16px",borderTop:"1px solid "+C.border,fontSize:12,color:C.textSub}} dangerouslySetInnerHTML={{__html:sanitizeEmailHtml(sig)}}/>
                  )}
                </div>
                {showQuoted&&replyMode&&selectedItem?.messages?.length>0&&(
                  <div style={{borderTop:"1px solid "+C.border,maxHeight:160,overflowY:"auto",background:"#f8f7f6",flexShrink:0}}>
                    <p style={{fontSize:10,color:C.textSub,padding:"6px 16px 2px",textTransform:"uppercase",letterSpacing:".5px"}}>Original conversation</p>
                    {selectedItem.messages.map(msg=>(
                      <div key={msg.id} style={{padding:"6px 16px",borderBottom:"1px solid "+C.listBorder}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                          <span style={{fontSize:12,fontWeight:600,color:C.text}}>{msg.fromName||settings.sdrName}</span>
                          <span style={{fontSize:10,color:C.textMuted}}>{msg.sentAt?new Date(msg.sentAt).toLocaleString():""}</span>
                        </div>
                        <div style={{fontSize:12,color:C.textSub}} dangerouslySetInnerHTML={{__html:sanitizeEmailHtml((msg.body||"").slice(0,200))+"..."}}/>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/*  Reading pane  Email thread  */}
            {!composing&&selectedItem&&isMail&&selectedItem.messages&&(
              <div style={{flex:1,display:"flex",overflow:"hidden",minHeight:0}}>
              <div style={{flex:1,overflowY:"auto",minWidth:0}}>
                <div style={{padding:"14px 20px",borderBottom:"1px solid "+C.border,background:"#f8f7f6",flexShrink:0}}>
                  <p style={{fontSize:18,fontWeight:700,color:C.text,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                    {selectedItem.messages?.[0]?.importance==="high"&&<span title="High importance" style={{color:C.red,fontSize:20}}>!</span>}
                    {selectedItem.messages?.[0]?.importance==="low"&&<span title="Low importance" style={{color:C.accent,display:"inline-flex",transform:"rotate(90deg)"}}><RIcon name="chevronRight" size={13}/></span>}
                    {selectedItem.subject}
                  </p>
                  <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:12,color:C.textSub}}>To: {selectedItem.to}</span>
                    <span style={{fontSize:12,color:C.textSub}}> {selectedItem.messages.length} messages</span>
                    {selectedItem.messages?.[0]?.readReceiptRequested&&<span style={{fontSize:11,color:C.textMuted,background:"#f3f2f1",borderRadius:3,padding:"2px 6px"}}>Read receipt requested</span>}
                    <div style={{flex:1}}/>
                    <button onClick={()=>doReply(selectedItem.messages[0],"reply")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:3,padding:"5px 14px",fontSize:12,cursor:"pointer",fontFamily:"'Segoe UI',sans-serif"}}> Reply</button>
                    <button onClick={()=>doReply(selectedItem.messages[0],"replyAll")} style={{background:"#f3f2f1",color:C.text,border:"1px solid "+C.border,borderRadius:3,padding:"5px 14px",fontSize:12,cursor:"pointer"}}> Reply All</button>
                    <button onClick={()=>doReply(selectedItem.messages[0],"forward")} style={{background:"#f3f2f1",color:C.text,border:"1px solid "+C.border,borderRadius:3,padding:"5px 14px",fontSize:12,cursor:"pointer"}}> Forward</button>
                    {/* Was its own direct fetch+PATCH here — a fourth,
                        independent move implementation alongside the
                        ribbon's Move dropdown, drag-drop, and the (now-
                        merged) Organize group, offering only 4 of the 8
                        folders those other three do and skipping the
                        Undo-toast/unread-count-refresh handling moveEmailTo
                        already does for everyone else. Now calls the same
                        moveEmailTo() the ribbon uses, with the same full
                        folder list. */}
                    <select onChange={e=>{if(!e.target.value)return;moveEmailTo(selectedItem,selectedItem.messages[0],e.target.value);e.target.value="";}}
                      style={{border:"1px solid "+C.border,borderRadius:3,padding:"5px 6px",fontSize:12,color:C.text,background:"#fff"}} defaultValue="">
                      <option value="" disabled>Move to...</option>
                      {[{id:"inbox",label:"Inbox"},{id:"focused",label:"Focused"},{id:"sent",label:"Sent Items"},{id:"drafts",label:"Drafts"},{id:"deleted",label:"Deleted Items"},{id:"archive",label:"Archive"},{id:"junk",label:"Junk Mail"}].map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                    </select>
                    {/* Was its own direct fetch+DELETE, bypassing
                        deleteEmail() (which already handles Undo and the
                        same soft-delete-to-trash semantics) — same class of
                        duplication as the select above, fixed the same way. */}
                    <button onClick={()=>deleteEmail(selectedItem,selectedItem.messages[0])} style={{background:C.redL,color:C.red,border:"1px solid "+C.red+"33",borderRadius:3,padding:"5px 10px",fontSize:12,cursor:"pointer"}}></button>
                  </div>
                </div>
                {selectedItem.messages.map((msg,i)=>{
                  const imagesUnblocked=unblockedImages.has(msg.id);
                  const{safeHtml,hadBlocked}=imagesUnblocked?{safeHtml:msg.body||"",hadBlocked:false}:blockRemoteImages(msg.body||"");
                  return(
                  <div key={msg.id} style={{borderBottom:"1px solid "+C.listBorder}}>
                    <div style={{padding:"10px 20px",display:"flex",alignItems:"center",gap:10,background:i===0?C.reading:"#f8f7f6"}}>
                      <div style={{width:36,height:36,borderRadius:"50%",background:i===0?C.accent:"#8a8886",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14,fontWeight:700,flexShrink:0}}>
                        {(msg.fromName||settings.sdrName||"?").slice(0,1).toUpperCase()}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                          <span style={{fontSize:13,fontWeight:600,color:C.text}}>{msg.fromName||settings.sdrName}</span>
                          {i===0&&<span style={{fontSize:10,background:C.greenL,color:C.green,padding:"1px 6px",borderRadius:3,fontWeight:500}}>Latest</span>}
                          {msg.status==="draft"&&<span style={{fontSize:10,background:C.amberL,color:C.amber,padding:"1px 6px",borderRadius:3}}>Draft</span>}
                          <span style={{fontSize:11,color:C.textMuted,marginLeft:"auto"}}>{msg.sentAt?new Date(msg.sentAt).toLocaleString():""}</span>
                        </div>
                        <p style={{fontSize:12,color:C.textSub}}>To: {msg.to}{msg.cc?"  Cc: "+msg.cc:""}</p>
                      </div>
                      <div style={{display:"flex",gap:4,flexShrink:0}}>
                        <button onClick={()=>doReply(msg,"reply")} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer"}}></button>
                        <button onClick={()=>doReply(msg,"replyAll")} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer"}}></button>
                        <button onClick={()=>doReply(msg,"forward")} style={{background:"none",border:"1px solid "+C.border,borderRadius:3,padding:"3px 8px",fontSize:11,cursor:"pointer"}}></button>
                      </div>
                    </div>
                    {msg.importance==="high"&&(
                      <NotificationBar tone="warning" text="This message was sent with High importance."/>
                    )}
                    {hadBlocked&&(
                      <NotificationBar tone="warning" text="Some pictures have been blocked for your safety."
                        action={{label:"Download Pictures",onClick:()=>setUnblockedImages(prev=>new Set(prev).add(msg.id))}}/>
                    )}
                    <div style={{padding:"8px 20px 16px 66px",fontSize:14,color:C.text,lineHeight:1.7,background:i===0?C.reading:"#f8f7f6",fontFamily:"Calibri,sans-serif"}} dangerouslySetInnerHTML={{__html:safeHtml}}/>
                    {msg.attachments?.length>0&&(
                      <div style={{padding:"0 20px 14px 66px",display:"flex",gap:8,flexWrap:"wrap",background:i===0?C.reading:"#f8f7f6"}}>
                        {msg.attachments.map((a,ai)=>{
                          const attUrl="/api/email/"+msg.id+"/attachment/"+ai;
                          const previewable=isPreviewableAttachment(a);
                          const kind=getAttachmentKind(a);
                          const visual=ATTACHMENT_VISUALS[kind]||ATTACHMENT_VISUALS.generic;
                          return(
                          <div key={ai} style={{display:"flex",alignItems:"stretch",background:"#fff",border:"1px solid "+C.border,borderRadius:6,overflow:"hidden",boxShadow:"0 1px 2px rgba(0,0,0,.06)",minWidth:192,maxWidth:260}}>
                            <a href={attUrl} target="_blank" rel="noreferrer" title={"Open "+a.filename}
                              style={{padding:"7px 10px",textDecoration:"none",display:"flex",alignItems:"center",gap:9,flex:1,minWidth:0}}>
                              {kind==="image"?(
                                <img src={attUrl} alt="" style={{width:36,height:36,borderRadius:5,objectFit:"cover",flexShrink:0,border:"1px solid "+C.border,background:"#f3f2f1"}}
                                  onError={e=>{e.currentTarget.outerHTML='<div style="width:36px;height:36px;border-radius:5px;background:#605e5c;color:#fff;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700;flex-shrink:0">IMG</div>';}}/>
                              ):(
                                <div style={{width:36,height:36,borderRadius:5,background:visual.bg,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,letterSpacing:".2px",flexShrink:0}}>{visual.label}</div>
                              )}
                              <div style={{minWidth:0,flex:1}}>
                                <div style={{fontSize:12,color:C.text,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{a.filename}</div>
                                <div style={{fontSize:10.5,color:C.textMuted,marginTop:1}}>{((a.size||0)/1024).toFixed(0)} KB</div>
                              </div>
                            </a>
                            {previewable&&(
                              <button onClick={()=>setAttachmentPreview({url:attUrl,filename:a.filename,contentType:a.contentType})}
                                title={"Preview "+a.filename} aria-label={"Preview "+a.filename}
                                style={{background:"none",border:"none",borderLeft:"1px solid "+C.border,padding:"0 10px",alignSelf:"stretch",color:C.textSub,cursor:"pointer",display:"flex",alignItems:"center",flexShrink:0}}
                                onMouseEnter={e=>e.currentTarget.style.color=C.accent} onMouseLeave={e=>e.currentTarget.style.color=C.textSub}>
                                <RIcon name="search" size={13}/>
                              </button>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
              {/*  Contact context panel  collapsible, shown only when the
                  open conversation's recipient matches a known contact.
                  Reuses ContactDetailContent (same component the standalone
                  Lead Detail view uses) so the two never drift apart, and
                  refreshes that one contact's record on open below to guard
                  against staleness between this file's own `leads` copy and
                  ContactsPage.js's independent one. */}
              {mailContact&&(contactPanelOpen?(
                <div style={{width:280,flexShrink:0,borderLeft:"1px solid "+C.border,background:"#fff",overflowY:"auto",padding:"14px 16px",position:"relative"}}>
                  <button onClick={()=>setContactPanelOpen(false)} title="Collapse contact panel" aria-label="Collapse contact panel"
                    style={{position:"absolute",top:10,right:10,background:"none",border:"1px solid "+C.border,borderRadius:3,width:22,height:22,cursor:"pointer",color:C.textSub,fontSize:12,lineHeight:1,padding:0}}></button>
                  <ContactDetailContent contact={mailContact} note={note} setNote={setNote}
                    onAddNote={addNote} onStatusChange={updateLeadStatus}
                    onEmail={c=>{setCompose(p=>({...p,to:c.email,subject:"Quick question, "+c.first}));setComposing(true);}}
                    onOpportunity={c=>{setShowNewOpp(true);setNewOpp(p=>({...p,name:(c.company||"")+" Opportunity",company:c.company||""}));setActiveFolder("opportunities");}}
                    compact/>
                </div>
              ):(
                <button onClick={()=>setContactPanelOpen(true)} title={"Show contact details for "+mailContact.name} aria-label={"Show contact details for "+mailContact.name}
                  style={{width:26,flexShrink:0,borderLeft:"1px solid "+C.border,background:"#f8f7f6",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",writingMode:"vertical-rl",fontSize:11,color:C.textSub,fontFamily:"'Segoe UI',sans-serif",letterSpacing:".3px",padding:"10px 0"}}>
                  {mailContact.name}
                </button>
              ))}
              </div>
            )}

            {/*  Reading pane  Lead detail  */}
            {!composing&&selectedItem&&isLead&&(
              <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
                <ContactDetailContent contact={selectedItem} note={note} setNote={setNote}
                  onAddNote={addNote} onStatusChange={updateLeadStatus}
                  onEmail={c=>{setCompose(p=>({...p,to:c.email,subject:"Quick question, "+c.first}));setComposing(true);}}
                  onOpportunity={c=>{setShowNewOpp(true);setNewOpp(p=>({...p,name:(c.company||"")+" Opportunity",company:c.company||""}));setActiveFolder("opportunities");}}/>
              </div>
            )}

            {/*  Campaign detail  */}
            {!composing&&selectedItem&&isCamp&&(
              <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,paddingBottom:14,borderBottom:"1px solid "+C.border}}>
                  <div>
                    <p style={{fontSize:20,fontWeight:700,color:C.text,marginBottom:4}}>{selectedItem.name}</p>
                    <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                      {selectedItem.sequenceName&&<span style={{fontSize:12,color:C.textSub}}> {selectedItem.sequenceName}</span>}
                      {selectedItem.fromEmail&&<span style={{fontSize:12,color:C.textSub}}> {selectedItem.fromEmail}</span>}
                      <span style={{fontSize:12,color:C.textSub}}> {selectedItem.totalContacts||0} contacts</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    {selectedItem.status==="Draft"&&<button onClick={()=>campaignAction(selectedItem.id,"start")} style={{background:C.green,color:"#fff",border:"none",borderRadius:3,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}> Start</button>}
                    {selectedItem.status==="Active"&&<button onClick={()=>campaignAction(selectedItem.id,"pause")} style={{background:C.amberL,color:C.amber,border:"1px solid "+C.amber+"44",borderRadius:3,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}> Pause</button>}
                    {selectedItem.status==="Paused"&&<button onClick={()=>campaignAction(selectedItem.id,"resume")} style={{background:C.greenL,color:C.green,border:"1px solid "+C.green+"44",borderRadius:3,padding:"7px 16px",fontSize:12,cursor:"pointer",fontWeight:600}}> Resume</button>}
                    <button onClick={()=>deleteCampaign(selectedItem.id)} style={{background:C.redL,color:C.red,border:"1px solid "+C.red+"33",borderRadius:3,padding:"7px 12px",fontSize:12,cursor:"pointer"}}> Delete</button>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:16}}>
                  {[{l:"Total",v:selectedItem.totalContacts||0,c:C.accent},{l:"Sent",v:selectedItem.sent||0,c:C.accent},{l:"Delivered",v:selectedItem.delivered||0,c:C.green},{l:"Opens",v:selectedItem.opened||0,c:C.amber},{l:"Clicks",v:selectedItem.clicked||0,c:C.purple},{l:"Replies",v:selectedItem.replied||0,c:C.green},{l:"Bounced",v:selectedItem.bounced||0,c:C.red},{l:"Unsub",v:selectedItem.unsubscribed||0,c:C.textSub}].map(s=>(
                    <div key={s.l} style={{background:"#f8f7f6",border:"1px solid "+C.border,borderRadius:3,padding:"10px 12px",textAlign:"center"}}>
                      <p style={{fontSize:20,fontWeight:700,color:s.c,fontFamily:"monospace",lineHeight:1}}>{s.v}</p>
                      <p style={{fontSize:10,color:C.textSub,marginTop:3}}>{s.l}</p>
                    </div>
                  ))}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[{l:"Open Rate",v:selectedItem.sent?((selectedItem.opened||0)/selectedItem.sent*100).toFixed(1)+"%":"0%",c:C.amber},{l:"Click Rate",v:selectedItem.sent?((selectedItem.clicked||0)/selectedItem.sent*100).toFixed(1)+"%":"0%",c:C.purple},{l:"Reply Rate",v:selectedItem.opened?((selectedItem.replied||0)/selectedItem.opened*100).toFixed(1)+"%":"0%",c:C.green},{l:"Bounce Rate",v:selectedItem.sent?((selectedItem.bounced||0)/selectedItem.sent*100).toFixed(1)+"%":"0%",c:C.red}].map(s=>(
                    <div key={s.l} style={{background:"#f8f7f6",border:"1px solid "+C.border,borderRadius:3,padding:"12px 16px",textAlign:"center"}}>
                      <p style={{fontSize:24,fontWeight:700,color:s.c,fontFamily:"monospace"}}>{s.v}</p>
                      <p style={{fontSize:11,color:C.textSub,marginTop:2}}>{s.l}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/*  Empty state  */}
            {!composing&&!selectedItem&&(
              <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:10,background:"#f8f7f6"}}>
                <p style={{fontSize:56,color:C.textMuted}}></p>
                <p style={{fontSize:16,color:C.textSub,fontFamily:"'Segoe UI',sans-serif"}}>Select a message to read</p>
                <p style={{fontSize:13,color:C.textMuted}}>Nothing is selected</p>

              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/*  Queue Panel  */}
      {showQueue&&<QueuePanel queue={queue} onClear={clearQueue} onRemove={removeFromQueue} onClose={()=>setShowQueue(false)}/>}

      {/*  Status Bar  */}
      <div style={{background:effectiveOffline?"#fde7e9":isSyncing?"#fff4ce":"#f3f2f1",borderTop:"1px solid "+(effectiveOffline?"#a4262c44":isSyncing?"#d83b0144":C.border),padding:"3px 14px",display:"flex",alignItems:"center",gap:16,flexShrink:0,transition:"all .3s"}}>
        <span style={{fontSize:11,color:C.textSub}}>Items: {listItems.length}</span>
        {multiSel.length>0&&<span style={{fontSize:11,color:C.accent,fontWeight:600}}>{multiSel.length} selected</span>}
        <span style={{fontSize:11,color:C.textSub}}>Leads: {leads.length}</span>
        <span style={{fontSize:11,color:C.textSub}}>Campaigns: {campaigns.length}</span>
        <div style={{flex:1}}/>
        <OfflineStatusBar
          effectiveOffline={effectiveOffline}
          browserOnline={browserOnline}
          isSyncing={isSyncing}
          queue={queue}
          onSync={syncQueue}
        />
        <div style={{display:"flex",gap:3}}>
          <button aria-label="Toggle reading pane" style={{background:"none",border:"none",fontSize:13,cursor:"pointer",color:C.textSub,padding:"0 2px",display:"flex",alignItems:"center"}} title="Reading pane" onClick={()=>setShowReadPane(!showReadPane)}><RIcon name="layoutRight" size={13}/></button>
          <button aria-label="Toggle folder pane" style={{background:"none",border:"none",fontSize:13,cursor:"pointer",color:C.textSub,padding:"0 2px",display:"flex",alignItems:"center"}} title="Toggle sidebar" onClick={()=>setShowSidebar(!showSidebar)}><RIcon name="layoutLeft" size={13}/></button>
        </div>
      </div>
      {folderCtxMenu&&(
        <ContextMenu open={!!folderCtxMenu} onClose={()=>setFolderCtxMenu(null)} x={folderCtxMenu.x} y={folderCtxMenu.y} width={190}>
          {[
            {label:"New Folder...",enabled:true,fn:()=>createFolderFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {label:"Rename Folder",enabled:!folderCtxMenu.canonical,fn:()=>renameFolderFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {label:"Copy Folder",enabled:false},
            {label:"Move Folder",enabled:false},
            {label:"Delete Folder",enabled:!folderCtxMenu.canonical,fn:()=>deleteFolderFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {divider:true},
            {label:"Mark All as Read",enabled:true,fn:()=>markAllReadFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {label:"Clean Up Folder",enabled:true,fn:()=>cleanUpFolderFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {label:"Delete All",enabled:true,fn:()=>emptyFolderFor(folderCtxMenu.accountEmail,folderCtxMenu.folderId)},
            {divider:true},
            {label:"Add to Favorites",enabled:false},
            {label:"Sort Subfolders A to Z",enabled:false},
            {label:"Move Up",enabled:false},
            {label:"Move Down",enabled:false},
            {divider:true},
            {label:"Properties...",enabled:false},
          ].map((item,i)=>item.divider
            ? <ContextMenuDivider key={i}/>
            : (
              <ContextMenuItem key={i} disabled={!item.enabled} onClick={()=>{if(item.fn){item.fn();}setFolderCtxMenu(null);}}>
                {item.label}
              </ContextMenuItem>
            )
          )}
        </ContextMenu>
      )}
      {emailCtxMenu&&(()=>{
        const {thread,msg}=emailCtxMenu;
        const close=()=>setEmailCtxMenu(null);
        const items=[
          ...(msg?.isDraft?[{label:"Edit Draft",fn:()=>resumeDraft(thread)},{divider:true}]:[]),
          {label:"Reply",fn:()=>{doReply(msg,"reply");}},
          {label:"Reply All",fn:()=>{doReply(msg,"replyAll");}},
          {label:"Forward",fn:()=>{doReply(msg,"forward");}},
          {divider:true},
          {label:"Move to Archive",fn:()=>moveEmailTo(thread,msg,"archive")},
          {label:"Move to Junk",fn:()=>moveEmailTo(thread,msg,"junk")},
          {submenu:true,label:"Junk",items:[
            {label:"Block Sender",fn:()=>junkBlockSender(thread,msg)},
            {label:"Never Block Sender",fn:()=>junkNeverBlockSender(thread,msg)},
            {label:"Never Block Sender's Domain",fn:()=>junkNeverBlockDomain(thread,msg)},
            {label:"Never Block this Group or Mailing List",disabled:true,title:"Mailing-list detection isn't available yet"},
            {divider:true},
            {label:"Not Junk",disabled:msg?.folder!=="junk",fn:()=>junkNotJunk(thread,msg)},
            {divider:true},
            {label:"Junk E-mail Options...",fn:()=>openRulesModal()},
          ]},
          {label:"Copy to Archive",fn:()=>copyEmailTo(msg,"archive")},
          {label:"Delete",fn:()=>deleteEmail(thread,msg)},
          {label:"Archive",fn:()=>moveEmailTo(thread,msg,"archive")},
          {divider:true},
          {label:"Ignore",fn:()=>ignoreThread(thread)},
          {label:"Clean Up Conversation",fn:()=>cleanUpConversation(thread)},
          {label:"Sweep...",fn:()=>sweepSender(thread,msg)},
          {divider:true},
          {label:msg?.isRead?"Mark as Unread":"Mark as Read",fn:()=>toggleReadFor(thread,msg)},
          {label:msg?.isFlagged?"Unflag":"Flag",fn:()=>toggleFlagFor(msg)},
          {divider:true},
          {label:"Open in New Window",fn:()=>openInNewWindow(thread,msg)},
          {label:"Download",fn:()=>downloadEmail(msg,thread.subject)},
          {label:"Print",fn:()=>printEmail(msg,thread.subject)},
          {label:"Copy Link",enabled:false},
          {divider:true},
          {label:"Properties...",fn:()=>setPropertiesModal({thread,msg})},
        ];
        return(
          <ContextMenu open={!!emailCtxMenu} onClose={close} x={emailCtxMenu.x} y={emailCtxMenu.y} width={190}>
            {items.map((item,i)=>item.divider
              ? <ContextMenuDivider key={i}/>
              : item.submenu
              ? <ContextSubMenu key={i} label={item.label} disabled={item.enabled===false} items={item.items} onDone={close}/>
              : (
                <ContextMenuItem key={i} disabled={item.enabled===false} onClick={()=>{if(item.fn)item.fn();close();}}>
                  {item.label}
                </ContextMenuItem>
              )
            )}
            <div style={{padding:"6px 14px 4px",fontSize:11,color:C.textSub}}>Categorize</div>
            <div style={{display:"flex",gap:6,padding:"0 14px 8px"}}>
              {Object.entries(CATEGORY_COLORS).map(([key,color])=>(
                <button key={key} title={key} aria-label={"Categorize as "+key} onClick={()=>{setCategoryFor(msg,key);close();}}
                  style={{width:18,height:18,borderRadius:"50%",background:color,border:msg?.category===key?"2px solid #333":"1px solid #00000022",cursor:"pointer",padding:0}}/>
              ))}
              <button title="Remove category" aria-label="Remove category" onClick={()=>{setCategoryFor(msg,null);close();}}
                style={{width:18,height:18,borderRadius:"50%",background:"#fff",border:"1px solid #00000044",cursor:"pointer",padding:0,fontSize:10,lineHeight:"16px",color:C.textSub}}>&times;</button>
            </div>
          </ContextMenu>
        );
      })()}
      {leadCtxMenu&&(()=>{
        const {lead}=leadCtxMenu;
        const close=()=>setLeadCtxMenu(null);
        const items=[
          {label:"Compose Email",fn:()=>{setSelectedItem(null);setCompose(p=>({...p,to:lead.email||""}));setComposing(true);}},
          {label:"Copy Email Address",fn:()=>{navigator.clipboard?.writeText(lead.email||"");addToast("Email address copied","success");}},
          {divider:true},
          {label:"Mark as Contacted",fn:()=>updateLeadStatus(lead.id,"Contacted")},
          {label:"Mark as Qualified",fn:()=>updateLeadStatus(lead.id,"Qualified")},
          {label:"Mark as New",fn:()=>updateLeadStatus(lead.id,"New")},
          {divider:true},
          {label:"View in Contacts",fn:()=>{setActiveFolder("contacts");setSelectedItem(lead);}},
        ];
        return(
          <ContextMenu open={!!leadCtxMenu} onClose={close} x={leadCtxMenu.x} y={leadCtxMenu.y} width={190}>
            {items.map((item,i)=>item.divider
              ? <ContextMenuDivider key={i}/>
              : (
                <ContextMenuItem key={i} disabled={item.enabled===false} onClick={()=>{if(item.fn)item.fn();close();}}>
                  {item.label}
                </ContextMenuItem>
              )
            )}
          </ContextMenu>
        );
      })()}
      {campaignCtxMenu&&(()=>{
        const {campaign}=campaignCtxMenu;
        const close=()=>setCampaignCtxMenu(null);
        const items=[
          ...(campaign.status==="Draft"?[{label:"Start",fn:()=>campaignAction(campaign.id,"start")}]:[]),
          ...(campaign.status==="Active"?[{label:"Pause",fn:()=>campaignAction(campaign.id,"pause")}]:[]),
          ...(campaign.status==="Paused"?[{label:"Resume",fn:()=>campaignAction(campaign.id,"resume")}]:[]),
          {divider:true},
          {label:"Open",fn:()=>{setSelectedItem(campaign);setComposing(false);}},
          {divider:true},
          {label:"Delete",fn:()=>deleteCampaign(campaign.id)},
        ];
        return(
          <ContextMenu open={!!campaignCtxMenu} onClose={close} x={campaignCtxMenu.x} y={campaignCtxMenu.y} width={170}>
            {items.map((item,i)=>item.divider
              ? <ContextMenuDivider key={i}/>
              : (
                <ContextMenuItem key={i} disabled={item.enabled===false} onClick={()=>{if(item.fn)item.fn();close();}}>
                  {item.label}
                </ContextMenuItem>
              )
            )}
          </ContextMenu>
        );
      })()}
      {seqCtxMenu&&(()=>{
        const {seq}=seqCtxMenu;
        const close=()=>setSeqCtxMenu(null);
        const items=[
          ...(seq.status==="Active"?[{label:"Pause",fn:()=>sequenceRowAction(seq.id,"pause")}]:[{label:seq.status==="Paused"?"Resume":"Start",fn:()=>sequenceRowAction(seq.id,seq.status==="Paused"?"resume":"start")}]),
          {label:"Duplicate",fn:()=>duplicateSequenceRow(seq.id)},
          {divider:true},
          // Was window.open("/sequences","_blank") — but "/sequences" isn't a
          // pathname App.js special-cases the way "/workspace"/"/mailmerge"
          // are, so that just opened a blank new tab that landed on whatever
          // page the app defaults to, never actually reaching Sequences.
          // Workspace is its own popup window (not the main app shell), so
          // jumping to a different tab means navigating THIS window to "/"
          // with a one-shot localStorage breadcrumb — the same convention
          // already used by the People icon (line ~4167) and
          // viewSequenceProgress (line ~697) for the identical situation.
          {label:"Open in Sequence Builder",fn:()=>{localStorage.setItem("sdr_page","sequences");window.location.href="/";}},
          {divider:true},
          {label:"Delete",fn:()=>deleteSequenceRow(seq.id)},
        ];
        return(
          <ContextMenu open={!!seqCtxMenu} onClose={close} x={seqCtxMenu.x} y={seqCtxMenu.y} width={190}>
            {items.map((item,i)=>item.divider
              ? <ContextMenuDivider key={i}/>
              : (
                <ContextMenuItem key={i} disabled={item.enabled===false} onClick={()=>{if(item.fn)item.fn();close();}}>
                  {item.label}
                </ContextMenuItem>
              )
            )}
          </ContextMenu>
        );
      })()}
      {propertiesModal&&(()=>{
        const {thread,msg}=propertiesModal;
        return(
          <Modal open={!!propertiesModal} onClose={()=>setPropertiesModal(null)} width={420} ariaLabel="Properties">
            <div style={{padding:20,fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
              <h3 style={{margin:"0 0 12px",fontSize:15}}>Properties</h3>
              {[["Subject",thread.subject],["From",`${msg?.fromName||""} <${msg?.from||""}>`],["To",msg?.to],["Cc",msg?.cc||"—"],["Folder",thread.folder],["Date",msg?.sentAt||msg?.receivedAt?new Date(msg.sentAt||msg.receivedAt).toLocaleString():"—"],["Read",msg?.isRead?"Yes":"No"],["Flagged",msg?.isFlagged?"Yes":"No"],["Category",msg?.category||"None"],["Message ID",msg?.messageId||"—"]].map(([k,v])=>(
                <div key={k} style={{display:"flex",padding:"4px 0",borderBottom:"1px solid "+C.border}}>
                  <span style={{width:100,color:C.textSub,flexShrink:0}}>{k}</span>
                  <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v}</span>
                </div>
              ))}
              <button onClick={()=>setPropertiesModal(null)} style={{marginTop:16,background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Close</button>
            </div>
          </Modal>
        );
      })()}

      {/* Attachment inline preview — images render directly, PDFs via
          <iframe> (browsers render these natively, no extra library
          needed); the existing download link is untouched, this is purely
          an additional way to look at a previewable attachment first. */}
      {attachmentPreview&&(
        <div onClick={()=>setAttachmentPreview(null)} style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,.55)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div ref={attachmentPreviewRef} tabIndex={-1} role="dialog" aria-label={"Preview: "+attachmentPreview.filename} onClick={e=>e.stopPropagation()}
            style={{background:"#fff",borderRadius:6,maxWidth:"90vw",maxHeight:"90vh",display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 20px 60px rgba(0,0,0,.4)",outline:"none"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,padding:"8px 8px 8px 14px",borderBottom:"1px solid "+C.border,flexShrink:0}}>
              <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",minWidth:120}}>{attachmentPreview.filename}</span>
              <a href={attachmentPreview.url} target="_blank" rel="noreferrer"
                style={{fontSize:12,color:C.accent,textDecoration:"none",fontWeight:600,border:"1px solid "+C.border,borderRadius:4,padding:"5px 12px",flexShrink:0}}>Download</a>
              <button onClick={()=>setAttachmentPreview(null)} aria-label="Close preview" title="Close"
                style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.textSub,padding:"0 8px",lineHeight:1,flexShrink:0}}>&times;</button>
            </div>
            <div style={{flex:1,overflow:"auto",background:"#3c3c3c",display:"flex",alignItems:"center",justifyContent:"center",minWidth:400,minHeight:300}}>
              {attachmentPreview.contentType.startsWith("image/")?(
                <img src={attachmentPreview.url} alt={attachmentPreview.filename} style={{maxWidth:"80vw",maxHeight:"80vh",objectFit:"contain"}}/>
              ):(
                <iframe title={attachmentPreview.filename} src={attachmentPreview.url} style={{width:"80vw",height:"80vh",border:"none",background:"#fff"}}/>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateFolderModal&&(
        <Modal open={!!showCreateFolderModal} onClose={()=>setShowCreateFolderModal(null)} width={340} ariaLabel={showCreateFolderModal.parentPath?`New subfolder in "${showCreateFolderModal.parentPath}"`:"New Folder"}>
          <div style={{padding:20,fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
            <h3 style={{margin:"0 0 12px",fontSize:15}}>{showCreateFolderModal.parentPath?`New subfolder in "${showCreateFolderModal.parentPath}"`:"New Folder"}</h3>
            <input autoFocus value={createFolderName} onChange={e=>{setCreateFolderName(e.target.value);setCreateFolderError("");}}
              onKeyDown={e=>{if(e.key==="Enter")submitCreateFolder();if(e.key==="Escape")setShowCreateFolderModal(null);}}
              placeholder="Folder name" style={{width:"100%",border:"1px solid "+(createFolderError?C.red:C.border),borderRadius:4,padding:"8px 10px",fontSize:13,outline:"none",boxSizing:"border-box",marginBottom:createFolderError?6:14}}/>
            {createFolderError&&<p style={{color:C.red,fontSize:12,margin:"0 0 12px",lineHeight:1.4}}>{createFolderError}</p>}
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setShowCreateFolderModal(null)} style={{background:"#f3f2f1",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={submitCreateFolder} disabled={!createFolderName.trim()||creatingFolder} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13,opacity:(!createFolderName.trim()||creatingFolder)?.5:1}}>{creatingFolder?"Creating...":"Create"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Rules engine (P3 #15) — list/manage view. A separate sibling
          overlay from the rule editor below (not nested), so each gets its
          own independent focus-trap/Escape via useModalA11y. */}
      {rulesModal&&(
        <Modal open={!!rulesModal} onClose={()=>setRulesModal(false)} width={560} ariaLabel="Rules">
          <div style={{padding:20,maxHeight:"80vh",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
            <div style={{display:"flex",alignItems:"center",marginBottom:8}}>
              <h3 style={{margin:0,fontSize:15,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>Rules — {compose.from}</h3>
              <button onClick={startNewRule} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:600,flexShrink:0}}>New Rule</button>
            </div>
            <p style={{margin:"0 0 12px",fontSize:12,color:C.textSub,lineHeight:1.4}}>Rules run in order, top to bottom — the first enabled rule that matches a message wins, and they apply automatically to new mail as it arrives.</p>
            <div style={{flex:1,overflowY:"auto",border:"1px solid "+C.border,borderRadius:4}}>
              {rulesLoading?(
                <div style={{padding:20,textAlign:"center",color:C.textSub}}>Loading…</div>
              ):rulesList.length===0?(
                <div style={{padding:20,textAlign:"center",color:C.textSub}}>No rules yet for this account.</div>
              ):rulesList.map((rule,i)=>(
                <div key={rule._id} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",borderBottom:i<rulesList.length-1?"1px solid "+C.border:"none",opacity:rule.enabled?1:.5}}>
                  <div style={{display:"flex",flexDirection:"column",gap:2,flexShrink:0}}>
                    <button onClick={()=>moveRuleOrder(rule,-1)} disabled={i===0} aria-label={"Move rule "+rule.name+" up"} title="Move up"
                      style={{background:"none",border:"none",cursor:i===0?"default":"pointer",color:i===0?"#ccc":C.textSub,fontSize:10,lineHeight:1,padding:0}}>&#9650;</button>
                    <button onClick={()=>moveRuleOrder(rule,1)} disabled={i===rulesList.length-1} aria-label={"Move rule "+rule.name+" down"} title="Move down"
                      style={{background:"none",border:"none",cursor:i===rulesList.length-1?"default":"pointer",color:i===rulesList.length-1?"#ccc":C.textSub,fontSize:10,lineHeight:1,padding:0}}>&#9660;</button>
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rule.name}</div>
                    <div style={{fontSize:11,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      If {rule.matchType==="any"?"any":"all"} of: {(rule.conditions||[]).map(c=>`${RULE_FIELD_LABELS[c.field]||c.field} ${RULE_OP_LABELS[c.op]||c.op} "${c.value}"`).join(rule.matchType==="any"?" OR ":" AND ")}
                      {" → "}{rule.action==="delete"?"Delete":`Move to ${rule.targetFolder}`}{rule.markAsRead?" + Mark as read":""}
                    </div>
                  </div>
                  <label style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:C.textSub,cursor:"pointer",flexShrink:0}}>
                    <input type="checkbox" checked={!!rule.enabled} onChange={()=>toggleRuleEnabled(rule)}/>On
                  </label>
                  <button onClick={()=>startEditRule(rule)} title="Edit rule" aria-label={"Edit rule "+rule.name}
                    style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 8px",cursor:"pointer",fontSize:11,color:C.text,flexShrink:0}}>Edit</button>
                  <button onClick={()=>deleteRule(rule)} title="Delete rule" aria-label={"Delete rule "+rule.name}
                    style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 8px",cursor:"pointer",fontSize:11,color:C.red,flexShrink:0}}>Delete</button>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:14}}>
              <button onClick={()=>setRulesModal(false)} style={{background:"#f3f2f1",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Rules engine — create/edit sub-view. */}
      {ruleEditor&&(
        <Modal open={!!ruleEditor} onClose={()=>setRuleEditor(null)} width={460} ariaLabel={ruleEditor.id?"Edit Rule":"New Rule"}>
          <div style={{padding:20,maxHeight:"85vh",overflowY:"auto",fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
            <h3 style={{margin:"0 0 12px",fontSize:15}}>{ruleEditor.id?"Edit Rule":"New Rule"}</h3>
            <label style={{display:"block",marginBottom:10}}>
              <div style={{fontSize:11,color:C.textSub,marginBottom:3}}>Rule name</div>
              <input autoFocus value={ruleEditor.name} onChange={e=>setRuleEditor(p=>({...p,name:e.target.value}))}
                placeholder="e.g. File newsletters to Archive"
                style={{width:"100%",border:"1px solid "+C.border,borderRadius:4,padding:"7px 9px",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
            </label>

            <div style={{fontSize:11,color:C.textSub,marginBottom:4,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span>Conditions</span>
              <label style={{display:"flex",alignItems:"center",gap:5,fontWeight:400,cursor:"pointer"}}>
                Match
                <select value={ruleEditor.matchType} onChange={e=>setRuleEditor(p=>({...p,matchType:e.target.value}))}
                  style={{fontSize:11,border:"1px solid "+C.border,borderRadius:3,padding:"2px 4px"}}>
                  <option value="all">ALL (AND)</option>
                  <option value="any">ANY (OR)</option>
                </select>
              </label>
            </div>
            {ruleEditor.conditions.map((c,i)=>(
              <div key={i} style={{display:"flex",gap:6,marginBottom:6,alignItems:"center"}}>
                <select value={c.field} onChange={e=>setRuleEditor(p=>({...p,conditions:p.conditions.map((cc,ci)=>ci===i?{...cc,field:e.target.value}:cc)}))}
                  style={{fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 4px"}}>
                  {Object.entries(RULE_FIELD_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                </select>
                <select value={c.op} onChange={e=>setRuleEditor(p=>({...p,conditions:p.conditions.map((cc,ci)=>ci===i?{...cc,op:e.target.value}:cc)}))}
                  style={{fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 4px"}}>
                  {Object.entries(RULE_OP_LABELS).map(([k,l])=><option key={k} value={k}>{l}</option>)}
                </select>
                <input value={c.value} onChange={e=>setRuleEditor(p=>({...p,conditions:p.conditions.map((cc,ci)=>ci===i?{...cc,value:e.target.value}:cc)}))}
                  placeholder="value" style={{flex:1,border:"1px solid "+C.border,borderRadius:4,padding:"6px 8px",fontSize:12,outline:"none",minWidth:0}}/>
                <button onClick={()=>setRuleEditor(p=>({...p,conditions:p.conditions.length>1?p.conditions.filter((_,ci)=>ci!==i):p.conditions}))}
                  disabled={ruleEditor.conditions.length<=1} aria-label="Remove condition" title="Remove condition"
                  style={{background:"none",border:"none",cursor:ruleEditor.conditions.length<=1?"default":"pointer",color:ruleEditor.conditions.length<=1?"#ccc":C.textSub,fontSize:16,padding:"0 4px",flexShrink:0}}>&times;</button>
              </div>
            ))}
            <button onClick={()=>setRuleEditor(p=>({...p,conditions:[...p.conditions,{field:"from",op:"contains",value:""}]}))}
              style={{background:"none",border:"none",color:C.accent,cursor:"pointer",fontSize:12,padding:"2px 0",marginBottom:12}}>+ Add condition</button>

            <div style={{fontSize:11,color:C.textSub,marginBottom:4}}>Then</div>
            <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
              <select value={ruleEditor.action} onChange={e=>setRuleEditor(p=>({...p,action:e.target.value}))}
                style={{fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 4px"}}>
                <option value="moveToFolder">Move to folder</option>
                <option value="delete">Delete</option>
              </select>
              {ruleEditor.action==="moveToFolder"&&(
                <select value={ruleEditor.targetFolder} onChange={e=>setRuleEditor(p=>({...p,targetFolder:e.target.value}))}
                  style={{flex:1,fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 8px",minWidth:0}}>
                  <option value="">Choose a folder…</option>
                  {RULE_FOLDER_OPTIONS.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                  {(customFolders[compose.from]||[]).map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              )}
            </div>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,marginBottom:10,cursor:"pointer"}}>
              <input type="checkbox" checked={ruleEditor.markAsRead} onChange={e=>setRuleEditor(p=>({...p,markAsRead:e.target.checked}))}/>
              Also mark as read
            </label>
            {!ruleEditor.id&&(
              <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,marginBottom:14,cursor:"pointer"}}>
                <input type="checkbox" checked={ruleEditor.alsoCleanExisting} onChange={e=>setRuleEditor(p=>({...p,alsoCleanExisting:e.target.checked}))}/>
                Also apply to mail already in this mailbox right now
              </label>
            )}
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setRuleEditor(null)} style={{background:"#f3f2f1",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={saveRuleEditor} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>{ruleEditor.id?"Save":"Create Rule"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Inbox Zero — unified Sweep + Rules management. The only entry
          point for ever viewing/removing a Sweep rule once created (Sweep's
          only prior UI was the write-only sweepSender() prompt on the
          right-click menu); Rules gets a live summary + a jump straight into
          its own full manager (openRulesModal) rather than duplicating that
          UI here. */}
      {inboxZeroModal&&(
        <Modal open={!!inboxZeroModal} onClose={()=>setInboxZeroModal(false)} width={600} ariaLabel="Inbox Zero">
          <div style={{padding:20,maxHeight:"82vh",display:"flex",flexDirection:"column",fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
            <div style={{display:"flex",alignItems:"center",marginBottom:4}}>
              <h3 style={{margin:0,fontSize:15,flex:1}}>Inbox Zero — {compose.from}</h3>
            </div>
            <p style={{margin:"0 0 14px",fontSize:12,color:C.textSub,lineHeight:1.4}}>Automatic inbox hygiene, built on real Outlook-style Sweep and Rules — mail from senders and patterns you've set here files, marks read, or clears itself out with no manual work.</p>

            <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:16}}>
              {/* Sweep */}
              <div>
                <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
                  <h4 style={{margin:0,fontSize:13,flex:1}}>Sweep <span style={{fontWeight:400,color:C.textSub,fontSize:11}}>— always handle mail from a specific sender</span></h4>
                  <button onClick={startNewSweep} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:600,flexShrink:0}}>New Sweep Rule</button>
                </div>
                <div style={{border:"1px solid "+C.border,borderRadius:4}}>
                  {sweepLoading?(
                    <div style={{padding:16,textAlign:"center",color:C.textSub,fontSize:12}}>Loading…</div>
                  ):sweepRules.length===0?(
                    <div style={{padding:16,textAlign:"center",color:C.textSub,fontSize:12}}>No Sweep rules yet. Right-click any email and choose "Sweep…", or add one here.</div>
                  ):sweepRules.map((rule,i)=>(
                    <div key={rule.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderBottom:i<sweepRules.length-1?"1px solid "+C.border:"none"}}>
                      <div style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        <span style={{fontWeight:600}}>{rule.senderEmail}</span>
                        <span style={{color:C.textSub}}> → {rule.action==="delete"?"Trash":`Move to "${rule.targetFolder}"`}</span>
                      </div>
                      <button onClick={()=>deleteSweepRule(rule)} title="Remove Sweep rule" aria-label={"Remove Sweep rule for "+rule.senderEmail}
                        style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"4px 8px",cursor:"pointer",fontSize:11,color:C.red,flexShrink:0}}>Remove</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rules summary */}
              <div>
                <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
                  <h4 style={{margin:0,fontSize:13,flex:1}}>Rules <span style={{fontWeight:400,color:C.textSub,fontSize:11}}>— multi-condition auto-filing</span></h4>
                  <button onClick={()=>{setInboxZeroModal(false);openRulesModal();}} style={{background:"none",border:"1px solid "+C.border,borderRadius:4,padding:"5px 10px",cursor:"pointer",fontSize:11,color:C.text,flexShrink:0}}>Manage Rules</button>
                </div>
                <div style={{border:"1px solid "+C.border,borderRadius:4,padding:rulesLoading||rulesList.length===0?16:0,textAlign:rulesLoading||rulesList.length===0?"center":"left"}}>
                  {rulesLoading?(
                    <span style={{color:C.textSub,fontSize:12}}>Loading…</span>
                  ):rulesList.length===0?(
                    <span style={{color:C.textSub,fontSize:12}}>No Rules yet for this account.</span>
                  ):rulesList.map((rule,i)=>(
                    <div key={rule._id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderBottom:i<rulesList.length-1?"1px solid "+C.border:"none",opacity:rule.enabled?1:.5}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:rule.enabled?C.green:C.textMuted,flexShrink:0}}/>
                      <span style={{flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{rule.name}</span>
                      <span style={{color:C.textSub,fontSize:11,flexShrink:0}}>{rule.action==="delete"?"Delete":`→ ${rule.targetFolder}`}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{display:"flex",justifyContent:"flex-end",marginTop:14}}>
              <button onClick={()=>setInboxZeroModal(false)} style={{background:"#f3f2f1",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Sweep — new-rule sub-view, same field set as the sweepSender() prompt() flow but as a real form. */}
      {sweepEditor&&(
        <Modal open={!!sweepEditor} onClose={()=>setSweepEditor(null)} width={420} ariaLabel="New Sweep Rule">
          <div style={{padding:20,fontFamily:"'Segoe UI',sans-serif",fontSize:13}}>
            <h3 style={{margin:"0 0 12px",fontSize:15}}>New Sweep Rule</h3>
            <label style={{display:"block",marginBottom:10}}>
              <div style={{fontSize:11,color:C.textSub,marginBottom:3}}>Sender email</div>
              <input autoFocus type="email" value={sweepEditor.senderEmail} onChange={e=>setSweepEditor(p=>({...p,senderEmail:e.target.value}))}
                placeholder="noreply@newsletter.com"
                style={{width:"100%",border:"1px solid "+C.border,borderRadius:4,padding:"7px 9px",fontSize:13,outline:"none",boxSizing:"border-box"}}/>
            </label>
            <div style={{fontSize:11,color:C.textSub,marginBottom:4}}>Always</div>
            <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
              <select value={sweepEditor.action} onChange={e=>setSweepEditor(p=>({...p,action:e.target.value}))}
                style={{fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 4px"}}>
                <option value="delete">Send to Trash</option>
                <option value="moveToFolder">Move to folder</option>
              </select>
              {sweepEditor.action==="moveToFolder"&&(
                <select value={sweepEditor.targetFolder} onChange={e=>setSweepEditor(p=>({...p,targetFolder:e.target.value}))}
                  style={{flex:1,fontSize:12,border:"1px solid "+C.border,borderRadius:4,padding:"6px 8px",minWidth:0}}>
                  <option value="">Choose a folder…</option>
                  {RULE_FOLDER_OPTIONS.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                  {(customFolders[compose.from]||[]).map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              )}
            </div>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,marginBottom:14,cursor:"pointer"}}>
              <input type="checkbox" checked={sweepEditor.alsoCleanExisting} onChange={e=>setSweepEditor(p=>({...p,alsoCleanExisting:e.target.checked}))}/>
              Also apply to mail already in this mailbox right now
            </label>
            <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
              <button onClick={()=>setSweepEditor(null)} style={{background:"#f3f2f1",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={saveSweepEditor} style={{background:C.accent,color:"#fff",border:"none",borderRadius:4,padding:"7px 16px",cursor:"pointer",fontSize:13}}>Create Sweep Rule</button>
            </div>
          </div>
        </Modal>
      )}
      <FindReplaceDialog open={showFindReplace} onClose={()=>setShowFindReplace(false)} getQuill={getQuill} addToast={addToast} navTheme={C.nav}/>
    </div>
  );
}